/**
 * Copyright 2026 Google LLC.
 * Copyright (c) Microsoft Corporation.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * THIS FILE IS AUTOGENERATED by cddlconv 0.2.0.
 * Run `tools/update_bidi_types.py` to regenerate.
 * @see https://github.com/w3c/webdriver-bidi/blob/master/index.bs
 */

import * as z from 'zod';

export const CommandSchema: z.ZodLazy<
  z.ZodIntersection<
    z.ZodIntersection<
      z.ZodObject<{
        id: typeof JsUintSchema;
      }>,
      typeof CommandDataSchema
    >,
    typeof ExtensibleSchema
  >
> = z.lazy(() =>
  z
    .object({
      id: JsUintSchema,
    })
    .and(CommandDataSchema)
    .and(ExtensibleSchema),
);
export const CommandDataSchema: z.ZodLazy<
  z.ZodUnion<
    readonly [
      typeof BrowserCommandSchema,
      typeof BrowsingContextCommandSchema,
      typeof EmulationCommandSchema,
      typeof InputCommandSchema,
      typeof NetworkCommandSchema,
      typeof ScriptCommandSchema,
      typeof SessionCommandSchema,
      typeof StorageCommandSchema,
      typeof WebExtensionCommandSchema,
    ]
  >
> = z.lazy(() =>
  z.union([
    BrowserCommandSchema,
    BrowsingContextCommandSchema,
    EmulationCommandSchema,
    InputCommandSchema,
    NetworkCommandSchema,
    ScriptCommandSchema,
    SessionCommandSchema,
    StorageCommandSchema,
    WebExtensionCommandSchema,
  ]),
);
export const EmptyParamsSchema: z.ZodLazy<typeof ExtensibleSchema> = z.lazy(
  () => ExtensibleSchema,
);
export const MessageSchema: z.ZodLazy<
  z.ZodUnion<
    readonly [
      typeof CommandResponseSchema,
      typeof ErrorResponseSchema,
      typeof EventSchema,
    ]
  >
> = z.lazy(() =>
  z.union([CommandResponseSchema, ErrorResponseSchema, EventSchema]),
);
export const CommandResponseSchema: z.ZodLazy<
  z.ZodIntersection<
    z.ZodObject<{
      type: z.ZodLiteral<'success'>;
      id: typeof JsUintSchema;
      result: typeof ResultDataSchema;
    }>,
    typeof ExtensibleSchema
  >
> = z.lazy(() =>
  z
    .object({
      type: z.literal('success'),
      id: JsUintSchema,
      result: ResultDataSchema,
    })
    .and(ExtensibleSchema),
);
export const ErrorResponseSchema: z.ZodLazy<
  z.ZodIntersection<
    z.ZodObject<{
      type: z.ZodLiteral<'error'>;
      id: z.ZodUnion<readonly [typeof JsUintSchema, z.ZodNull]>;
      error: typeof ErrorCodeSchema;
      message: z.ZodString;
      stacktrace: z.ZodOptional<z.ZodString>;
    }>,
    typeof ExtensibleSchema
  >
> = z.lazy(() =>
  z
    .object({
      type: z.literal('error'),
      id: z.union([JsUintSchema, z.null()]),
      error: ErrorCodeSchema,
      message: z.string(),
      stacktrace: z.string().optional(),
    })
    .and(ExtensibleSchema),
);
export const ResultDataSchema: z.ZodLazy<
  z.ZodUnion<
    readonly [
      typeof BrowserResultSchema,
      typeof BrowsingContextResultSchema,
      typeof EmulationResultSchema,
      typeof InputResultSchema,
      typeof NetworkResultSchema,
      typeof ScriptResultSchema,
      typeof SessionResultSchema,
      typeof StorageResultSchema,
      typeof WebExtensionResultSchema,
    ]
  >
> = z.lazy(() =>
  z.union([
    BrowserResultSchema,
    BrowsingContextResultSchema,
    EmulationResultSchema,
    InputResultSchema,
    NetworkResultSchema,
    ScriptResultSchema,
    SessionResultSchema,
    StorageResultSchema,
    WebExtensionResultSchema,
  ]),
);
export const EmptyResultSchema: z.ZodLazy<typeof ExtensibleSchema> = z.lazy(
  () => ExtensibleSchema,
);
export const EventSchema: z.ZodLazy<
  z.ZodIntersection<
    z.ZodIntersection<
      z.ZodObject<{
        type: z.ZodLiteral<'event'>;
      }>,
      typeof EventDataSchema
    >,
    typeof ExtensibleSchema
  >
> = z.lazy(() =>
  z
    .object({
      type: z.literal('event'),
    })
    .and(EventDataSchema)
    .and(ExtensibleSchema),
);
export const EventDataSchema: z.ZodLazy<
  z.ZodUnion<
    readonly [
      typeof BrowsingContextEventSchema,
      typeof InputEventSchema,
      typeof LogEventSchema,
      typeof NetworkEventSchema,
      typeof ScriptEventSchema,
    ]
  >
> = z.lazy(() =>
  z.union([
    BrowsingContextEventSchema,
    InputEventSchema,
    LogEventSchema,
    NetworkEventSchema,
    ScriptEventSchema,
  ]),
);
export const ExtensibleSchema: z.ZodLazy<z.ZodRecord<z.ZodString, z.ZodAny>> =
  z.lazy(() => z.record(z.string(), z.any()));
export const JsIntSchema: z.ZodNumber = z
  .number()
  .int()
  .gte(-9007199254740991)
  .lte(9007199254740991);
export const JsUintSchema: z.ZodNumber = z
  .number()
  .int()
  .nonnegative()
  .gte(0)
  .lte(9007199254740991);
export const ErrorCodeSchema: z.ZodLazy<
  z.ZodEnum<{
    'invalid argument': 'invalid argument';
    'invalid selector': 'invalid selector';
    'invalid session id': 'invalid session id';
    'invalid web extension': 'invalid web extension';
    'move target out of bounds': 'move target out of bounds';
    'no such alert': 'no such alert';
    'no such network collector': 'no such network collector';
    'no such element': 'no such element';
    'no such frame': 'no such frame';
    'no such handle': 'no such handle';
    'no such history entry': 'no such history entry';
    'no such intercept': 'no such intercept';
    'no such network data': 'no such network data';
    'no such node': 'no such node';
    'no such request': 'no such request';
    'no such screencast': 'no such screencast';
    'no such script': 'no such script';
    'no such storage partition': 'no such storage partition';
    'no such user context': 'no such user context';
    'no such web extension': 'no such web extension';
    'session not created': 'session not created';
    'unable to capture screen': 'unable to capture screen';
    'unable to close browser': 'unable to close browser';
    'unable to set cookie': 'unable to set cookie';
    'unable to set file input': 'unable to set file input';
    'unavailable network data': 'unavailable network data';
    'underspecified storage partition': 'underspecified storage partition';
    'unknown command': 'unknown command';
    'unknown error': 'unknown error';
    'unsupported operation': 'unsupported operation';
  }>
> = z.lazy(() =>
  z.enum([
    'invalid argument',
    'invalid selector',
    'invalid session id',
    'invalid web extension',
    'move target out of bounds',
    'no such alert',
    'no such network collector',
    'no such element',
    'no such frame',
    'no such handle',
    'no such history entry',
    'no such intercept',
    'no such network data',
    'no such node',
    'no such request',
    'no such screencast',
    'no such script',
    'no such storage partition',
    'no such user context',
    'no such web extension',
    'session not created',
    'unable to capture screen',
    'unable to close browser',
    'unable to set cookie',
    'unable to set file input',
    'unavailable network data',
    'underspecified storage partition',
    'unknown command',
    'unknown error',
    'unsupported operation',
  ]),
);
export const SessionCommandSchema: z.ZodLazy<
  z.ZodUnion<
    readonly [
      typeof Session.EndSchema,
      typeof Session.NewSchema,
      typeof Session.StatusSchema,
      typeof Session.SubscribeSchema,
      typeof Session.UnsubscribeSchema,
    ]
  >
> = z.lazy(() =>
  z.union([
    Session.EndSchema,
    Session.NewSchema,
    Session.StatusSchema,
    Session.SubscribeSchema,
    Session.UnsubscribeSchema,
  ]),
);
export const SessionResultSchema: z.ZodLazy<
  z.ZodUnion<
    readonly [
      typeof Session.EndResultSchema,
      typeof Session.NewResultSchema,
      typeof Session.StatusResultSchema,
      typeof Session.SubscribeResultSchema,
      typeof Session.UnsubscribeResultSchema,
    ]
  >
> = z.lazy(() =>
  z.union([
    Session.EndResultSchema,
    Session.NewResultSchema,
    Session.StatusResultSchema,
    Session.SubscribeResultSchema,
    Session.UnsubscribeResultSchema,
  ]),
);
export namespace Session {
  export const CapabilitiesRequestSchema: z.ZodLazy<
    z.ZodObject<{
      alwaysMatch: z.ZodOptional<typeof Session.CapabilityRequestSchema>;
      firstMatch: z.ZodOptional<
        z.ZodArray<typeof Session.CapabilityRequestSchema>
      >;
    }>
  > = z.lazy(() =>
    z.object({
      alwaysMatch: Session.CapabilityRequestSchema.optional(),
      firstMatch: z.array(Session.CapabilityRequestSchema).optional(),
    }),
  );
}
export namespace Session {
  export const CapabilityRequestSchema: z.ZodLazy<
    z.ZodIntersection<
      z.ZodObject<{
        acceptInsecureCerts: z.ZodOptional<z.ZodBoolean>;
        browserName: z.ZodOptional<z.ZodString>;
        browserVersion: z.ZodOptional<z.ZodString>;
        platformName: z.ZodOptional<z.ZodString>;
        proxy: z.ZodOptional<typeof Session.ProxyConfigurationSchema>;
        unhandledPromptBehavior: z.ZodOptional<
          typeof Session.UserPromptHandlerSchema
        >;
      }>,
      typeof ExtensibleSchema
    >
  > = z.lazy(() =>
    z
      .object({
        acceptInsecureCerts: z.boolean().optional(),
        browserName: z.string().optional(),
        browserVersion: z.string().optional(),
        platformName: z.string().optional(),
        proxy: Session.ProxyConfigurationSchema.optional(),
        unhandledPromptBehavior: Session.UserPromptHandlerSchema.optional(),
      })
      .and(ExtensibleSchema),
  );
}
export namespace Session {
  export const ProxyConfigurationSchema: z.ZodLazy<
    z.ZodUnion<
      readonly [
        typeof Session.AutodetectProxyConfigurationSchema,
        typeof Session.DirectProxyConfigurationSchema,
        typeof Session.ManualProxyConfigurationSchema,
        typeof Session.PacProxyConfigurationSchema,
        typeof Session.SystemProxyConfigurationSchema,
      ]
    >
  > = z.lazy(() =>
    z.union([
      Session.AutodetectProxyConfigurationSchema,
      Session.DirectProxyConfigurationSchema,
      Session.ManualProxyConfigurationSchema,
      Session.PacProxyConfigurationSchema,
      Session.SystemProxyConfigurationSchema,
    ]),
  );
}
export namespace Session {
  export const AutodetectProxyConfigurationSchema: z.ZodLazy<
    z.ZodIntersection<
      z.ZodObject<{
        proxyType: z.ZodLiteral<'autodetect'>;
      }>,
      typeof ExtensibleSchema
    >
  > = z.lazy(() =>
    z
      .object({
        proxyType: z.literal('autodetect'),
      })
      .and(ExtensibleSchema),
  );
}
export namespace Session {
  export const DirectProxyConfigurationSchema: z.ZodLazy<
    z.ZodIntersection<
      z.ZodObject<{
        proxyType: z.ZodLiteral<'direct'>;
      }>,
      typeof ExtensibleSchema
    >
  > = z.lazy(() =>
    z
      .object({
        proxyType: z.literal('direct'),
      })
      .and(ExtensibleSchema),
  );
}
export namespace Session {
  export const ManualProxyConfigurationSchema: z.ZodLazy<
    z.ZodIntersection<
      z.ZodIntersection<
        z.ZodIntersection<
          z.ZodObject<{
            proxyType: z.ZodLiteral<'manual'>;
            httpProxy: z.ZodOptional<z.ZodString>;
            sslProxy: z.ZodOptional<z.ZodString>;
          }>,
          z.ZodUnion<
            readonly [
              typeof Session.SocksProxyConfigurationSchema,
              z.ZodObject<{}>,
            ]
          >
        >,
        z.ZodObject<{
          noProxy: z.ZodOptional<z.ZodArray<z.ZodString>>;
        }>
      >,
      typeof ExtensibleSchema
    >
  > = z.lazy(() =>
    z
      .object({
        proxyType: z.literal('manual'),
        httpProxy: z.string().optional(),
        sslProxy: z.string().optional(),
      })
      .and(Session.SocksProxyConfigurationSchema.or(z.object({})))
      .and(
        z.object({
          noProxy: z.array(z.string()).optional(),
        }),
      )
      .and(ExtensibleSchema),
  );
}
export namespace Session {
  export const SocksProxyConfigurationSchema: z.ZodLazy<
    z.ZodObject<{
      socksProxy: z.ZodString;
      socksVersion: z.ZodNumber;
    }>
  > = z.lazy(() =>
    z.object({
      socksProxy: z.string(),
      socksVersion: z.number().int().nonnegative().gte(0).lte(255),
    }),
  );
}
export namespace Session {
  export const PacProxyConfigurationSchema: z.ZodLazy<
    z.ZodIntersection<
      z.ZodObject<{
        proxyType: z.ZodLiteral<'pac'>;
        proxyAutoconfigUrl: z.ZodString;
      }>,
      typeof ExtensibleSchema
    >
  > = z.lazy(() =>
    z
      .object({
        proxyType: z.literal('pac'),
        proxyAutoconfigUrl: z.string(),
      })
      .and(ExtensibleSchema),
  );
}
export namespace Session {
  export const SystemProxyConfigurationSchema: z.ZodLazy<
    z.ZodIntersection<
      z.ZodObject<{
        proxyType: z.ZodLiteral<'system'>;
      }>,
      typeof ExtensibleSchema
    >
  > = z.lazy(() =>
    z
      .object({
        proxyType: z.literal('system'),
      })
      .and(ExtensibleSchema),
  );
}
export namespace Session {
  export const UserPromptHandlerSchema: z.ZodLazy<
    z.ZodObject<{
      alert: z.ZodOptional<typeof Session.UserPromptHandlerTypeSchema>;
      beforeUnload: z.ZodOptional<typeof Session.UserPromptHandlerTypeSchema>;
      confirm: z.ZodOptional<typeof Session.UserPromptHandlerTypeSchema>;
      default: z.ZodOptional<typeof Session.UserPromptHandlerTypeSchema>;
      file: z.ZodOptional<typeof Session.UserPromptHandlerTypeSchema>;
      prompt: z.ZodOptional<typeof Session.UserPromptHandlerTypeSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      alert: Session.UserPromptHandlerTypeSchema.optional(),
      beforeUnload: Session.UserPromptHandlerTypeSchema.optional(),
      confirm: Session.UserPromptHandlerTypeSchema.optional(),
      default: Session.UserPromptHandlerTypeSchema.optional(),
      file: Session.UserPromptHandlerTypeSchema.optional(),
      prompt: Session.UserPromptHandlerTypeSchema.optional(),
    }),
  );
}
export namespace Session {
  export const UserPromptHandlerTypeSchema: z.ZodLazy<
    z.ZodEnum<{accept: 'accept'; dismiss: 'dismiss'; ignore: 'ignore'}>
  > = z.lazy(() => z.enum(['accept', 'dismiss', 'ignore']));
}
export namespace Session {
  export const SubscriptionSchema: z.ZodLazy<z.ZodString> = z.lazy(() =>
    z.string(),
  );
}
export namespace Session {
  export const SubscribeParametersSchema: z.ZodLazy<
    z.ZodObject<{
      events: z.ZodArray<z.ZodString>;
      contexts: z.ZodOptional<
        z.ZodArray<typeof BrowsingContext.BrowsingContextSchema>
      >;
      userContexts: z.ZodOptional<z.ZodArray<typeof Browser.UserContextSchema>>;
    }>
  > = z.lazy(() =>
    z.object({
      events: z.array(z.string()).nonempty(),
      contexts: z
        .array(BrowsingContext.BrowsingContextSchema)
        .nonempty()
        .optional(),
      userContexts: z.array(Browser.UserContextSchema).nonempty().optional(),
    }),
  );
}
export namespace Session {
  export const UnsubscribeByIdRequestSchema: z.ZodLazy<
    z.ZodObject<{
      subscriptions: z.ZodArray<typeof Session.SubscriptionSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      subscriptions: z.array(Session.SubscriptionSchema).nonempty(),
    }),
  );
}
export namespace Session {
  export const UnsubscribeByAttributesRequestSchema: z.ZodLazy<
    z.ZodObject<{
      events: z.ZodArray<z.ZodString>;
    }>
  > = z.lazy(() =>
    z.object({
      events: z.array(z.string()).nonempty(),
    }),
  );
}
export namespace Session {
  export const StatusSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'session.status'>;
      params: typeof EmptyParamsSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('session.status'),
      params: EmptyParamsSchema,
    }),
  );
}
export namespace Session {
  export const StatusResultSchema: z.ZodLazy<
    z.ZodObject<{
      ready: z.ZodBoolean;
      message: z.ZodString;
    }>
  > = z.lazy(() =>
    z.object({
      ready: z.boolean(),
      message: z.string(),
    }),
  );
}
export namespace Session {
  export const NewSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'session.new'>;
      params: typeof Session.NewParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('session.new'),
      params: Session.NewParametersSchema,
    }),
  );
}
export namespace Session {
  export const NewParametersSchema: z.ZodLazy<
    z.ZodObject<{
      capabilities: typeof Session.CapabilitiesRequestSchema;
    }>
  > = z.lazy(() =>
    z.object({
      capabilities: Session.CapabilitiesRequestSchema,
    }),
  );
}
export namespace Session {
  export const NewResultSchema: z.ZodLazy<
    z.ZodObject<{
      sessionId: z.ZodString;
      capabilities: z.ZodIntersection<
        z.ZodObject<{
          acceptInsecureCerts: z.ZodBoolean;
          browserName: z.ZodString;
          browserVersion: z.ZodString;
          platformName: z.ZodString;
          setWindowRect: z.ZodBoolean;
          userAgent: z.ZodString;
          proxy: z.ZodOptional<typeof Session.ProxyConfigurationSchema>;
          unhandledPromptBehavior: z.ZodOptional<
            typeof Session.UserPromptHandlerSchema
          >;
          webSocketUrl: z.ZodOptional<z.ZodString>;
        }>,
        typeof ExtensibleSchema
      >;
    }>
  > = z.lazy(() =>
    z.object({
      sessionId: z.string(),
      capabilities: z
        .object({
          acceptInsecureCerts: z.boolean(),
          browserName: z.string(),
          browserVersion: z.string(),
          platformName: z.string(),
          setWindowRect: z.boolean(),
          userAgent: z.string(),
          proxy: Session.ProxyConfigurationSchema.optional(),
          unhandledPromptBehavior: Session.UserPromptHandlerSchema.optional(),
          webSocketUrl: z.string().optional(),
        })
        .and(ExtensibleSchema),
    }),
  );
}
export namespace Session {
  export const EndSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'session.end'>;
      params: typeof EmptyParamsSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('session.end'),
      params: EmptyParamsSchema,
    }),
  );
}
export namespace Session {
  export const EndResultSchema: z.ZodLazy<typeof EmptyResultSchema> = z.lazy(
    () => EmptyResultSchema,
  );
}
export namespace Session {
  export const SubscribeSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'session.subscribe'>;
      params: typeof Session.SubscribeParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('session.subscribe'),
      params: Session.SubscribeParametersSchema,
    }),
  );
}
export namespace Session {
  export const SubscribeResultSchema: z.ZodLazy<
    z.ZodObject<{
      subscription: typeof Session.SubscriptionSchema;
    }>
  > = z.lazy(() =>
    z.object({
      subscription: Session.SubscriptionSchema,
    }),
  );
}
export namespace Session {
  export const UnsubscribeSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'session.unsubscribe'>;
      params: typeof Session.UnsubscribeParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('session.unsubscribe'),
      params: Session.UnsubscribeParametersSchema,
    }),
  );
}
export namespace Session {
  export const UnsubscribeParametersSchema: z.ZodLazy<
    z.ZodUnion<
      readonly [
        typeof Session.UnsubscribeByAttributesRequestSchema,
        typeof Session.UnsubscribeByIdRequestSchema,
      ]
    >
  > = z.lazy(() =>
    z.union([
      Session.UnsubscribeByAttributesRequestSchema,
      Session.UnsubscribeByIdRequestSchema,
    ]),
  );
}
export namespace Session {
  export const UnsubscribeResultSchema: z.ZodLazy<typeof EmptyResultSchema> =
    z.lazy(() => EmptyResultSchema);
}
export const BrowserCommandSchema: z.ZodLazy<
  z.ZodUnion<
    readonly [
      typeof Browser.CloseSchema,
      typeof Browser.CreateUserContextSchema,
      typeof Browser.GetClientWindowsSchema,
      typeof Browser.GetUserContextsSchema,
      typeof Browser.RemoveUserContextSchema,
      typeof Browser.SetClientWindowStateSchema,
      typeof Browser.SetDownloadBehaviorSchema,
    ]
  >
> = z.lazy(() =>
  z.union([
    Browser.CloseSchema,
    Browser.CreateUserContextSchema,
    Browser.GetClientWindowsSchema,
    Browser.GetUserContextsSchema,
    Browser.RemoveUserContextSchema,
    Browser.SetClientWindowStateSchema,
    Browser.SetDownloadBehaviorSchema,
  ]),
);
export const BrowserResultSchema: z.ZodLazy<
  z.ZodUnion<
    readonly [
      typeof Browser.CloseResultSchema,
      typeof Browser.CreateUserContextResultSchema,
      typeof Browser.GetClientWindowsResultSchema,
      typeof Browser.GetUserContextsResultSchema,
      typeof Browser.RemoveUserContextResultSchema,
      typeof Browser.SetClientWindowStateResultSchema,
      typeof Browser.SetDownloadBehaviorResultSchema,
    ]
  >
> = z.lazy(() =>
  z.union([
    Browser.CloseResultSchema,
    Browser.CreateUserContextResultSchema,
    Browser.GetClientWindowsResultSchema,
    Browser.GetUserContextsResultSchema,
    Browser.RemoveUserContextResultSchema,
    Browser.SetClientWindowStateResultSchema,
    Browser.SetDownloadBehaviorResultSchema,
  ]),
);
export namespace Browser {
  export const ClientWindowSchema: z.ZodLazy<z.ZodString> = z.lazy(() =>
    z.string(),
  );
}
export namespace Browser {
  export const ClientWindowInfoSchema: z.ZodLazy<
    z.ZodObject<{
      active: z.ZodBoolean;
      clientWindow: typeof Browser.ClientWindowSchema;
      height: typeof JsUintSchema;
      state: z.ZodEnum<{
        fullscreen: 'fullscreen';
        maximized: 'maximized';
        minimized: 'minimized';
        normal: 'normal';
      }>;
      width: typeof JsUintSchema;
      x: typeof JsIntSchema;
      y: typeof JsIntSchema;
    }>
  > = z.lazy(() =>
    z.object({
      active: z.boolean(),
      clientWindow: Browser.ClientWindowSchema,
      height: JsUintSchema,
      state: z.enum(['fullscreen', 'maximized', 'minimized', 'normal']),
      width: JsUintSchema,
      x: JsIntSchema,
      y: JsIntSchema,
    }),
  );
}
export namespace Browser {
  export const UserContextSchema: z.ZodLazy<z.ZodString> = z.lazy(() =>
    z.string(),
  );
}
export namespace Browser {
  export const UserContextInfoSchema: z.ZodLazy<
    z.ZodObject<{
      userContext: typeof Browser.UserContextSchema;
    }>
  > = z.lazy(() =>
    z.object({
      userContext: Browser.UserContextSchema,
    }),
  );
}
export namespace Browser {
  export const CloseSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'browser.close'>;
      params: typeof EmptyParamsSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('browser.close'),
      params: EmptyParamsSchema,
    }),
  );
}
export namespace Browser {
  export const CloseResultSchema: z.ZodLazy<typeof EmptyResultSchema> = z.lazy(
    () => EmptyResultSchema,
  );
}
export namespace Browser {
  export const CreateUserContextSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'browser.createUserContext'>;
      params: typeof Browser.CreateUserContextParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('browser.createUserContext'),
      params: Browser.CreateUserContextParametersSchema,
    }),
  );
}
export namespace Browser {
  export const CreateUserContextParametersSchema: z.ZodLazy<
    z.ZodObject<{
      acceptInsecureCerts: z.ZodOptional<z.ZodBoolean>;
      proxy: z.ZodOptional<typeof Session.ProxyConfigurationSchema>;
      unhandledPromptBehavior: z.ZodOptional<
        typeof Session.UserPromptHandlerSchema
      >;
    }>
  > = z.lazy(() =>
    z.object({
      acceptInsecureCerts: z.boolean().optional(),
      proxy: Session.ProxyConfigurationSchema.optional(),
      unhandledPromptBehavior: Session.UserPromptHandlerSchema.optional(),
    }),
  );
}
export namespace Browser {
  export const CreateUserContextResultSchema: z.ZodLazy<
    typeof Browser.UserContextInfoSchema
  > = z.lazy(() => Browser.UserContextInfoSchema);
}
export namespace Browser {
  export const GetClientWindowsSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'browser.getClientWindows'>;
      params: typeof EmptyParamsSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('browser.getClientWindows'),
      params: EmptyParamsSchema,
    }),
  );
}
export namespace Browser {
  export const GetClientWindowsResultSchema: z.ZodLazy<
    z.ZodObject<{
      clientWindows: z.ZodArray<typeof Browser.ClientWindowInfoSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      clientWindows: z.array(Browser.ClientWindowInfoSchema),
    }),
  );
}
export namespace Browser {
  export const GetUserContextsSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'browser.getUserContexts'>;
      params: typeof EmptyParamsSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('browser.getUserContexts'),
      params: EmptyParamsSchema,
    }),
  );
}
export namespace Browser {
  export const GetUserContextsResultSchema: z.ZodLazy<
    z.ZodObject<{
      userContexts: z.ZodArray<typeof Browser.UserContextInfoSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      userContexts: z.array(Browser.UserContextInfoSchema).nonempty(),
    }),
  );
}
export namespace Browser {
  export const RemoveUserContextSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'browser.removeUserContext'>;
      params: typeof Browser.RemoveUserContextParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('browser.removeUserContext'),
      params: Browser.RemoveUserContextParametersSchema,
    }),
  );
}
export namespace Browser {
  export const RemoveUserContextParametersSchema: z.ZodLazy<
    z.ZodObject<{
      userContext: typeof Browser.UserContextSchema;
    }>
  > = z.lazy(() =>
    z.object({
      userContext: Browser.UserContextSchema,
    }),
  );
}
export namespace Browser {
  export const RemoveUserContextResultSchema: z.ZodLazy<
    typeof EmptyResultSchema
  > = z.lazy(() => EmptyResultSchema);
}
export namespace Browser {
  export const SetClientWindowStateSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'browser.setClientWindowState'>;
      params: typeof Browser.SetClientWindowStateParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('browser.setClientWindowState'),
      params: Browser.SetClientWindowStateParametersSchema,
    }),
  );
}
export namespace Browser {
  export const SetClientWindowStateParametersSchema: z.ZodLazy<
    z.ZodIntersection<
      z.ZodObject<{
        clientWindow: typeof Browser.ClientWindowSchema;
      }>,
      z.ZodUnion<
        readonly [
          typeof Browser.ClientWindowNamedStateSchema,
          typeof Browser.ClientWindowRectStateSchema,
        ]
      >
    >
  > = z.lazy(() =>
    z
      .object({
        clientWindow: Browser.ClientWindowSchema,
      })
      .and(
        z.union([
          Browser.ClientWindowNamedStateSchema,
          Browser.ClientWindowRectStateSchema,
        ]),
      ),
  );
}
export namespace Browser {
  export const ClientWindowNamedStateSchema: z.ZodLazy<
    z.ZodObject<{
      state: z.ZodEnum<{
        fullscreen: 'fullscreen';
        maximized: 'maximized';
        minimized: 'minimized';
      }>;
    }>
  > = z.lazy(() =>
    z.object({
      state: z.enum(['fullscreen', 'maximized', 'minimized']),
    }),
  );
}
export namespace Browser {
  export const ClientWindowRectStateSchema: z.ZodLazy<
    z.ZodObject<{
      state: z.ZodLiteral<'normal'>;
      width: z.ZodOptional<typeof JsUintSchema>;
      height: z.ZodOptional<typeof JsUintSchema>;
      x: z.ZodOptional<typeof JsIntSchema>;
      y: z.ZodOptional<typeof JsIntSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      state: z.literal('normal'),
      width: JsUintSchema.optional(),
      height: JsUintSchema.optional(),
      x: JsIntSchema.optional(),
      y: JsIntSchema.optional(),
    }),
  );
}
export namespace Browser {
  export const SetClientWindowStateResultSchema: z.ZodLazy<
    typeof Browser.ClientWindowInfoSchema
  > = z.lazy(() => Browser.ClientWindowInfoSchema);
}
export namespace Browser {
  export const SetDownloadBehaviorSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'browser.setDownloadBehavior'>;
      params: typeof Browser.SetDownloadBehaviorParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('browser.setDownloadBehavior'),
      params: Browser.SetDownloadBehaviorParametersSchema,
    }),
  );
}
export namespace Browser {
  export const SetDownloadBehaviorParametersSchema: z.ZodLazy<
    z.ZodObject<{
      downloadBehavior: z.ZodUnion<
        readonly [typeof Browser.DownloadBehaviorSchema, z.ZodNull]
      >;
      userContexts: z.ZodOptional<z.ZodArray<typeof Browser.UserContextSchema>>;
    }>
  > = z.lazy(() =>
    z.object({
      downloadBehavior: z.union([Browser.DownloadBehaviorSchema, z.null()]),
      userContexts: z.array(Browser.UserContextSchema).nonempty().optional(),
    }),
  );
}
export namespace Browser {
  export const DownloadBehaviorSchema: z.ZodLazy<
    z.ZodUnion<
      readonly [
        typeof Browser.DownloadBehaviorAllowedSchema,
        typeof Browser.DownloadBehaviorDeniedSchema,
      ]
    >
  > = z.lazy(() =>
    z.union([
      Browser.DownloadBehaviorAllowedSchema,
      Browser.DownloadBehaviorDeniedSchema,
    ]),
  );
}
export namespace Browser {
  export const DownloadBehaviorAllowedSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'allowed'>;
      destinationFolder: z.ZodString;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('allowed'),
      destinationFolder: z.string(),
    }),
  );
}
export namespace Browser {
  export const DownloadBehaviorDeniedSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'denied'>;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('denied'),
    }),
  );
}
export namespace Browser {
  export const SetDownloadBehaviorResultSchema: z.ZodLazy<
    typeof EmptyResultSchema
  > = z.lazy(() => EmptyResultSchema);
}
export const BrowsingContextCommandSchema: z.ZodLazy<
  z.ZodUnion<
    readonly [
      typeof BrowsingContext.ActivateSchema,
      typeof BrowsingContext.CaptureScreenshotSchema,
      typeof BrowsingContext.CloseSchema,
      typeof BrowsingContext.CreateSchema,
      typeof BrowsingContext.GetTreeSchema,
      typeof BrowsingContext.HandleUserPromptSchema,
      typeof BrowsingContext.LocateNodesSchema,
      typeof BrowsingContext.NavigateSchema,
      typeof BrowsingContext.PrintSchema,
      typeof BrowsingContext.ReloadSchema,
      typeof BrowsingContext.SetBypassCspSchema,
      typeof BrowsingContext.SetViewportSchema,
      typeof BrowsingContext.StartScreencastSchema,
      typeof BrowsingContext.StopScreencastSchema,
      typeof BrowsingContext.TraverseHistorySchema,
    ]
  >
> = z.lazy(() =>
  z.union([
    BrowsingContext.ActivateSchema,
    BrowsingContext.CaptureScreenshotSchema,
    BrowsingContext.CloseSchema,
    BrowsingContext.CreateSchema,
    BrowsingContext.GetTreeSchema,
    BrowsingContext.HandleUserPromptSchema,
    BrowsingContext.LocateNodesSchema,
    BrowsingContext.NavigateSchema,
    BrowsingContext.PrintSchema,
    BrowsingContext.ReloadSchema,
    BrowsingContext.SetBypassCspSchema,
    BrowsingContext.SetViewportSchema,
    BrowsingContext.StartScreencastSchema,
    BrowsingContext.StopScreencastSchema,
    BrowsingContext.TraverseHistorySchema,
  ]),
);
export const BrowsingContextResultSchema: z.ZodLazy<
  z.ZodUnion<
    readonly [
      typeof BrowsingContext.ActivateResultSchema,
      typeof BrowsingContext.CaptureScreenshotResultSchema,
      typeof BrowsingContext.CloseResultSchema,
      typeof BrowsingContext.CreateResultSchema,
      typeof BrowsingContext.GetTreeResultSchema,
      typeof BrowsingContext.HandleUserPromptResultSchema,
      typeof BrowsingContext.LocateNodesResultSchema,
      typeof BrowsingContext.NavigateResultSchema,
      typeof BrowsingContext.PrintResultSchema,
      typeof BrowsingContext.ReloadResultSchema,
      typeof BrowsingContext.SetBypassCspResultSchema,
      typeof BrowsingContext.SetViewportResultSchema,
      typeof BrowsingContext.StartScreencastResultSchema,
      typeof BrowsingContext.StopScreencastResultSchema,
      typeof BrowsingContext.TraverseHistoryResultSchema,
    ]
  >
> = z.lazy(() =>
  z.union([
    BrowsingContext.ActivateResultSchema,
    BrowsingContext.CaptureScreenshotResultSchema,
    BrowsingContext.CloseResultSchema,
    BrowsingContext.CreateResultSchema,
    BrowsingContext.GetTreeResultSchema,
    BrowsingContext.HandleUserPromptResultSchema,
    BrowsingContext.LocateNodesResultSchema,
    BrowsingContext.NavigateResultSchema,
    BrowsingContext.PrintResultSchema,
    BrowsingContext.ReloadResultSchema,
    BrowsingContext.SetBypassCspResultSchema,
    BrowsingContext.SetViewportResultSchema,
    BrowsingContext.StartScreencastResultSchema,
    BrowsingContext.StopScreencastResultSchema,
    BrowsingContext.TraverseHistoryResultSchema,
  ]),
);
export const BrowsingContextEventSchema: z.ZodLazy<
  z.ZodUnion<
    readonly [
      typeof BrowsingContext.ContextCreatedSchema,
      typeof BrowsingContext.ContextDestroyedSchema,
      typeof BrowsingContext.DomContentLoadedSchema,
      typeof BrowsingContext.DownloadEndSchema,
      typeof BrowsingContext.DownloadWillBeginSchema,
      typeof BrowsingContext.FragmentNavigatedSchema,
      typeof BrowsingContext.HistoryUpdatedSchema,
      typeof BrowsingContext.LoadSchema,
      typeof BrowsingContext.NavigationAbortedSchema,
      typeof BrowsingContext.NavigationCommittedSchema,
      typeof BrowsingContext.NavigationFailedSchema,
      typeof BrowsingContext.NavigationStartedSchema,
      typeof BrowsingContext.UserPromptClosedSchema,
      typeof BrowsingContext.UserPromptOpenedSchema,
    ]
  >
> = z.lazy(() =>
  z.union([
    BrowsingContext.ContextCreatedSchema,
    BrowsingContext.ContextDestroyedSchema,
    BrowsingContext.DomContentLoadedSchema,
    BrowsingContext.DownloadEndSchema,
    BrowsingContext.DownloadWillBeginSchema,
    BrowsingContext.FragmentNavigatedSchema,
    BrowsingContext.HistoryUpdatedSchema,
    BrowsingContext.LoadSchema,
    BrowsingContext.NavigationAbortedSchema,
    BrowsingContext.NavigationCommittedSchema,
    BrowsingContext.NavigationFailedSchema,
    BrowsingContext.NavigationStartedSchema,
    BrowsingContext.UserPromptClosedSchema,
    BrowsingContext.UserPromptOpenedSchema,
  ]),
);
export namespace BrowsingContext {
  export const BrowsingContextSchema: z.ZodLazy<z.ZodString> = z.lazy(() =>
    z.string(),
  );
}
export namespace BrowsingContext {
  export const InfoListSchema: z.ZodType = z.lazy(() =>
    z.array(BrowsingContext.InfoSchema),
  );
}
export namespace BrowsingContext {
  export const BaseInfoSchema: z.ZodLazy<
    z.ZodObject<{
      children: z.ZodUnion<
        readonly [typeof BrowsingContext.InfoListSchema, z.ZodNull]
      >;
      clientWindow: typeof Browser.ClientWindowSchema;
      context: typeof BrowsingContext.BrowsingContextSchema;
      originalOpener: z.ZodUnion<
        readonly [typeof BrowsingContext.BrowsingContextSchema, z.ZodNull]
      >;
      url: z.ZodString;
      userContext: typeof Browser.UserContextSchema;
      parent: z.ZodOptional<
        z.ZodUnion<
          readonly [typeof BrowsingContext.BrowsingContextSchema, z.ZodNull]
        >
      >;
    }>
  > = z.lazy(() =>
    z.object({
      children: z.union([BrowsingContext.InfoListSchema, z.null()]),
      clientWindow: Browser.ClientWindowSchema,
      context: BrowsingContext.BrowsingContextSchema,
      originalOpener: z.union([
        BrowsingContext.BrowsingContextSchema,
        z.null(),
      ]),
      url: z.string(),
      userContext: Browser.UserContextSchema,
      parent: z
        .union([BrowsingContext.BrowsingContextSchema, z.null()])
        .optional(),
    }),
  );
}
export namespace BrowsingContext {
  export const InfoSchema: z.ZodLazy<typeof BrowsingContext.BaseInfoSchema> =
    z.lazy(() => BrowsingContext.BaseInfoSchema);
}
export namespace BrowsingContext {
  export const LocatorSchema: z.ZodLazy<
    z.ZodUnion<
      readonly [
        typeof BrowsingContext.AccessibilityLocatorSchema,
        typeof BrowsingContext.CssLocatorSchema,
        typeof BrowsingContext.ContextLocatorSchema,
        typeof BrowsingContext.InnerTextLocatorSchema,
        typeof BrowsingContext.XPathLocatorSchema,
      ]
    >
  > = z.lazy(() =>
    z.union([
      BrowsingContext.AccessibilityLocatorSchema,
      BrowsingContext.CssLocatorSchema,
      BrowsingContext.ContextLocatorSchema,
      BrowsingContext.InnerTextLocatorSchema,
      BrowsingContext.XPathLocatorSchema,
    ]),
  );
}
export namespace BrowsingContext {
  export const AccessibilityLocatorSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'accessibility'>;
      value: z.ZodObject<{
        name: z.ZodOptional<z.ZodString>;
        role: z.ZodOptional<z.ZodString>;
      }>;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('accessibility'),
      value: z.object({
        name: z.string().optional(),
        role: z.string().optional(),
      }),
    }),
  );
}
export namespace BrowsingContext {
  export const CssLocatorSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'css'>;
      value: z.ZodString;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('css'),
      value: z.string(),
    }),
  );
}
export namespace BrowsingContext {
  export const ContextLocatorSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'context'>;
      value: z.ZodObject<{
        context: typeof BrowsingContext.BrowsingContextSchema;
      }>;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('context'),
      value: z.object({
        context: BrowsingContext.BrowsingContextSchema,
      }),
    }),
  );
}
export namespace BrowsingContext {
  export const InnerTextLocatorSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'innerText'>;
      value: z.ZodString;
      ignoreCase: z.ZodOptional<z.ZodBoolean>;
      matchType: z.ZodOptional<z.ZodEnum<{full: 'full'; partial: 'partial'}>>;
      maxDepth: z.ZodOptional<typeof JsUintSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('innerText'),
      value: z.string(),
      ignoreCase: z.boolean().optional(),
      matchType: z.enum(['full', 'partial']).optional(),
      maxDepth: JsUintSchema.optional(),
    }),
  );
}
export namespace BrowsingContext {
  export const XPathLocatorSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'xpath'>;
      value: z.ZodString;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('xpath'),
      value: z.string(),
    }),
  );
}
export namespace BrowsingContext {
  export const NavigationSchema: z.ZodLazy<z.ZodString> = z.lazy(() =>
    z.string(),
  );
}
export namespace BrowsingContext {
  export const DownloadSchema: z.ZodLazy<z.ZodString> = z.lazy(() =>
    z.string(),
  );
}
export namespace BrowsingContext {
  export const BaseNavigationInfoSchema: z.ZodLazy<
    z.ZodObject<{
      context: typeof BrowsingContext.BrowsingContextSchema;
      navigation: z.ZodUnion<
        readonly [typeof BrowsingContext.NavigationSchema, z.ZodNull]
      >;
      timestamp: typeof JsUintSchema;
      url: z.ZodString;
      userContext: z.ZodOptional<typeof Browser.UserContextSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      context: BrowsingContext.BrowsingContextSchema,
      navigation: z.union([BrowsingContext.NavigationSchema, z.null()]),
      timestamp: JsUintSchema,
      url: z.string(),
      userContext: Browser.UserContextSchema.optional(),
    }),
  );
}
export namespace BrowsingContext {
  export const NavigationInfoSchema: z.ZodLazy<
    typeof BrowsingContext.BaseNavigationInfoSchema
  > = z.lazy(() => BrowsingContext.BaseNavigationInfoSchema);
}
export namespace BrowsingContext {
  export const ReadinessStateSchema: z.ZodLazy<
    z.ZodEnum<{none: 'none'; interactive: 'interactive'; complete: 'complete'}>
  > = z.lazy(() => z.enum(['none', 'interactive', 'complete']));
}
export namespace BrowsingContext {
  export const UserPromptTypeSchema: z.ZodLazy<
    z.ZodEnum<{
      alert: 'alert';
      beforeunload: 'beforeunload';
      confirm: 'confirm';
      prompt: 'prompt';
    }>
  > = z.lazy(() => z.enum(['alert', 'beforeunload', 'confirm', 'prompt']));
}
export namespace BrowsingContext {
  export const ActivateSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'browsingContext.activate'>;
      params: typeof BrowsingContext.ActivateParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('browsingContext.activate'),
      params: BrowsingContext.ActivateParametersSchema,
    }),
  );
}
export namespace BrowsingContext {
  export const ActivateParametersSchema: z.ZodLazy<
    z.ZodObject<{
      context: typeof BrowsingContext.BrowsingContextSchema;
    }>
  > = z.lazy(() =>
    z.object({
      context: BrowsingContext.BrowsingContextSchema,
    }),
  );
}
export namespace BrowsingContext {
  export const ActivateResultSchema: z.ZodLazy<typeof EmptyResultSchema> =
    z.lazy(() => EmptyResultSchema);
}
export namespace BrowsingContext {
  export const CaptureScreenshotSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'browsingContext.captureScreenshot'>;
      params: typeof BrowsingContext.CaptureScreenshotParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('browsingContext.captureScreenshot'),
      params: BrowsingContext.CaptureScreenshotParametersSchema,
    }),
  );
}
export namespace BrowsingContext {
  export const CaptureScreenshotParametersSchema: z.ZodLazy<
    z.ZodObject<{
      context: typeof BrowsingContext.BrowsingContextSchema;
      origin: z.ZodOptional<
        z.ZodDefault<z.ZodEnum<{viewport: 'viewport'; document: 'document'}>>
      >;
      format: z.ZodOptional<typeof BrowsingContext.ImageFormatSchema>;
      clip: z.ZodOptional<typeof BrowsingContext.ClipRectangleSchema>;
      imageSize: z.ZodOptional<typeof BrowsingContext.ImageSizeSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      context: BrowsingContext.BrowsingContextSchema,
      origin: z.enum(['viewport', 'document']).default('viewport').optional(),
      format: BrowsingContext.ImageFormatSchema.optional(),
      clip: BrowsingContext.ClipRectangleSchema.optional(),
      imageSize: BrowsingContext.ImageSizeSchema.optional(),
    }),
  );
}
export namespace BrowsingContext {
  export const ImageFormatSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodString;
      quality: z.ZodOptional<z.ZodNumber>;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.string(),
      quality: z.number().gte(0).lte(1).optional(),
    }),
  );
}
export namespace BrowsingContext {
  export const ImageSizeSchema: z.ZodLazy<
    z.ZodObject<{
      maxWidth: z.ZodOptional<typeof JsUintSchema>;
      maxHeight: z.ZodOptional<typeof JsUintSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      maxWidth: JsUintSchema.gte(1).optional(),
      maxHeight: JsUintSchema.gte(1).optional(),
    }),
  );
}
export namespace BrowsingContext {
  export const ClipRectangleSchema: z.ZodLazy<
    z.ZodUnion<
      readonly [
        typeof BrowsingContext.BoxClipRectangleSchema,
        typeof BrowsingContext.ElementClipRectangleSchema,
      ]
    >
  > = z.lazy(() =>
    z.union([
      BrowsingContext.BoxClipRectangleSchema,
      BrowsingContext.ElementClipRectangleSchema,
    ]),
  );
}
export namespace BrowsingContext {
  export const ElementClipRectangleSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'element'>;
      element: typeof Script.SharedReferenceSchema;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('element'),
      element: Script.SharedReferenceSchema,
    }),
  );
}
export namespace BrowsingContext {
  export const BoxClipRectangleSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'box'>;
      x: z.ZodNumber;
      y: z.ZodNumber;
      width: z.ZodNumber;
      height: z.ZodNumber;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('box'),
      x: z.number(),
      y: z.number(),
      width: z.number(),
      height: z.number(),
    }),
  );
}
export namespace BrowsingContext {
  export const CaptureScreenshotResultSchema: z.ZodLazy<
    z.ZodObject<{
      data: z.ZodString;
    }>
  > = z.lazy(() =>
    z.object({
      data: z.string(),
    }),
  );
}
export namespace BrowsingContext {
  export const CloseSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'browsingContext.close'>;
      params: typeof BrowsingContext.CloseParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('browsingContext.close'),
      params: BrowsingContext.CloseParametersSchema,
    }),
  );
}
export namespace BrowsingContext {
  export const CloseParametersSchema: z.ZodLazy<
    z.ZodObject<{
      context: typeof BrowsingContext.BrowsingContextSchema;
      promptUnload: z.ZodOptional<z.ZodDefault<z.ZodBoolean>>;
    }>
  > = z.lazy(() =>
    z.object({
      context: BrowsingContext.BrowsingContextSchema,
      promptUnload: z.boolean().default(false).optional(),
    }),
  );
}
export namespace BrowsingContext {
  export const CloseResultSchema: z.ZodLazy<typeof EmptyResultSchema> = z.lazy(
    () => EmptyResultSchema,
  );
}
export namespace BrowsingContext {
  export const CreateSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'browsingContext.create'>;
      params: typeof BrowsingContext.CreateParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('browsingContext.create'),
      params: BrowsingContext.CreateParametersSchema,
    }),
  );
}
export namespace BrowsingContext {
  export const CreateTypeSchema: z.ZodLazy<
    z.ZodEnum<{tab: 'tab'; window: 'window'}>
  > = z.lazy(() => z.enum(['tab', 'window']));
}
export namespace BrowsingContext {
  export const CreateParametersSchema: z.ZodLazy<
    z.ZodObject<{
      type: typeof BrowsingContext.CreateTypeSchema;
      referenceContext: z.ZodOptional<
        typeof BrowsingContext.BrowsingContextSchema
      >;
      background: z.ZodOptional<z.ZodDefault<z.ZodBoolean>>;
      userContext: z.ZodOptional<typeof Browser.UserContextSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      type: BrowsingContext.CreateTypeSchema,
      referenceContext: BrowsingContext.BrowsingContextSchema.optional(),
      background: z.boolean().default(false).optional(),
      userContext: Browser.UserContextSchema.optional(),
    }),
  );
}
export namespace BrowsingContext {
  export const CreateResultSchema: z.ZodLazy<
    z.ZodObject<{
      context: typeof BrowsingContext.BrowsingContextSchema;
      userContext: z.ZodOptional<typeof Browser.UserContextSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      context: BrowsingContext.BrowsingContextSchema,
      userContext: Browser.UserContextSchema.optional(),
    }),
  );
}
export namespace BrowsingContext {
  export const GetTreeSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'browsingContext.getTree'>;
      params: typeof BrowsingContext.GetTreeParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('browsingContext.getTree'),
      params: BrowsingContext.GetTreeParametersSchema,
    }),
  );
}
export namespace BrowsingContext {
  export const GetTreeParametersSchema: z.ZodLazy<
    z.ZodObject<{
      maxDepth: z.ZodOptional<typeof JsUintSchema>;
      root: z.ZodOptional<typeof BrowsingContext.BrowsingContextSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      maxDepth: JsUintSchema.optional(),
      root: BrowsingContext.BrowsingContextSchema.optional(),
    }),
  );
}
export namespace BrowsingContext {
  export const GetTreeResultSchema: z.ZodLazy<
    z.ZodObject<{
      contexts: typeof BrowsingContext.InfoListSchema;
    }>
  > = z.lazy(() =>
    z.object({
      contexts: BrowsingContext.InfoListSchema,
    }),
  );
}
export namespace BrowsingContext {
  export const HandleUserPromptSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'browsingContext.handleUserPrompt'>;
      params: typeof BrowsingContext.HandleUserPromptParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('browsingContext.handleUserPrompt'),
      params: BrowsingContext.HandleUserPromptParametersSchema,
    }),
  );
}
export namespace BrowsingContext {
  export const HandleUserPromptParametersSchema: z.ZodLazy<
    z.ZodObject<{
      context: typeof BrowsingContext.BrowsingContextSchema;
      accept: z.ZodOptional<z.ZodBoolean>;
      userText: z.ZodOptional<z.ZodString>;
    }>
  > = z.lazy(() =>
    z.object({
      context: BrowsingContext.BrowsingContextSchema,
      accept: z.boolean().optional(),
      userText: z.string().optional(),
    }),
  );
}
export namespace BrowsingContext {
  export const HandleUserPromptResultSchema: z.ZodLazy<
    typeof EmptyResultSchema
  > = z.lazy(() => EmptyResultSchema);
}
export namespace BrowsingContext {
  export const LocateNodesSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'browsingContext.locateNodes'>;
      params: typeof BrowsingContext.LocateNodesParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('browsingContext.locateNodes'),
      params: BrowsingContext.LocateNodesParametersSchema,
    }),
  );
}
export namespace BrowsingContext {
  export const LocateNodesParametersSchema: z.ZodLazy<
    z.ZodObject<{
      context: typeof BrowsingContext.BrowsingContextSchema;
      locator: typeof BrowsingContext.LocatorSchema;
      maxNodeCount: z.ZodOptional<typeof JsUintSchema>;
      serializationOptions: z.ZodOptional<
        typeof Script.SerializationOptionsSchema
      >;
      startNodes: z.ZodOptional<
        z.ZodArray<typeof Script.SharedReferenceSchema>
      >;
    }>
  > = z.lazy(() =>
    z.object({
      context: BrowsingContext.BrowsingContextSchema,
      locator: BrowsingContext.LocatorSchema,
      maxNodeCount: JsUintSchema.gte(1).optional(),
      serializationOptions: Script.SerializationOptionsSchema.optional(),
      startNodes: z.array(Script.SharedReferenceSchema).nonempty().optional(),
    }),
  );
}
export namespace BrowsingContext {
  export const LocateNodesResultSchema: z.ZodLazy<
    z.ZodObject<{
      nodes: z.ZodArray<typeof Script.NodeRemoteValueSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      nodes: z.array(Script.NodeRemoteValueSchema),
    }),
  );
}
export namespace BrowsingContext {
  export const NavigateSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'browsingContext.navigate'>;
      params: typeof BrowsingContext.NavigateParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('browsingContext.navigate'),
      params: BrowsingContext.NavigateParametersSchema,
    }),
  );
}
export namespace BrowsingContext {
  export const NavigateParametersSchema: z.ZodLazy<
    z.ZodObject<{
      context: typeof BrowsingContext.BrowsingContextSchema;
      url: z.ZodString;
      wait: z.ZodOptional<typeof BrowsingContext.ReadinessStateSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      context: BrowsingContext.BrowsingContextSchema,
      url: z.string(),
      wait: BrowsingContext.ReadinessStateSchema.optional(),
    }),
  );
}
export namespace BrowsingContext {
  export const NavigateResultSchema: z.ZodLazy<
    z.ZodObject<{
      navigation: z.ZodUnion<
        readonly [typeof BrowsingContext.NavigationSchema, z.ZodNull]
      >;
      url: z.ZodString;
    }>
  > = z.lazy(() =>
    z.object({
      navigation: z.union([BrowsingContext.NavigationSchema, z.null()]),
      url: z.string(),
    }),
  );
}
export namespace BrowsingContext {
  export const PrintSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'browsingContext.print'>;
      params: typeof BrowsingContext.PrintParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('browsingContext.print'),
      params: BrowsingContext.PrintParametersSchema,
    }),
  );
}
export namespace BrowsingContext {
  export const PrintParametersSchema: z.ZodLazy<
    z.ZodObject<{
      context: typeof BrowsingContext.BrowsingContextSchema;
      background: z.ZodOptional<z.ZodDefault<z.ZodBoolean>>;
      margin: z.ZodOptional<typeof BrowsingContext.PrintMarginParametersSchema>;
      orientation: z.ZodOptional<
        z.ZodDefault<z.ZodEnum<{portrait: 'portrait'; landscape: 'landscape'}>>
      >;
      page: z.ZodOptional<typeof BrowsingContext.PrintPageParametersSchema>;
      pageRanges: z.ZodOptional<
        z.ZodArray<z.ZodUnion<readonly [typeof JsUintSchema, z.ZodString]>>
      >;
      scale: z.ZodOptional<z.ZodDefault<z.ZodNumber>>;
      shrinkToFit: z.ZodOptional<z.ZodDefault<z.ZodBoolean>>;
    }>
  > = z.lazy(() =>
    z.object({
      context: BrowsingContext.BrowsingContextSchema,
      background: z.boolean().default(false).optional(),
      margin: BrowsingContext.PrintMarginParametersSchema.optional(),
      orientation: z
        .enum(['portrait', 'landscape'])
        .default('portrait')
        .optional(),
      page: BrowsingContext.PrintPageParametersSchema.optional(),
      pageRanges: z.array(z.union([JsUintSchema, z.string()])).optional(),
      scale: z.number().gte(0.1).lte(2).default(1).optional(),
      shrinkToFit: z.boolean().default(true).optional(),
    }),
  );
}
export namespace BrowsingContext {
  export const PrintMarginParametersSchema: z.ZodLazy<
    z.ZodObject<{
      bottom: z.ZodOptional<z.ZodDefault<z.ZodNumber>>;
      left: z.ZodOptional<z.ZodDefault<z.ZodNumber>>;
      right: z.ZodOptional<z.ZodDefault<z.ZodNumber>>;
      top: z.ZodOptional<z.ZodDefault<z.ZodNumber>>;
    }>
  > = z.lazy(() =>
    z.object({
      bottom: z.number().gte(0).default(1).optional(),
      left: z.number().gte(0).default(1).optional(),
      right: z.number().gte(0).default(1).optional(),
      top: z.number().gte(0).default(1).optional(),
    }),
  );
}
export namespace BrowsingContext {
  export const PrintPageParametersSchema: z.ZodLazy<
    z.ZodObject<{
      height: z.ZodOptional<z.ZodDefault<z.ZodNumber>>;
      width: z.ZodOptional<z.ZodDefault<z.ZodNumber>>;
    }>
  > = z.lazy(() =>
    z.object({
      height: z.number().gte(0.0352).default(27.94).optional(),
      width: z.number().gte(0.0352).default(21.59).optional(),
    }),
  );
}
export namespace BrowsingContext {
  export const PrintResultSchema: z.ZodLazy<
    z.ZodObject<{
      data: z.ZodString;
    }>
  > = z.lazy(() =>
    z.object({
      data: z.string(),
    }),
  );
}
export namespace BrowsingContext {
  export const ReloadSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'browsingContext.reload'>;
      params: typeof BrowsingContext.ReloadParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('browsingContext.reload'),
      params: BrowsingContext.ReloadParametersSchema,
    }),
  );
}
export namespace BrowsingContext {
  export const ReloadParametersSchema: z.ZodLazy<
    z.ZodObject<{
      context: typeof BrowsingContext.BrowsingContextSchema;
      ignoreCache: z.ZodOptional<z.ZodBoolean>;
      wait: z.ZodOptional<typeof BrowsingContext.ReadinessStateSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      context: BrowsingContext.BrowsingContextSchema,
      ignoreCache: z.boolean().optional(),
      wait: BrowsingContext.ReadinessStateSchema.optional(),
    }),
  );
}
export namespace BrowsingContext {
  export const ReloadResultSchema: z.ZodLazy<
    typeof BrowsingContext.NavigateResultSchema
  > = z.lazy(() => BrowsingContext.NavigateResultSchema);
}
export namespace BrowsingContext {
  export const SetBypassCspSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'browsingContext.setBypassCSP'>;
      params: typeof BrowsingContext.SetBypassCspParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('browsingContext.setBypassCSP'),
      params: BrowsingContext.SetBypassCspParametersSchema,
    }),
  );
}
export namespace BrowsingContext {
  export const SetBypassCspParametersSchema: z.ZodLazy<
    z.ZodObject<{
      bypass: z.ZodUnion<readonly [z.ZodLiteral<true>, z.ZodNull]>;
      contexts: z.ZodOptional<
        z.ZodArray<typeof BrowsingContext.BrowsingContextSchema>
      >;
      userContexts: z.ZodOptional<z.ZodArray<typeof Browser.UserContextSchema>>;
    }>
  > = z.lazy(() =>
    z.object({
      bypass: z.union([z.literal(true), z.null()]),
      contexts: z
        .array(BrowsingContext.BrowsingContextSchema)
        .nonempty()
        .optional(),
      userContexts: z.array(Browser.UserContextSchema).nonempty().optional(),
    }),
  );
}
export namespace BrowsingContext {
  export const SetBypassCspResultSchema: z.ZodLazy<typeof EmptyResultSchema> =
    z.lazy(() => EmptyResultSchema);
}
export namespace BrowsingContext {
  export const SetViewportSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'browsingContext.setViewport'>;
      params: typeof BrowsingContext.SetViewportParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('browsingContext.setViewport'),
      params: BrowsingContext.SetViewportParametersSchema,
    }),
  );
}
export namespace BrowsingContext {
  export const SetViewportParametersSchema: z.ZodLazy<
    z.ZodObject<{
      context: z.ZodOptional<typeof BrowsingContext.BrowsingContextSchema>;
      viewport: z.ZodOptional<
        z.ZodUnion<readonly [typeof BrowsingContext.ViewportSchema, z.ZodNull]>
      >;
      devicePixelRatio: z.ZodOptional<
        z.ZodUnion<readonly [z.ZodNumber, z.ZodNull]>
      >;
      userContexts: z.ZodOptional<z.ZodArray<typeof Browser.UserContextSchema>>;
    }>
  > = z.lazy(() =>
    z.object({
      context: BrowsingContext.BrowsingContextSchema.optional(),
      viewport: z.union([BrowsingContext.ViewportSchema, z.null()]).optional(),
      devicePixelRatio: z.union([z.number().gt(0), z.null()]).optional(),
      userContexts: z.array(Browser.UserContextSchema).nonempty().optional(),
    }),
  );
}
export namespace BrowsingContext {
  export const ViewportSchema: z.ZodLazy<
    z.ZodObject<{
      width: typeof JsUintSchema;
      height: typeof JsUintSchema;
    }>
  > = z.lazy(() =>
    z.object({
      width: JsUintSchema,
      height: JsUintSchema,
    }),
  );
}
export namespace BrowsingContext {
  export const SetViewportResultSchema: z.ZodLazy<typeof EmptyResultSchema> =
    z.lazy(() => EmptyResultSchema);
}
export namespace BrowsingContext {
  export const StartScreencastSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'browsingContext.startScreencast'>;
      params: typeof BrowsingContext.StartScreencastParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('browsingContext.startScreencast'),
      params: BrowsingContext.StartScreencastParametersSchema,
    }),
  );
}
export namespace BrowsingContext {
  export const StartScreencastParametersSchema: z.ZodLazy<
    z.ZodObject<{
      context: typeof BrowsingContext.BrowsingContextSchema;
      destinationFolder: z.ZodOptional<z.ZodString>;
      mimeType: z.ZodOptional<z.ZodString>;
      video: z.ZodOptional<typeof BrowsingContext.MediaTrackConstraintsSchema>;
      audio: z.ZodOptional<z.ZodDefault<z.ZodBoolean>>;
    }>
  > = z.lazy(() =>
    z.object({
      context: BrowsingContext.BrowsingContextSchema,
      destinationFolder: z.string().optional(),
      mimeType: z.string().optional(),
      video: BrowsingContext.MediaTrackConstraintsSchema.optional(),
      audio: z.boolean().default(false).optional(),
    }),
  );
}
export namespace BrowsingContext {
  export const MediaTrackConstraintsSchema: z.ZodLazy<
    z.ZodObject<{
      width: z.ZodOptional<typeof JsUintSchema>;
      height: z.ZodOptional<typeof JsUintSchema>;
      frameRate: z.ZodOptional<typeof JsUintSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      width: JsUintSchema.optional(),
      height: JsUintSchema.optional(),
      frameRate: JsUintSchema.optional(),
    }),
  );
}
export namespace BrowsingContext {
  export const StartScreencastResultSchema: z.ZodLazy<
    z.ZodObject<{
      screencast: typeof BrowsingContext.ScreencastSchema;
      path: z.ZodString;
    }>
  > = z.lazy(() =>
    z.object({
      screencast: BrowsingContext.ScreencastSchema,
      path: z.string(),
    }),
  );
}
export namespace BrowsingContext {
  export const ScreencastSchema: z.ZodLazy<z.ZodString> = z.lazy(() =>
    z.string(),
  );
}
export namespace BrowsingContext {
  export const StopScreencastSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'browsingContext.stopScreencast'>;
      params: typeof BrowsingContext.StopScreencastParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('browsingContext.stopScreencast'),
      params: BrowsingContext.StopScreencastParametersSchema,
    }),
  );
}
export namespace BrowsingContext {
  export const StopScreencastParametersSchema: z.ZodLazy<
    z.ZodObject<{
      screencast: typeof BrowsingContext.ScreencastSchema;
    }>
  > = z.lazy(() =>
    z.object({
      screencast: BrowsingContext.ScreencastSchema,
    }),
  );
}
export namespace BrowsingContext {
  export const StopScreencastResultSchema: z.ZodLazy<
    z.ZodObject<{
      path: z.ZodString;
      error: z.ZodOptional<z.ZodString>;
    }>
  > = z.lazy(() =>
    z.object({
      path: z.string(),
      error: z.string().optional(),
    }),
  );
}
export namespace BrowsingContext {
  export const TraverseHistorySchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'browsingContext.traverseHistory'>;
      params: typeof BrowsingContext.TraverseHistoryParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('browsingContext.traverseHistory'),
      params: BrowsingContext.TraverseHistoryParametersSchema,
    }),
  );
}
export namespace BrowsingContext {
  export const TraverseHistoryParametersSchema: z.ZodLazy<
    z.ZodObject<{
      context: typeof BrowsingContext.BrowsingContextSchema;
      delta: typeof JsIntSchema;
    }>
  > = z.lazy(() =>
    z.object({
      context: BrowsingContext.BrowsingContextSchema,
      delta: JsIntSchema,
    }),
  );
}
export namespace BrowsingContext {
  export const TraverseHistoryResultSchema: z.ZodLazy<
    typeof EmptyResultSchema
  > = z.lazy(() => EmptyResultSchema);
}
export namespace BrowsingContext {
  export const ContextCreatedSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'browsingContext.contextCreated'>;
      params: typeof BrowsingContext.InfoSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('browsingContext.contextCreated'),
      params: BrowsingContext.InfoSchema,
    }),
  );
}
export namespace BrowsingContext {
  export const ContextDestroyedSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'browsingContext.contextDestroyed'>;
      params: typeof BrowsingContext.InfoSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('browsingContext.contextDestroyed'),
      params: BrowsingContext.InfoSchema,
    }),
  );
}
export namespace BrowsingContext {
  export const NavigationStartedSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'browsingContext.navigationStarted'>;
      params: typeof BrowsingContext.NavigationInfoSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('browsingContext.navigationStarted'),
      params: BrowsingContext.NavigationInfoSchema,
    }),
  );
}
export namespace BrowsingContext {
  export const FragmentNavigatedSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'browsingContext.fragmentNavigated'>;
      params: typeof BrowsingContext.NavigationInfoSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('browsingContext.fragmentNavigated'),
      params: BrowsingContext.NavigationInfoSchema,
    }),
  );
}
export namespace BrowsingContext {
  export const HistoryUpdatedSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'browsingContext.historyUpdated'>;
      params: typeof BrowsingContext.HistoryUpdatedParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('browsingContext.historyUpdated'),
      params: BrowsingContext.HistoryUpdatedParametersSchema,
    }),
  );
}
export namespace BrowsingContext {
  export const HistoryUpdatedParametersSchema: z.ZodLazy<
    z.ZodObject<{
      context: typeof BrowsingContext.BrowsingContextSchema;
      timestamp: typeof JsUintSchema;
      url: z.ZodString;
      userContext: z.ZodOptional<typeof Browser.UserContextSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      context: BrowsingContext.BrowsingContextSchema,
      timestamp: JsUintSchema,
      url: z.string(),
      userContext: Browser.UserContextSchema.optional(),
    }),
  );
}
export namespace BrowsingContext {
  export const DomContentLoadedSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'browsingContext.domContentLoaded'>;
      params: typeof BrowsingContext.NavigationInfoSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('browsingContext.domContentLoaded'),
      params: BrowsingContext.NavigationInfoSchema,
    }),
  );
}
export namespace BrowsingContext {
  export const LoadSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'browsingContext.load'>;
      params: typeof BrowsingContext.NavigationInfoSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('browsingContext.load'),
      params: BrowsingContext.NavigationInfoSchema,
    }),
  );
}
export namespace BrowsingContext {
  export const DownloadWillBeginSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'browsingContext.downloadWillBegin'>;
      params: typeof BrowsingContext.DownloadWillBeginParamsSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('browsingContext.downloadWillBegin'),
      params: BrowsingContext.DownloadWillBeginParamsSchema,
    }),
  );
}
export namespace BrowsingContext {
  export const DownloadWillBeginParamsSchema: z.ZodLazy<
    z.ZodIntersection<
      z.ZodObject<{
        download: typeof BrowsingContext.DownloadSchema;
        suggestedFilename: z.ZodString;
      }>,
      typeof BrowsingContext.BaseNavigationInfoSchema
    >
  > = z.lazy(() =>
    z
      .object({
        download: BrowsingContext.DownloadSchema,
        suggestedFilename: z.string(),
      })
      .and(BrowsingContext.BaseNavigationInfoSchema),
  );
}
export namespace BrowsingContext {
  export const DownloadEndSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'browsingContext.downloadEnd'>;
      params: typeof BrowsingContext.DownloadEndParamsSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('browsingContext.downloadEnd'),
      params: BrowsingContext.DownloadEndParamsSchema,
    }),
  );
}
export namespace BrowsingContext {
  export const DownloadEndParamsSchema: z.ZodLazy<
    z.ZodUnion<
      readonly [
        typeof BrowsingContext.DownloadCanceledParamsSchema,
        typeof BrowsingContext.DownloadCompleteParamsSchema,
      ]
    >
  > = z.lazy(() =>
    z.union([
      BrowsingContext.DownloadCanceledParamsSchema,
      BrowsingContext.DownloadCompleteParamsSchema,
    ]),
  );
}
export namespace BrowsingContext {
  export const DownloadCanceledParamsSchema: z.ZodLazy<
    z.ZodIntersection<
      z.ZodObject<{
        status: z.ZodLiteral<'canceled'>;
        download: typeof BrowsingContext.DownloadSchema;
      }>,
      typeof BrowsingContext.BaseNavigationInfoSchema
    >
  > = z.lazy(() =>
    z
      .object({
        status: z.literal('canceled'),
        download: BrowsingContext.DownloadSchema,
      })
      .and(BrowsingContext.BaseNavigationInfoSchema),
  );
}
export namespace BrowsingContext {
  export const DownloadCompleteParamsSchema: z.ZodLazy<
    z.ZodIntersection<
      z.ZodObject<{
        status: z.ZodLiteral<'complete'>;
        download: typeof BrowsingContext.DownloadSchema;
        filepath: z.ZodUnion<readonly [z.ZodString, z.ZodNull]>;
      }>,
      typeof BrowsingContext.BaseNavigationInfoSchema
    >
  > = z.lazy(() =>
    z
      .object({
        status: z.literal('complete'),
        download: BrowsingContext.DownloadSchema,
        filepath: z.union([z.string(), z.null()]),
      })
      .and(BrowsingContext.BaseNavigationInfoSchema),
  );
}
export namespace BrowsingContext {
  export const NavigationAbortedSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'browsingContext.navigationAborted'>;
      params: typeof BrowsingContext.NavigationInfoSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('browsingContext.navigationAborted'),
      params: BrowsingContext.NavigationInfoSchema,
    }),
  );
}
export namespace BrowsingContext {
  export const NavigationCommittedSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'browsingContext.navigationCommitted'>;
      params: typeof BrowsingContext.NavigationInfoSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('browsingContext.navigationCommitted'),
      params: BrowsingContext.NavigationInfoSchema,
    }),
  );
}
export namespace BrowsingContext {
  export const NavigationFailedSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'browsingContext.navigationFailed'>;
      params: typeof BrowsingContext.NavigationInfoSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('browsingContext.navigationFailed'),
      params: BrowsingContext.NavigationInfoSchema,
    }),
  );
}
export namespace BrowsingContext {
  export const UserPromptClosedSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'browsingContext.userPromptClosed'>;
      params: typeof BrowsingContext.UserPromptClosedParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('browsingContext.userPromptClosed'),
      params: BrowsingContext.UserPromptClosedParametersSchema,
    }),
  );
}
export namespace BrowsingContext {
  export const UserPromptClosedParametersSchema: z.ZodLazy<
    z.ZodObject<{
      context: typeof BrowsingContext.BrowsingContextSchema;
      accepted: z.ZodBoolean;
      type: typeof BrowsingContext.UserPromptTypeSchema;
      userContext: z.ZodOptional<typeof Browser.UserContextSchema>;
      userText: z.ZodOptional<z.ZodString>;
    }>
  > = z.lazy(() =>
    z.object({
      context: BrowsingContext.BrowsingContextSchema,
      accepted: z.boolean(),
      type: BrowsingContext.UserPromptTypeSchema,
      userContext: Browser.UserContextSchema.optional(),
      userText: z.string().optional(),
    }),
  );
}
export namespace BrowsingContext {
  export const UserPromptOpenedSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'browsingContext.userPromptOpened'>;
      params: typeof BrowsingContext.UserPromptOpenedParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('browsingContext.userPromptOpened'),
      params: BrowsingContext.UserPromptOpenedParametersSchema,
    }),
  );
}
export namespace BrowsingContext {
  export const UserPromptOpenedParametersSchema: z.ZodLazy<
    z.ZodObject<{
      context: typeof BrowsingContext.BrowsingContextSchema;
      handler: typeof Session.UserPromptHandlerTypeSchema;
      message: z.ZodString;
      type: typeof BrowsingContext.UserPromptTypeSchema;
      userContext: z.ZodOptional<typeof Browser.UserContextSchema>;
      defaultValue: z.ZodOptional<z.ZodString>;
    }>
  > = z.lazy(() =>
    z.object({
      context: BrowsingContext.BrowsingContextSchema,
      handler: Session.UserPromptHandlerTypeSchema,
      message: z.string(),
      type: BrowsingContext.UserPromptTypeSchema,
      userContext: Browser.UserContextSchema.optional(),
      defaultValue: z.string().optional(),
    }),
  );
}
export const EmulationCommandSchema: z.ZodLazy<
  z.ZodUnion<
    readonly [
      typeof Emulation.SetForcedColorsModeThemeOverrideSchema,
      typeof Emulation.SetGeolocationOverrideSchema,
      typeof Emulation.SetLocaleOverrideSchema,
      typeof Emulation.SetMediaFeaturesOverrideSchema,
      typeof Emulation.SetNetworkConditionsSchema,
      typeof Emulation.SetScreenOrientationOverrideSchema,
      typeof Emulation.SetScreenSettingsOverrideSchema,
      typeof Emulation.SetScriptingEnabledSchema,
      typeof Emulation.SetScrollbarTypeOverrideSchema,
      typeof Emulation.SetTextLayoutModeOverrideSchema,
      typeof Emulation.SetTimezoneOverrideSchema,
      typeof Emulation.SetTouchOverrideSchema,
      typeof Emulation.SetUserAgentOverrideSchema,
      typeof Emulation.SetViewportMetaOverrideSchema,
    ]
  >
> = z.lazy(() =>
  z.union([
    Emulation.SetForcedColorsModeThemeOverrideSchema,
    Emulation.SetGeolocationOverrideSchema,
    Emulation.SetLocaleOverrideSchema,
    Emulation.SetMediaFeaturesOverrideSchema,
    Emulation.SetNetworkConditionsSchema,
    Emulation.SetScreenOrientationOverrideSchema,
    Emulation.SetScreenSettingsOverrideSchema,
    Emulation.SetScriptingEnabledSchema,
    Emulation.SetScrollbarTypeOverrideSchema,
    Emulation.SetTextLayoutModeOverrideSchema,
    Emulation.SetTimezoneOverrideSchema,
    Emulation.SetTouchOverrideSchema,
    Emulation.SetUserAgentOverrideSchema,
    Emulation.SetViewportMetaOverrideSchema,
  ]),
);
export const EmulationResultSchema: z.ZodLazy<
  z.ZodUnion<
    readonly [
      typeof Emulation.SetForcedColorsModeThemeOverrideResultSchema,
      typeof Emulation.SetGeolocationOverrideResultSchema,
      typeof Emulation.SetLocaleOverrideResultSchema,
      typeof Emulation.SetMediaFeaturesOverrideResultSchema,
      typeof Emulation.SetScreenOrientationOverrideResultSchema,
      typeof Emulation.SetScriptingEnabledResultSchema,
      typeof Emulation.SetScrollbarTypeOverrideResultSchema,
      typeof Emulation.SetTextLayoutModeOverrideResultSchema,
      typeof Emulation.SetTimezoneOverrideResultSchema,
      typeof Emulation.SetTouchOverrideResultSchema,
      typeof Emulation.SetUserAgentOverrideResultSchema,
      typeof Emulation.SetViewportMetaOverrideResultSchema,
    ]
  >
> = z.lazy(() =>
  z.union([
    Emulation.SetForcedColorsModeThemeOverrideResultSchema,
    Emulation.SetGeolocationOverrideResultSchema,
    Emulation.SetLocaleOverrideResultSchema,
    Emulation.SetMediaFeaturesOverrideResultSchema,
    Emulation.SetScreenOrientationOverrideResultSchema,
    Emulation.SetScriptingEnabledResultSchema,
    Emulation.SetScrollbarTypeOverrideResultSchema,
    Emulation.SetTextLayoutModeOverrideResultSchema,
    Emulation.SetTimezoneOverrideResultSchema,
    Emulation.SetTouchOverrideResultSchema,
    Emulation.SetUserAgentOverrideResultSchema,
    Emulation.SetViewportMetaOverrideResultSchema,
  ]),
);
export namespace Emulation {
  export const SetForcedColorsModeThemeOverrideSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'emulation.setForcedColorsModeThemeOverride'>;
      params: typeof Emulation.SetForcedColorsModeThemeOverrideParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('emulation.setForcedColorsModeThemeOverride'),
      params: Emulation.SetForcedColorsModeThemeOverrideParametersSchema,
    }),
  );
}
export namespace Emulation {
  export const SetForcedColorsModeThemeOverrideParametersSchema: z.ZodLazy<
    z.ZodObject<{
      theme: z.ZodUnion<
        readonly [typeof Emulation.ForcedColorsModeThemeSchema, z.ZodNull]
      >;
      contexts: z.ZodOptional<
        z.ZodArray<typeof BrowsingContext.BrowsingContextSchema>
      >;
      userContexts: z.ZodOptional<z.ZodArray<typeof Browser.UserContextSchema>>;
    }>
  > = z.lazy(() =>
    z.object({
      theme: z.union([Emulation.ForcedColorsModeThemeSchema, z.null()]),
      contexts: z
        .array(BrowsingContext.BrowsingContextSchema)
        .nonempty()
        .optional(),
      userContexts: z.array(Browser.UserContextSchema).nonempty().optional(),
    }),
  );
}
export namespace Emulation {
  export const ForcedColorsModeThemeSchema: z.ZodLazy<
    z.ZodEnum<{light: 'light'; dark: 'dark'}>
  > = z.lazy(() => z.enum(['light', 'dark']));
}
export namespace Emulation {
  export const SetForcedColorsModeThemeOverrideResultSchema: z.ZodLazy<
    typeof EmptyResultSchema
  > = z.lazy(() => EmptyResultSchema);
}
export namespace Emulation {
  export const SetGeolocationOverrideSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'emulation.setGeolocationOverride'>;
      params: typeof Emulation.SetGeolocationOverrideParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('emulation.setGeolocationOverride'),
      params: Emulation.SetGeolocationOverrideParametersSchema,
    }),
  );
}
export namespace Emulation {
  export const SetGeolocationOverrideParametersSchema: z.ZodLazy<
    z.ZodIntersection<
      z.ZodUnion<
        readonly [
          z.ZodObject<{
            coordinates: z.ZodUnion<
              readonly [
                typeof Emulation.GeolocationCoordinatesSchema,
                z.ZodNull,
              ]
            >;
          }>,
          z.ZodObject<{
            error: typeof Emulation.GeolocationPositionErrorSchema;
          }>,
        ]
      >,
      z.ZodObject<{
        contexts: z.ZodOptional<
          z.ZodArray<typeof BrowsingContext.BrowsingContextSchema>
        >;
        userContexts: z.ZodOptional<
          z.ZodArray<typeof Browser.UserContextSchema>
        >;
      }>
    >
  > = z.lazy(() =>
    z
      .union([
        z.object({
          coordinates: z.union([
            Emulation.GeolocationCoordinatesSchema,
            z.null(),
          ]),
        }),
        z.object({
          error: Emulation.GeolocationPositionErrorSchema,
        }),
      ])
      .and(
        z.object({
          contexts: z
            .array(BrowsingContext.BrowsingContextSchema)
            .nonempty()
            .optional(),
          userContexts: z
            .array(Browser.UserContextSchema)
            .nonempty()
            .optional(),
        }),
      ),
  );
}
export namespace Emulation {
  export const GeolocationCoordinatesSchema: z.ZodLazy<
    z.ZodObject<{
      latitude: z.ZodNumber;
      longitude: z.ZodNumber;
      accuracy: z.ZodOptional<z.ZodDefault<z.ZodNumber>>;
      altitude: z.ZodOptional<
        z.ZodUnion<readonly [z.ZodNumber, z.ZodDefault<z.ZodNull>]>
      >;
      altitudeAccuracy: z.ZodOptional<
        z.ZodUnion<readonly [z.ZodNumber, z.ZodDefault<z.ZodNull>]>
      >;
      heading: z.ZodOptional<
        z.ZodUnion<readonly [z.ZodNumber, z.ZodDefault<z.ZodNull>]>
      >;
      speed: z.ZodOptional<
        z.ZodUnion<readonly [z.ZodNumber, z.ZodDefault<z.ZodNull>]>
      >;
    }>
  > = z.lazy(() =>
    z.object({
      latitude: z.number().gte(-90).lte(90),
      longitude: z.number().gte(-180).lte(180),
      accuracy: z.number().gte(0).default(1).optional(),
      altitude: z.union([z.number(), z.null().default(null)]).optional(),
      altitudeAccuracy: z
        .union([z.number().gte(0), z.null().default(null)])
        .optional(),
      heading: z
        .union([z.number().gt(0).lt(360), z.null().default(null)])
        .optional(),
      speed: z.union([z.number().gte(0), z.null().default(null)]).optional(),
    }),
  );
}
export namespace Emulation {
  export const GeolocationPositionErrorSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'positionUnavailable'>;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('positionUnavailable'),
    }),
  );
}
export namespace Emulation {
  export const SetGeolocationOverrideResultSchema: z.ZodLazy<
    typeof EmptyResultSchema
  > = z.lazy(() => EmptyResultSchema);
}
export namespace Emulation {
  export const SetLocaleOverrideSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'emulation.setLocaleOverride'>;
      params: typeof Emulation.SetLocaleOverrideParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('emulation.setLocaleOverride'),
      params: Emulation.SetLocaleOverrideParametersSchema,
    }),
  );
}
export namespace Emulation {
  export const SetLocaleOverrideParametersSchema: z.ZodLazy<
    z.ZodObject<{
      locale: z.ZodUnion<readonly [z.ZodString, z.ZodNull]>;
      contexts: z.ZodOptional<
        z.ZodArray<typeof BrowsingContext.BrowsingContextSchema>
      >;
      userContexts: z.ZodOptional<z.ZodArray<typeof Browser.UserContextSchema>>;
    }>
  > = z.lazy(() =>
    z.object({
      locale: z.union([z.string(), z.null()]),
      contexts: z
        .array(BrowsingContext.BrowsingContextSchema)
        .nonempty()
        .optional(),
      userContexts: z.array(Browser.UserContextSchema).nonempty().optional(),
    }),
  );
}
export namespace Emulation {
  export const SetLocaleOverrideResultSchema: z.ZodLazy<
    typeof EmptyResultSchema
  > = z.lazy(() => EmptyResultSchema);
}
export namespace Emulation {
  export const SetMediaFeaturesOverrideSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'emulation.setMediaFeaturesOverride'>;
      params: typeof Emulation.SetMediaFeaturesOverrideParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('emulation.setMediaFeaturesOverride'),
      params: Emulation.SetMediaFeaturesOverrideParametersSchema,
    }),
  );
}
export namespace Emulation {
  export const SetMediaFeaturesOverrideParametersSchema: z.ZodLazy<
    z.ZodObject<{
      features: z.ZodUnion<
        readonly [typeof Emulation.MediaFeaturesSchema, z.ZodNull]
      >;
      contexts: z.ZodOptional<
        z.ZodArray<typeof BrowsingContext.BrowsingContextSchema>
      >;
      userContexts: z.ZodOptional<z.ZodArray<typeof Browser.UserContextSchema>>;
    }>
  > = z.lazy(() =>
    z.object({
      features: z.union([Emulation.MediaFeaturesSchema, z.null()]),
      contexts: z
        .array(BrowsingContext.BrowsingContextSchema)
        .nonempty()
        .optional(),
      userContexts: z.array(Browser.UserContextSchema).nonempty().optional(),
    }),
  );
}
export namespace Emulation {
  export const MediaFeaturesSchema: z.ZodLazy<
    z.ZodObject<{
      'any-hover': z.ZodOptional<
        z.ZodUnion<
          readonly [z.ZodLiteral<'none'>, z.ZodLiteral<'hover'>, z.ZodNull]
        >
      >;
      'any-pointer': z.ZodOptional<
        z.ZodUnion<
          readonly [
            z.ZodLiteral<'none'>,
            z.ZodLiteral<'coarse'>,
            z.ZodLiteral<'fine'>,
            z.ZodNull,
          ]
        >
      >;
      color: z.ZodOptional<
        z.ZodUnion<readonly [typeof JsUintSchema, z.ZodNull]>
      >;
      'color-gamut': z.ZodOptional<
        z.ZodUnion<
          readonly [
            z.ZodLiteral<'srgb'>,
            z.ZodLiteral<'p3'>,
            z.ZodLiteral<'rec2020'>,
            z.ZodNull,
          ]
        >
      >;
      'color-index': z.ZodOptional<
        z.ZodUnion<readonly [typeof JsUintSchema, z.ZodNull]>
      >;
      'display-mode': z.ZodOptional<
        z.ZodUnion<
          readonly [
            z.ZodLiteral<'fullscreen'>,
            z.ZodLiteral<'standalone'>,
            z.ZodLiteral<'minimal-ui'>,
            z.ZodLiteral<'browser'>,
            z.ZodLiteral<'picture-in-picture'>,
            z.ZodNull,
          ]
        >
      >;
      'dynamic-range': z.ZodOptional<
        z.ZodUnion<
          readonly [z.ZodLiteral<'standard'>, z.ZodLiteral<'high'>, z.ZodNull]
        >
      >;
      'environment-blending': z.ZodOptional<
        z.ZodUnion<
          readonly [
            z.ZodLiteral<'opaque'>,
            z.ZodLiteral<'additive'>,
            z.ZodLiteral<'subtractive'>,
            z.ZodNull,
          ]
        >
      >;
      'forced-colors': z.ZodOptional<
        z.ZodUnion<
          readonly [z.ZodLiteral<'none'>, z.ZodLiteral<'active'>, z.ZodNull]
        >
      >;
      grid: z.ZodOptional<
        z.ZodUnion<readonly [z.ZodLiteral<0>, z.ZodLiteral<1>, z.ZodNull]>
      >;
      'horizontal-viewport-segments': z.ZodOptional<
        z.ZodUnion<readonly [typeof JsUintSchema, z.ZodNull]>
      >;
      hover: z.ZodOptional<
        z.ZodUnion<
          readonly [z.ZodLiteral<'none'>, z.ZodLiteral<'hover'>, z.ZodNull]
        >
      >;
      'inverted-colors': z.ZodOptional<
        z.ZodUnion<
          readonly [z.ZodLiteral<'none'>, z.ZodLiteral<'inverted'>, z.ZodNull]
        >
      >;
      monochrome: z.ZodOptional<
        z.ZodUnion<readonly [typeof JsUintSchema, z.ZodNull]>
      >;
      'nav-controls': z.ZodOptional<
        z.ZodUnion<
          readonly [z.ZodLiteral<'none'>, z.ZodLiteral<'back'>, z.ZodNull]
        >
      >;
      'overflow-block': z.ZodOptional<
        z.ZodUnion<
          readonly [
            z.ZodLiteral<'none'>,
            z.ZodLiteral<'scroll'>,
            z.ZodLiteral<'optional-paged'>,
            z.ZodLiteral<'paged'>,
            z.ZodNull,
          ]
        >
      >;
      'overflow-inline': z.ZodOptional<
        z.ZodUnion<
          readonly [z.ZodLiteral<'none'>, z.ZodLiteral<'scroll'>, z.ZodNull]
        >
      >;
      pointer: z.ZodOptional<
        z.ZodUnion<
          readonly [
            z.ZodLiteral<'none'>,
            z.ZodLiteral<'coarse'>,
            z.ZodLiteral<'fine'>,
            z.ZodNull,
          ]
        >
      >;
      'prefers-color-scheme': z.ZodOptional<
        z.ZodUnion<
          readonly [z.ZodLiteral<'light'>, z.ZodLiteral<'dark'>, z.ZodNull]
        >
      >;
      'prefers-contrast': z.ZodOptional<
        z.ZodUnion<
          readonly [
            z.ZodLiteral<'no-preference'>,
            z.ZodLiteral<'more'>,
            z.ZodLiteral<'less'>,
            z.ZodLiteral<'custom'>,
            z.ZodNull,
          ]
        >
      >;
      'prefers-reduced-data': z.ZodOptional<
        z.ZodUnion<
          readonly [
            z.ZodLiteral<'no-preference'>,
            z.ZodLiteral<'reduce'>,
            z.ZodNull,
          ]
        >
      >;
      'prefers-reduced-motion': z.ZodOptional<
        z.ZodUnion<
          readonly [
            z.ZodLiteral<'no-preference'>,
            z.ZodLiteral<'reduce'>,
            z.ZodNull,
          ]
        >
      >;
      'prefers-reduced-transparency': z.ZodOptional<
        z.ZodUnion<
          readonly [
            z.ZodLiteral<'no-preference'>,
            z.ZodLiteral<'reduce'>,
            z.ZodNull,
          ]
        >
      >;
      scan: z.ZodOptional<
        z.ZodUnion<
          readonly [
            z.ZodLiteral<'interlace'>,
            z.ZodLiteral<'progressive'>,
            z.ZodNull,
          ]
        >
      >;
      scripting: z.ZodOptional<
        z.ZodUnion<
          readonly [
            z.ZodLiteral<'none'>,
            z.ZodLiteral<'initial-only'>,
            z.ZodLiteral<'enabled'>,
            z.ZodNull,
          ]
        >
      >;
      update: z.ZodOptional<
        z.ZodUnion<
          readonly [
            z.ZodLiteral<'none'>,
            z.ZodLiteral<'slow'>,
            z.ZodLiteral<'fast'>,
            z.ZodNull,
          ]
        >
      >;
      'vertical-viewport-segments': z.ZodOptional<
        z.ZodUnion<readonly [typeof JsUintSchema, z.ZodNull]>
      >;
      'video-color-gamut': z.ZodOptional<
        z.ZodUnion<
          readonly [
            z.ZodLiteral<'srgb'>,
            z.ZodLiteral<'p3'>,
            z.ZodLiteral<'rec2020'>,
            z.ZodNull,
          ]
        >
      >;
      'video-dynamic-range': z.ZodOptional<
        z.ZodUnion<
          readonly [z.ZodLiteral<'standard'>, z.ZodLiteral<'high'>, z.ZodNull]
        >
      >;
    }>
  > = z.lazy(() =>
    z.object({
      'any-hover': z
        .union([z.literal('none'), z.literal('hover'), z.null()])
        .optional(),
      'any-pointer': z
        .union([
          z.literal('none'),
          z.literal('coarse'),
          z.literal('fine'),
          z.null(),
        ])
        .optional(),
      color: z.union([JsUintSchema, z.null()]).optional(),
      'color-gamut': z
        .union([
          z.literal('srgb'),
          z.literal('p3'),
          z.literal('rec2020'),
          z.null(),
        ])
        .optional(),
      'color-index': z.union([JsUintSchema, z.null()]).optional(),
      'display-mode': z
        .union([
          z.literal('fullscreen'),
          z.literal('standalone'),
          z.literal('minimal-ui'),
          z.literal('browser'),
          z.literal('picture-in-picture'),
          z.null(),
        ])
        .optional(),
      'dynamic-range': z
        .union([z.literal('standard'), z.literal('high'), z.null()])
        .optional(),
      'environment-blending': z
        .union([
          z.literal('opaque'),
          z.literal('additive'),
          z.literal('subtractive'),
          z.null(),
        ])
        .optional(),
      'forced-colors': z
        .union([z.literal('none'), z.literal('active'), z.null()])
        .optional(),
      grid: z.union([z.literal(0), z.literal(1), z.null()]).optional(),
      'horizontal-viewport-segments': z
        .union([JsUintSchema, z.null()])
        .optional(),
      hover: z
        .union([z.literal('none'), z.literal('hover'), z.null()])
        .optional(),
      'inverted-colors': z
        .union([z.literal('none'), z.literal('inverted'), z.null()])
        .optional(),
      monochrome: z.union([JsUintSchema, z.null()]).optional(),
      'nav-controls': z
        .union([z.literal('none'), z.literal('back'), z.null()])
        .optional(),
      'overflow-block': z
        .union([
          z.literal('none'),
          z.literal('scroll'),
          z.literal('optional-paged'),
          z.literal('paged'),
          z.null(),
        ])
        .optional(),
      'overflow-inline': z
        .union([z.literal('none'), z.literal('scroll'), z.null()])
        .optional(),
      pointer: z
        .union([
          z.literal('none'),
          z.literal('coarse'),
          z.literal('fine'),
          z.null(),
        ])
        .optional(),
      'prefers-color-scheme': z
        .union([z.literal('light'), z.literal('dark'), z.null()])
        .optional(),
      'prefers-contrast': z
        .union([
          z.literal('no-preference'),
          z.literal('more'),
          z.literal('less'),
          z.literal('custom'),
          z.null(),
        ])
        .optional(),
      'prefers-reduced-data': z
        .union([z.literal('no-preference'), z.literal('reduce'), z.null()])
        .optional(),
      'prefers-reduced-motion': z
        .union([z.literal('no-preference'), z.literal('reduce'), z.null()])
        .optional(),
      'prefers-reduced-transparency': z
        .union([z.literal('no-preference'), z.literal('reduce'), z.null()])
        .optional(),
      scan: z
        .union([z.literal('interlace'), z.literal('progressive'), z.null()])
        .optional(),
      scripting: z
        .union([
          z.literal('none'),
          z.literal('initial-only'),
          z.literal('enabled'),
          z.null(),
        ])
        .optional(),
      update: z
        .union([
          z.literal('none'),
          z.literal('slow'),
          z.literal('fast'),
          z.null(),
        ])
        .optional(),
      'vertical-viewport-segments': z
        .union([JsUintSchema, z.null()])
        .optional(),
      'video-color-gamut': z
        .union([
          z.literal('srgb'),
          z.literal('p3'),
          z.literal('rec2020'),
          z.null(),
        ])
        .optional(),
      'video-dynamic-range': z
        .union([z.literal('standard'), z.literal('high'), z.null()])
        .optional(),
    }),
  );
}
export namespace Emulation {
  export const SetMediaFeaturesOverrideResultSchema: z.ZodLazy<
    typeof EmptyResultSchema
  > = z.lazy(() => EmptyResultSchema);
}
export namespace Emulation {
  export const SetNetworkConditionsSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'emulation.setNetworkConditions'>;
      params: typeof Emulation.SetNetworkConditionsParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('emulation.setNetworkConditions'),
      params: Emulation.SetNetworkConditionsParametersSchema,
    }),
  );
}
export namespace Emulation {
  export const SetNetworkConditionsParametersSchema: z.ZodLazy<
    z.ZodObject<{
      networkConditions: z.ZodUnion<
        readonly [typeof Emulation.NetworkConditionsSchema, z.ZodNull]
      >;
      contexts: z.ZodOptional<
        z.ZodArray<typeof BrowsingContext.BrowsingContextSchema>
      >;
      userContexts: z.ZodOptional<z.ZodArray<typeof Browser.UserContextSchema>>;
    }>
  > = z.lazy(() =>
    z.object({
      networkConditions: z.union([Emulation.NetworkConditionsSchema, z.null()]),
      contexts: z
        .array(BrowsingContext.BrowsingContextSchema)
        .nonempty()
        .optional(),
      userContexts: z.array(Browser.UserContextSchema).nonempty().optional(),
    }),
  );
}
export namespace Emulation {
  export const NetworkConditionsSchema: z.ZodLazy<
    typeof Emulation.NetworkConditionsOfflineSchema
  > = z.lazy(() => Emulation.NetworkConditionsOfflineSchema);
}
export namespace Emulation {
  export const NetworkConditionsOfflineSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'offline'>;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('offline'),
    }),
  );
}
export namespace Emulation {
  export const SetNetworkConditionsResultSchema: z.ZodLazy<
    typeof EmptyResultSchema
  > = z.lazy(() => EmptyResultSchema);
}
export namespace Emulation {
  export const SetScreenSettingsOverrideSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'emulation.setScreenSettingsOverride'>;
      params: typeof Emulation.SetScreenSettingsOverrideParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('emulation.setScreenSettingsOverride'),
      params: Emulation.SetScreenSettingsOverrideParametersSchema,
    }),
  );
}
export namespace Emulation {
  export const ScreenAreaSchema: z.ZodLazy<
    z.ZodObject<{
      width: typeof JsUintSchema;
      height: typeof JsUintSchema;
    }>
  > = z.lazy(() =>
    z.object({
      width: JsUintSchema,
      height: JsUintSchema,
    }),
  );
}
export namespace Emulation {
  export const SetScreenSettingsOverrideParametersSchema: z.ZodLazy<
    z.ZodObject<{
      screenArea: z.ZodUnion<
        readonly [typeof Emulation.ScreenAreaSchema, z.ZodNull]
      >;
      contexts: z.ZodOptional<
        z.ZodArray<typeof BrowsingContext.BrowsingContextSchema>
      >;
      userContexts: z.ZodOptional<z.ZodArray<typeof Browser.UserContextSchema>>;
    }>
  > = z.lazy(() =>
    z.object({
      screenArea: z.union([Emulation.ScreenAreaSchema, z.null()]),
      contexts: z
        .array(BrowsingContext.BrowsingContextSchema)
        .nonempty()
        .optional(),
      userContexts: z.array(Browser.UserContextSchema).nonempty().optional(),
    }),
  );
}
export namespace Emulation {
  export const SetScreenSettingsOverrideResultSchema: z.ZodLazy<
    typeof EmptyResultSchema
  > = z.lazy(() => EmptyResultSchema);
}
export namespace Emulation {
  export const SetScreenOrientationOverrideSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'emulation.setScreenOrientationOverride'>;
      params: typeof Emulation.SetScreenOrientationOverrideParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('emulation.setScreenOrientationOverride'),
      params: Emulation.SetScreenOrientationOverrideParametersSchema,
    }),
  );
}
export namespace Emulation {
  export const ScreenOrientationNaturalSchema: z.ZodLazy<
    z.ZodEnum<{portrait: 'portrait'; landscape: 'landscape'}>
  > = z.lazy(() => z.enum(['portrait', 'landscape']));
}
export namespace Emulation {
  export const ScreenOrientationTypeSchema: z.ZodLazy<
    z.ZodEnum<{
      'portrait-primary': 'portrait-primary';
      'portrait-secondary': 'portrait-secondary';
      'landscape-primary': 'landscape-primary';
      'landscape-secondary': 'landscape-secondary';
    }>
  > = z.lazy(() =>
    z.enum([
      'portrait-primary',
      'portrait-secondary',
      'landscape-primary',
      'landscape-secondary',
    ]),
  );
}
export namespace Emulation {
  export const ScreenOrientationSchema: z.ZodLazy<
    z.ZodObject<{
      natural: typeof Emulation.ScreenOrientationNaturalSchema;
      type: typeof Emulation.ScreenOrientationTypeSchema;
    }>
  > = z.lazy(() =>
    z.object({
      natural: Emulation.ScreenOrientationNaturalSchema,
      type: Emulation.ScreenOrientationTypeSchema,
    }),
  );
}
export namespace Emulation {
  export const SetScreenOrientationOverrideParametersSchema: z.ZodLazy<
    z.ZodObject<{
      screenOrientation: z.ZodUnion<
        readonly [typeof Emulation.ScreenOrientationSchema, z.ZodNull]
      >;
      contexts: z.ZodOptional<
        z.ZodArray<typeof BrowsingContext.BrowsingContextSchema>
      >;
      userContexts: z.ZodOptional<z.ZodArray<typeof Browser.UserContextSchema>>;
    }>
  > = z.lazy(() =>
    z.object({
      screenOrientation: z.union([Emulation.ScreenOrientationSchema, z.null()]),
      contexts: z
        .array(BrowsingContext.BrowsingContextSchema)
        .nonempty()
        .optional(),
      userContexts: z.array(Browser.UserContextSchema).nonempty().optional(),
    }),
  );
}
export namespace Emulation {
  export const SetScreenOrientationOverrideResultSchema: z.ZodLazy<
    typeof EmptyResultSchema
  > = z.lazy(() => EmptyResultSchema);
}
export namespace Emulation {
  export const SetUserAgentOverrideSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'emulation.setUserAgentOverride'>;
      params: typeof Emulation.SetUserAgentOverrideParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('emulation.setUserAgentOverride'),
      params: Emulation.SetUserAgentOverrideParametersSchema,
    }),
  );
}
export namespace Emulation {
  export const SetUserAgentOverrideParametersSchema: z.ZodLazy<
    z.ZodObject<{
      userAgent: z.ZodUnion<readonly [z.ZodString, z.ZodNull]>;
      contexts: z.ZodOptional<
        z.ZodArray<typeof BrowsingContext.BrowsingContextSchema>
      >;
      userContexts: z.ZodOptional<z.ZodArray<typeof Browser.UserContextSchema>>;
    }>
  > = z.lazy(() =>
    z.object({
      userAgent: z.union([z.string(), z.null()]),
      contexts: z
        .array(BrowsingContext.BrowsingContextSchema)
        .nonempty()
        .optional(),
      userContexts: z.array(Browser.UserContextSchema).nonempty().optional(),
    }),
  );
}
export namespace Emulation {
  export const SetUserAgentOverrideResultSchema: z.ZodLazy<
    typeof EmptyResultSchema
  > = z.lazy(() => EmptyResultSchema);
}
export namespace Emulation {
  export const SetViewportMetaOverrideSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'emulation.setViewportMetaOverride'>;
      params: typeof Emulation.SetViewportMetaOverrideParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('emulation.setViewportMetaOverride'),
      params: Emulation.SetViewportMetaOverrideParametersSchema,
    }),
  );
}
export namespace Emulation {
  export const SetViewportMetaOverrideParametersSchema: z.ZodLazy<
    z.ZodObject<{
      viewportMeta: z.ZodUnion<readonly [z.ZodLiteral<true>, z.ZodNull]>;
      contexts: z.ZodOptional<
        z.ZodArray<typeof BrowsingContext.BrowsingContextSchema>
      >;
      userContexts: z.ZodOptional<z.ZodArray<typeof Browser.UserContextSchema>>;
    }>
  > = z.lazy(() =>
    z.object({
      viewportMeta: z.union([z.literal(true), z.null()]),
      contexts: z
        .array(BrowsingContext.BrowsingContextSchema)
        .nonempty()
        .optional(),
      userContexts: z.array(Browser.UserContextSchema).nonempty().optional(),
    }),
  );
}
export namespace Emulation {
  export const SetViewportMetaOverrideResultSchema: z.ZodLazy<
    typeof EmptyResultSchema
  > = z.lazy(() => EmptyResultSchema);
}
export namespace Emulation {
  export const SetScriptingEnabledSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'emulation.setScriptingEnabled'>;
      params: typeof Emulation.SetScriptingEnabledParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('emulation.setScriptingEnabled'),
      params: Emulation.SetScriptingEnabledParametersSchema,
    }),
  );
}
export namespace Emulation {
  export const SetScriptingEnabledParametersSchema: z.ZodLazy<
    z.ZodObject<{
      enabled: z.ZodUnion<readonly [z.ZodLiteral<false>, z.ZodNull]>;
      contexts: z.ZodOptional<
        z.ZodArray<typeof BrowsingContext.BrowsingContextSchema>
      >;
      userContexts: z.ZodOptional<z.ZodArray<typeof Browser.UserContextSchema>>;
    }>
  > = z.lazy(() =>
    z.object({
      enabled: z.union([z.literal(false), z.null()]),
      contexts: z
        .array(BrowsingContext.BrowsingContextSchema)
        .nonempty()
        .optional(),
      userContexts: z.array(Browser.UserContextSchema).nonempty().optional(),
    }),
  );
}
export namespace Emulation {
  export const SetScriptingEnabledResultSchema: z.ZodLazy<
    typeof EmptyResultSchema
  > = z.lazy(() => EmptyResultSchema);
}
export namespace Emulation {
  export const SetScrollbarTypeOverrideSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'emulation.setScrollbarTypeOverride'>;
      params: typeof Emulation.SetScrollbarTypeOverrideParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('emulation.setScrollbarTypeOverride'),
      params: Emulation.SetScrollbarTypeOverrideParametersSchema,
    }),
  );
}
export namespace Emulation {
  export const SetScrollbarTypeOverrideParametersSchema: z.ZodLazy<
    z.ZodObject<{
      scrollbarType: z.ZodUnion<
        readonly [z.ZodLiteral<'classic'>, z.ZodLiteral<'overlay'>, z.ZodNull]
      >;
      contexts: z.ZodOptional<
        z.ZodArray<typeof BrowsingContext.BrowsingContextSchema>
      >;
      userContexts: z.ZodOptional<z.ZodArray<typeof Browser.UserContextSchema>>;
    }>
  > = z.lazy(() =>
    z.object({
      scrollbarType: z.union([
        z.literal('classic'),
        z.literal('overlay'),
        z.null(),
      ]),
      contexts: z
        .array(BrowsingContext.BrowsingContextSchema)
        .nonempty()
        .optional(),
      userContexts: z.array(Browser.UserContextSchema).nonempty().optional(),
    }),
  );
}
export namespace Emulation {
  export const SetScrollbarTypeOverrideResultSchema: z.ZodLazy<
    typeof EmptyResultSchema
  > = z.lazy(() => EmptyResultSchema);
}
export namespace Emulation {
  export const SetTextLayoutModeOverrideSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'emulation.setTextLayoutModeOverride'>;
      params: typeof Emulation.SetTextLayoutModeOverrideParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('emulation.setTextLayoutModeOverride'),
      params: Emulation.SetTextLayoutModeOverrideParametersSchema,
    }),
  );
}
export namespace Emulation {
  export const SetTextLayoutModeOverrideParametersSchema: z.ZodLazy<
    z.ZodObject<{
      textLayoutMode: z.ZodUnion<
        readonly [typeof Emulation.TextLayoutModeSchema, z.ZodNull]
      >;
      contexts: z.ZodOptional<
        z.ZodArray<typeof BrowsingContext.BrowsingContextSchema>
      >;
      userContexts: z.ZodOptional<z.ZodArray<typeof Browser.UserContextSchema>>;
    }>
  > = z.lazy(() =>
    z.object({
      textLayoutMode: z.union([Emulation.TextLayoutModeSchema, z.null()]),
      contexts: z
        .array(BrowsingContext.BrowsingContextSchema)
        .nonempty()
        .optional(),
      userContexts: z.array(Browser.UserContextSchema).nonempty().optional(),
    }),
  );
}
export namespace Emulation {
  export const TextLayoutModeSchema: z.ZodLiteral<'mobile'> =
    z.literal('mobile');
}
export namespace Emulation {
  export const SetTextLayoutModeOverrideResultSchema: z.ZodLazy<
    typeof EmptyResultSchema
  > = z.lazy(() => EmptyResultSchema);
}
export namespace Emulation {
  export const SetTimezoneOverrideSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'emulation.setTimezoneOverride'>;
      params: typeof Emulation.SetTimezoneOverrideParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('emulation.setTimezoneOverride'),
      params: Emulation.SetTimezoneOverrideParametersSchema,
    }),
  );
}
export namespace Emulation {
  export const SetTimezoneOverrideParametersSchema: z.ZodLazy<
    z.ZodObject<{
      timezone: z.ZodUnion<readonly [z.ZodString, z.ZodNull]>;
      contexts: z.ZodOptional<
        z.ZodArray<typeof BrowsingContext.BrowsingContextSchema>
      >;
      userContexts: z.ZodOptional<z.ZodArray<typeof Browser.UserContextSchema>>;
    }>
  > = z.lazy(() =>
    z.object({
      timezone: z.union([z.string(), z.null()]),
      contexts: z
        .array(BrowsingContext.BrowsingContextSchema)
        .nonempty()
        .optional(),
      userContexts: z.array(Browser.UserContextSchema).nonempty().optional(),
    }),
  );
}
export namespace Emulation {
  export const SetTimezoneOverrideResultSchema: z.ZodLazy<
    typeof EmptyResultSchema
  > = z.lazy(() => EmptyResultSchema);
}
export namespace Emulation {
  export const SetTouchOverrideSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'emulation.setTouchOverride'>;
      params: typeof Emulation.SetTouchOverrideParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('emulation.setTouchOverride'),
      params: Emulation.SetTouchOverrideParametersSchema,
    }),
  );
}
export namespace Emulation {
  export const SetTouchOverrideParametersSchema: z.ZodLazy<
    z.ZodObject<{
      maxTouchPoints: z.ZodUnion<readonly [typeof JsUintSchema, z.ZodNull]>;
      contexts: z.ZodOptional<
        z.ZodArray<typeof BrowsingContext.BrowsingContextSchema>
      >;
      userContexts: z.ZodOptional<z.ZodArray<typeof Browser.UserContextSchema>>;
    }>
  > = z.lazy(() =>
    z.object({
      maxTouchPoints: z.union([JsUintSchema.gte(1), z.null()]),
      contexts: z
        .array(BrowsingContext.BrowsingContextSchema)
        .nonempty()
        .optional(),
      userContexts: z.array(Browser.UserContextSchema).nonempty().optional(),
    }),
  );
}
export namespace Emulation {
  export const SetTouchOverrideResultSchema: z.ZodLazy<
    typeof EmptyResultSchema
  > = z.lazy(() => EmptyResultSchema);
}
export const NetworkCommandSchema: z.ZodLazy<
  z.ZodUnion<
    readonly [
      typeof Network.AddDataCollectorSchema,
      typeof Network.AddInterceptSchema,
      typeof Network.ContinueRequestSchema,
      typeof Network.ContinueResponseSchema,
      typeof Network.ContinueWithAuthSchema,
      typeof Network.DisownDataSchema,
      typeof Network.FailRequestSchema,
      typeof Network.GetDataSchema,
      typeof Network.ProvideResponseSchema,
      typeof Network.RemoveDataCollectorSchema,
      typeof Network.RemoveInterceptSchema,
      typeof Network.SetCacheBehaviorSchema,
      typeof Network.SetExtraHeadersSchema,
    ]
  >
> = z.lazy(() =>
  z.union([
    Network.AddDataCollectorSchema,
    Network.AddInterceptSchema,
    Network.ContinueRequestSchema,
    Network.ContinueResponseSchema,
    Network.ContinueWithAuthSchema,
    Network.DisownDataSchema,
    Network.FailRequestSchema,
    Network.GetDataSchema,
    Network.ProvideResponseSchema,
    Network.RemoveDataCollectorSchema,
    Network.RemoveInterceptSchema,
    Network.SetCacheBehaviorSchema,
    Network.SetExtraHeadersSchema,
  ]),
);
export const NetworkResultSchema: z.ZodLazy<
  z.ZodUnion<
    readonly [
      typeof Network.AddDataCollectorResultSchema,
      typeof Network.AddInterceptResultSchema,
      typeof Network.ContinueRequestResultSchema,
      typeof Network.ContinueResponseResultSchema,
      typeof Network.ContinueWithAuthResultSchema,
      typeof Network.DisownDataResultSchema,
      typeof Network.FailRequestResultSchema,
      typeof Network.GetDataResultSchema,
      typeof Network.ProvideResponseResultSchema,
      typeof Network.RemoveDataCollectorResultSchema,
      typeof Network.RemoveInterceptResultSchema,
      typeof Network.SetCacheBehaviorResultSchema,
      typeof Network.SetExtraHeadersResultSchema,
    ]
  >
> = z.lazy(() =>
  z.union([
    Network.AddDataCollectorResultSchema,
    Network.AddInterceptResultSchema,
    Network.ContinueRequestResultSchema,
    Network.ContinueResponseResultSchema,
    Network.ContinueWithAuthResultSchema,
    Network.DisownDataResultSchema,
    Network.FailRequestResultSchema,
    Network.GetDataResultSchema,
    Network.ProvideResponseResultSchema,
    Network.RemoveDataCollectorResultSchema,
    Network.RemoveInterceptResultSchema,
    Network.SetCacheBehaviorResultSchema,
    Network.SetExtraHeadersResultSchema,
  ]),
);
export const NetworkEventSchema: z.ZodLazy<
  z.ZodUnion<
    readonly [
      typeof Network.AuthRequiredSchema,
      typeof Network.BeforeRequestSentSchema,
      typeof Network.FetchErrorSchema,
      typeof Network.ResponseCompletedSchema,
      typeof Network.ResponseStartedSchema,
    ]
  >
> = z.lazy(() =>
  z.union([
    Network.AuthRequiredSchema,
    Network.BeforeRequestSentSchema,
    Network.FetchErrorSchema,
    Network.ResponseCompletedSchema,
    Network.ResponseStartedSchema,
  ]),
);
export namespace Network {
  export const AuthChallengeSchema: z.ZodLazy<
    z.ZodObject<{
      scheme: z.ZodString;
      realm: z.ZodString;
    }>
  > = z.lazy(() =>
    z.object({
      scheme: z.string(),
      realm: z.string(),
    }),
  );
}
export namespace Network {
  export const AuthCredentialsSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'password'>;
      username: z.ZodString;
      password: z.ZodString;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('password'),
      username: z.string(),
      password: z.string(),
    }),
  );
}
export namespace Network {
  export const BaseParametersSchema: z.ZodLazy<
    z.ZodObject<{
      context: z.ZodUnion<
        readonly [typeof BrowsingContext.BrowsingContextSchema, z.ZodNull]
      >;
      isBlocked: z.ZodBoolean;
      navigation: z.ZodUnion<
        readonly [typeof BrowsingContext.NavigationSchema, z.ZodNull]
      >;
      redirectCount: typeof JsUintSchema;
      request: typeof Network.RequestDataSchema;
      timestamp: typeof JsUintSchema;
      userContext: z.ZodOptional<
        z.ZodUnion<readonly [typeof Browser.UserContextSchema, z.ZodNull]>
      >;
      intercepts: z.ZodOptional<z.ZodArray<typeof Network.InterceptSchema>>;
    }>
  > = z.lazy(() =>
    z.object({
      context: z.union([BrowsingContext.BrowsingContextSchema, z.null()]),
      isBlocked: z.boolean(),
      navigation: z.union([BrowsingContext.NavigationSchema, z.null()]),
      redirectCount: JsUintSchema,
      request: Network.RequestDataSchema,
      timestamp: JsUintSchema,
      userContext: z.union([Browser.UserContextSchema, z.null()]).optional(),
      intercepts: z.array(Network.InterceptSchema).nonempty().optional(),
    }),
  );
}
export namespace Network {
  export const BytesValueSchema: z.ZodLazy<
    z.ZodUnion<
      readonly [
        typeof Network.StringValueSchema,
        typeof Network.Base64ValueSchema,
      ]
    >
  > = z.lazy(() =>
    z.union([Network.StringValueSchema, Network.Base64ValueSchema]),
  );
}
export namespace Network {
  export const StringValueSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'string'>;
      value: z.ZodString;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('string'),
      value: z.string(),
    }),
  );
}
export namespace Network {
  export const Base64ValueSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'base64'>;
      value: z.ZodString;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('base64'),
      value: z.string(),
    }),
  );
}
export namespace Network {
  export const CollectorSchema: z.ZodLazy<z.ZodString> = z.lazy(() =>
    z.string(),
  );
}
export namespace Network {
  export const CollectorTypeSchema: z.ZodLiteral<'blob'> = z.literal('blob');
}
export namespace Network {
  export const SameSiteSchema: z.ZodLazy<
    z.ZodEnum<{strict: 'strict'; lax: 'lax'; none: 'none'; default: 'default'}>
  > = z.lazy(() => z.enum(['strict', 'lax', 'none', 'default']));
}
export namespace Network {
  export const CookieSchema: z.ZodLazy<
    z.ZodIntersection<
      z.ZodObject<{
        name: z.ZodString;
        value: typeof Network.BytesValueSchema;
        domain: z.ZodString;
        path: z.ZodString;
        size: typeof JsUintSchema;
        httpOnly: z.ZodBoolean;
        secure: z.ZodBoolean;
        sameSite: typeof Network.SameSiteSchema;
        expiry: z.ZodOptional<typeof JsUintSchema>;
      }>,
      typeof ExtensibleSchema
    >
  > = z.lazy(() =>
    z
      .object({
        name: z.string(),
        value: Network.BytesValueSchema,
        domain: z.string(),
        path: z.string(),
        size: JsUintSchema,
        httpOnly: z.boolean(),
        secure: z.boolean(),
        sameSite: Network.SameSiteSchema,
        expiry: JsUintSchema.optional(),
      })
      .and(ExtensibleSchema),
  );
}
export namespace Network {
  export const CookieHeaderSchema: z.ZodLazy<
    z.ZodObject<{
      name: z.ZodString;
      value: typeof Network.BytesValueSchema;
    }>
  > = z.lazy(() =>
    z.object({
      name: z.string(),
      value: Network.BytesValueSchema,
    }),
  );
}
export namespace Network {
  export const DataTypeSchema: z.ZodLazy<
    z.ZodEnum<{request: 'request'; response: 'response'}>
  > = z.lazy(() => z.enum(['request', 'response']));
}
export namespace Network {
  export const FetchTimingInfoSchema: z.ZodLazy<
    z.ZodObject<{
      timeOrigin: z.ZodNumber;
      requestTime: z.ZodNumber;
      redirectStart: z.ZodNumber;
      redirectEnd: z.ZodNumber;
      fetchStart: z.ZodNumber;
      dnsStart: z.ZodNumber;
      dnsEnd: z.ZodNumber;
      connectStart: z.ZodNumber;
      connectEnd: z.ZodNumber;
      tlsStart: z.ZodNumber;
      requestStart: z.ZodNumber;
      responseStart: z.ZodNumber;
      responseEnd: z.ZodNumber;
    }>
  > = z.lazy(() =>
    z.object({
      timeOrigin: z.number(),
      requestTime: z.number(),
      redirectStart: z.number(),
      redirectEnd: z.number(),
      fetchStart: z.number(),
      dnsStart: z.number(),
      dnsEnd: z.number(),
      connectStart: z.number(),
      connectEnd: z.number(),
      tlsStart: z.number(),
      requestStart: z.number(),
      responseStart: z.number(),
      responseEnd: z.number(),
    }),
  );
}
export namespace Network {
  export const HeaderSchema: z.ZodLazy<
    z.ZodObject<{
      name: z.ZodString;
      value: typeof Network.BytesValueSchema;
    }>
  > = z.lazy(() =>
    z.object({
      name: z.string(),
      value: Network.BytesValueSchema,
    }),
  );
}
export namespace Network {
  export const InitiatorSchema: z.ZodLazy<
    z.ZodObject<{
      columnNumber: z.ZodOptional<typeof JsUintSchema>;
      lineNumber: z.ZodOptional<typeof JsUintSchema>;
      request: z.ZodOptional<typeof Network.RequestSchema>;
      stackTrace: z.ZodOptional<typeof Script.StackTraceSchema>;
      type: z.ZodOptional<
        z.ZodEnum<{
          parser: 'parser';
          script: 'script';
          preflight: 'preflight';
          other: 'other';
        }>
      >;
    }>
  > = z.lazy(() =>
    z.object({
      columnNumber: JsUintSchema.optional(),
      lineNumber: JsUintSchema.optional(),
      request: Network.RequestSchema.optional(),
      stackTrace: Script.StackTraceSchema.optional(),
      type: z.enum(['parser', 'script', 'preflight', 'other']).optional(),
    }),
  );
}
export namespace Network {
  export const InterceptSchema: z.ZodLazy<z.ZodString> = z.lazy(() =>
    z.string(),
  );
}
export namespace Network {
  export const RequestSchema: z.ZodLazy<z.ZodString> = z.lazy(() => z.string());
}
export namespace Network {
  export const RequestDataSchema: z.ZodLazy<
    z.ZodObject<{
      request: typeof Network.RequestSchema;
      url: z.ZodString;
      method: z.ZodString;
      headers: z.ZodArray<typeof Network.HeaderSchema>;
      cookies: z.ZodArray<typeof Network.CookieSchema>;
      headersSize: typeof JsUintSchema;
      bodySize: z.ZodUnion<readonly [typeof JsUintSchema, z.ZodNull]>;
      destination: z.ZodString;
      initiatorType: z.ZodUnion<readonly [z.ZodString, z.ZodNull]>;
      timings: typeof Network.FetchTimingInfoSchema;
    }>
  > = z.lazy(() =>
    z.object({
      request: Network.RequestSchema,
      url: z.string(),
      method: z.string(),
      headers: z.array(Network.HeaderSchema),
      cookies: z.array(Network.CookieSchema),
      headersSize: JsUintSchema,
      bodySize: z.union([JsUintSchema, z.null()]),
      destination: z.string(),
      initiatorType: z.union([z.string(), z.null()]),
      timings: Network.FetchTimingInfoSchema,
    }),
  );
}
export namespace Network {
  export const ResponseContentSchema: z.ZodLazy<
    z.ZodObject<{
      size: typeof JsUintSchema;
    }>
  > = z.lazy(() =>
    z.object({
      size: JsUintSchema,
    }),
  );
}
export namespace Network {
  export const ResponseDataSchema: z.ZodLazy<
    z.ZodObject<{
      url: z.ZodString;
      protocol: z.ZodString;
      status: typeof JsUintSchema;
      statusText: z.ZodString;
      fromCache: z.ZodBoolean;
      headers: z.ZodArray<typeof Network.HeaderSchema>;
      mimeType: z.ZodString;
      bytesReceived: typeof JsUintSchema;
      headersSize: z.ZodUnion<readonly [typeof JsUintSchema, z.ZodNull]>;
      bodySize: z.ZodUnion<readonly [typeof JsUintSchema, z.ZodNull]>;
      content: typeof Network.ResponseContentSchema;
      authChallenges: z.ZodOptional<
        z.ZodArray<typeof Network.AuthChallengeSchema>
      >;
    }>
  > = z.lazy(() =>
    z.object({
      url: z.string(),
      protocol: z.string(),
      status: JsUintSchema,
      statusText: z.string(),
      fromCache: z.boolean(),
      headers: z.array(Network.HeaderSchema),
      mimeType: z.string(),
      bytesReceived: JsUintSchema,
      headersSize: z.union([JsUintSchema, z.null()]),
      bodySize: z.union([JsUintSchema, z.null()]),
      content: Network.ResponseContentSchema,
      authChallenges: z.array(Network.AuthChallengeSchema).optional(),
    }),
  );
}
export namespace Network {
  export const SetCookieHeaderSchema: z.ZodLazy<
    z.ZodObject<{
      name: z.ZodString;
      value: typeof Network.BytesValueSchema;
      domain: z.ZodOptional<z.ZodString>;
      httpOnly: z.ZodOptional<z.ZodBoolean>;
      expiry: z.ZodOptional<z.ZodString>;
      maxAge: z.ZodOptional<typeof JsIntSchema>;
      path: z.ZodOptional<z.ZodString>;
      sameSite: z.ZodOptional<typeof Network.SameSiteSchema>;
      secure: z.ZodOptional<z.ZodBoolean>;
    }>
  > = z.lazy(() =>
    z.object({
      name: z.string(),
      value: Network.BytesValueSchema,
      domain: z.string().optional(),
      httpOnly: z.boolean().optional(),
      expiry: z.string().optional(),
      maxAge: JsIntSchema.optional(),
      path: z.string().optional(),
      sameSite: Network.SameSiteSchema.optional(),
      secure: z.boolean().optional(),
    }),
  );
}
export namespace Network {
  export const UrlPatternSchema: z.ZodLazy<
    z.ZodUnion<
      readonly [
        typeof Network.UrlPatternPatternSchema,
        typeof Network.UrlPatternStringSchema,
      ]
    >
  > = z.lazy(() =>
    z.union([Network.UrlPatternPatternSchema, Network.UrlPatternStringSchema]),
  );
}
export namespace Network {
  export const UrlPatternPatternSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'pattern'>;
      protocol: z.ZodOptional<z.ZodString>;
      hostname: z.ZodOptional<z.ZodString>;
      port: z.ZodOptional<z.ZodString>;
      pathname: z.ZodOptional<z.ZodString>;
      search: z.ZodOptional<z.ZodString>;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('pattern'),
      protocol: z.string().optional(),
      hostname: z.string().optional(),
      port: z.string().optional(),
      pathname: z.string().optional(),
      search: z.string().optional(),
    }),
  );
}
export namespace Network {
  export const UrlPatternStringSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'string'>;
      pattern: z.ZodString;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('string'),
      pattern: z.string(),
    }),
  );
}
export namespace Network {
  export const AddDataCollectorSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'network.addDataCollector'>;
      params: typeof Network.AddDataCollectorParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('network.addDataCollector'),
      params: Network.AddDataCollectorParametersSchema,
    }),
  );
}
export namespace Network {
  export const AddDataCollectorParametersSchema: z.ZodLazy<
    z.ZodObject<{
      dataTypes: z.ZodArray<typeof Network.DataTypeSchema>;
      maxEncodedDataSize: typeof JsUintSchema;
      collectorType: z.ZodOptional<
        z.ZodDefault<typeof Network.CollectorTypeSchema>
      >;
      contexts: z.ZodOptional<
        z.ZodArray<typeof BrowsingContext.BrowsingContextSchema>
      >;
      userContexts: z.ZodOptional<z.ZodArray<typeof Browser.UserContextSchema>>;
    }>
  > = z.lazy(() =>
    z.object({
      dataTypes: z.array(Network.DataTypeSchema).nonempty(),
      maxEncodedDataSize: JsUintSchema,
      collectorType: Network.CollectorTypeSchema.default('blob').optional(),
      contexts: z
        .array(BrowsingContext.BrowsingContextSchema)
        .nonempty()
        .optional(),
      userContexts: z.array(Browser.UserContextSchema).nonempty().optional(),
    }),
  );
}
export namespace Network {
  export const AddDataCollectorResultSchema: z.ZodLazy<
    z.ZodObject<{
      collector: typeof Network.CollectorSchema;
    }>
  > = z.lazy(() =>
    z.object({
      collector: Network.CollectorSchema,
    }),
  );
}
export namespace Network {
  export const AddInterceptSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'network.addIntercept'>;
      params: typeof Network.AddInterceptParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('network.addIntercept'),
      params: Network.AddInterceptParametersSchema,
    }),
  );
}
export namespace Network {
  export const AddInterceptParametersSchema: z.ZodLazy<
    z.ZodObject<{
      phases: z.ZodArray<typeof Network.InterceptPhaseSchema>;
      contexts: z.ZodOptional<
        z.ZodArray<typeof BrowsingContext.BrowsingContextSchema>
      >;
      urlPatterns: z.ZodOptional<z.ZodArray<typeof Network.UrlPatternSchema>>;
    }>
  > = z.lazy(() =>
    z.object({
      phases: z.array(Network.InterceptPhaseSchema).nonempty(),
      contexts: z
        .array(BrowsingContext.BrowsingContextSchema)
        .nonempty()
        .optional(),
      urlPatterns: z.array(Network.UrlPatternSchema).optional(),
    }),
  );
}
export namespace Network {
  export const InterceptPhaseSchema: z.ZodLazy<
    z.ZodEnum<{
      beforeRequestSent: 'beforeRequestSent';
      responseStarted: 'responseStarted';
      authRequired: 'authRequired';
    }>
  > = z.lazy(() =>
    z.enum(['beforeRequestSent', 'responseStarted', 'authRequired']),
  );
}
export namespace Network {
  export const AddInterceptResultSchema: z.ZodLazy<
    z.ZodObject<{
      intercept: typeof Network.InterceptSchema;
    }>
  > = z.lazy(() =>
    z.object({
      intercept: Network.InterceptSchema,
    }),
  );
}
export namespace Network {
  export const ContinueRequestSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'network.continueRequest'>;
      params: typeof Network.ContinueRequestParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('network.continueRequest'),
      params: Network.ContinueRequestParametersSchema,
    }),
  );
}
export namespace Network {
  export const ContinueRequestParametersSchema: z.ZodLazy<
    z.ZodObject<{
      request: typeof Network.RequestSchema;
      body: z.ZodOptional<typeof Network.BytesValueSchema>;
      cookies: z.ZodOptional<z.ZodArray<typeof Network.CookieHeaderSchema>>;
      headers: z.ZodOptional<z.ZodArray<typeof Network.HeaderSchema>>;
      method: z.ZodOptional<z.ZodString>;
      url: z.ZodOptional<z.ZodString>;
    }>
  > = z.lazy(() =>
    z.object({
      request: Network.RequestSchema,
      body: Network.BytesValueSchema.optional(),
      cookies: z.array(Network.CookieHeaderSchema).optional(),
      headers: z.array(Network.HeaderSchema).optional(),
      method: z.string().optional(),
      url: z.string().optional(),
    }),
  );
}
export namespace Network {
  export const ContinueRequestResultSchema: z.ZodLazy<
    typeof EmptyResultSchema
  > = z.lazy(() => EmptyResultSchema);
}
export namespace Network {
  export const ContinueResponseSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'network.continueResponse'>;
      params: typeof Network.ContinueResponseParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('network.continueResponse'),
      params: Network.ContinueResponseParametersSchema,
    }),
  );
}
export namespace Network {
  export const ContinueResponseParametersSchema: z.ZodLazy<
    z.ZodObject<{
      request: typeof Network.RequestSchema;
      cookies: z.ZodOptional<z.ZodArray<typeof Network.SetCookieHeaderSchema>>;
      credentials: z.ZodOptional<typeof Network.AuthCredentialsSchema>;
      headers: z.ZodOptional<z.ZodArray<typeof Network.HeaderSchema>>;
      reasonPhrase: z.ZodOptional<z.ZodString>;
      statusCode: z.ZodOptional<typeof JsUintSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      request: Network.RequestSchema,
      cookies: z.array(Network.SetCookieHeaderSchema).optional(),
      credentials: Network.AuthCredentialsSchema.optional(),
      headers: z.array(Network.HeaderSchema).optional(),
      reasonPhrase: z.string().optional(),
      statusCode: JsUintSchema.optional(),
    }),
  );
}
export namespace Network {
  export const ContinueResponseResultSchema: z.ZodLazy<
    typeof EmptyResultSchema
  > = z.lazy(() => EmptyResultSchema);
}
export namespace Network {
  export const ContinueWithAuthSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'network.continueWithAuth'>;
      params: typeof Network.ContinueWithAuthParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('network.continueWithAuth'),
      params: Network.ContinueWithAuthParametersSchema,
    }),
  );
}
export namespace Network {
  export const ContinueWithAuthParametersSchema: z.ZodLazy<
    z.ZodIntersection<
      z.ZodObject<{
        request: typeof Network.RequestSchema;
      }>,
      z.ZodUnion<
        readonly [
          typeof Network.ContinueWithAuthCredentialsSchema,
          typeof Network.ContinueWithAuthNoCredentialsSchema,
        ]
      >
    >
  > = z.lazy(() =>
    z
      .object({
        request: Network.RequestSchema,
      })
      .and(
        z.union([
          Network.ContinueWithAuthCredentialsSchema,
          Network.ContinueWithAuthNoCredentialsSchema,
        ]),
      ),
  );
}
export namespace Network {
  export const ContinueWithAuthCredentialsSchema: z.ZodLazy<
    z.ZodObject<{
      action: z.ZodLiteral<'provideCredentials'>;
      credentials: typeof Network.AuthCredentialsSchema;
    }>
  > = z.lazy(() =>
    z.object({
      action: z.literal('provideCredentials'),
      credentials: Network.AuthCredentialsSchema,
    }),
  );
}
export namespace Network {
  export const ContinueWithAuthNoCredentialsSchema: z.ZodLazy<
    z.ZodObject<{
      action: z.ZodEnum<{default: 'default'; cancel: 'cancel'}>;
    }>
  > = z.lazy(() =>
    z.object({
      action: z.enum(['default', 'cancel']),
    }),
  );
}
export namespace Network {
  export const ContinueWithAuthResultSchema: z.ZodLazy<
    typeof EmptyResultSchema
  > = z.lazy(() => EmptyResultSchema);
}
export namespace Network {
  export const DisownDataSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'network.disownData'>;
      params: typeof Network.DisownDataParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('network.disownData'),
      params: Network.DisownDataParametersSchema,
    }),
  );
}
export namespace Network {
  export const DisownDataParametersSchema: z.ZodLazy<
    z.ZodObject<{
      dataType: typeof Network.DataTypeSchema;
      collector: typeof Network.CollectorSchema;
      request: typeof Network.RequestSchema;
    }>
  > = z.lazy(() =>
    z.object({
      dataType: Network.DataTypeSchema,
      collector: Network.CollectorSchema,
      request: Network.RequestSchema,
    }),
  );
}
export namespace Network {
  export const DisownDataResultSchema: z.ZodLazy<typeof EmptyResultSchema> =
    z.lazy(() => EmptyResultSchema);
}
export namespace Network {
  export const FailRequestSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'network.failRequest'>;
      params: typeof Network.FailRequestParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('network.failRequest'),
      params: Network.FailRequestParametersSchema,
    }),
  );
}
export namespace Network {
  export const FailRequestParametersSchema: z.ZodLazy<
    z.ZodObject<{
      request: typeof Network.RequestSchema;
    }>
  > = z.lazy(() =>
    z.object({
      request: Network.RequestSchema,
    }),
  );
}
export namespace Network {
  export const FailRequestResultSchema: z.ZodLazy<typeof EmptyResultSchema> =
    z.lazy(() => EmptyResultSchema);
}
export namespace Network {
  export const GetDataSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'network.getData'>;
      params: typeof Network.GetDataParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('network.getData'),
      params: Network.GetDataParametersSchema,
    }),
  );
}
export namespace Network {
  export const GetDataParametersSchema: z.ZodLazy<
    z.ZodObject<{
      dataType: typeof Network.DataTypeSchema;
      collector: z.ZodOptional<typeof Network.CollectorSchema>;
      disown: z.ZodOptional<z.ZodDefault<z.ZodBoolean>>;
      request: typeof Network.RequestSchema;
    }>
  > = z.lazy(() =>
    z.object({
      dataType: Network.DataTypeSchema,
      collector: Network.CollectorSchema.optional(),
      disown: z.boolean().default(false).optional(),
      request: Network.RequestSchema,
    }),
  );
}
export namespace Network {
  export const GetDataResultSchema: z.ZodLazy<
    z.ZodObject<{
      bytes: typeof Network.BytesValueSchema;
    }>
  > = z.lazy(() =>
    z.object({
      bytes: Network.BytesValueSchema,
    }),
  );
}
export namespace Network {
  export const ProvideResponseSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'network.provideResponse'>;
      params: typeof Network.ProvideResponseParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('network.provideResponse'),
      params: Network.ProvideResponseParametersSchema,
    }),
  );
}
export namespace Network {
  export const ProvideResponseParametersSchema: z.ZodLazy<
    z.ZodObject<{
      request: typeof Network.RequestSchema;
      body: z.ZodOptional<typeof Network.BytesValueSchema>;
      cookies: z.ZodOptional<z.ZodArray<typeof Network.SetCookieHeaderSchema>>;
      headers: z.ZodOptional<z.ZodArray<typeof Network.HeaderSchema>>;
      reasonPhrase: z.ZodOptional<z.ZodString>;
      statusCode: z.ZodOptional<typeof JsUintSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      request: Network.RequestSchema,
      body: Network.BytesValueSchema.optional(),
      cookies: z.array(Network.SetCookieHeaderSchema).optional(),
      headers: z.array(Network.HeaderSchema).optional(),
      reasonPhrase: z.string().optional(),
      statusCode: JsUintSchema.optional(),
    }),
  );
}
export namespace Network {
  export const ProvideResponseResultSchema: z.ZodLazy<
    typeof EmptyResultSchema
  > = z.lazy(() => EmptyResultSchema);
}
export namespace Network {
  export const RemoveDataCollectorSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'network.removeDataCollector'>;
      params: typeof Network.RemoveDataCollectorParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('network.removeDataCollector'),
      params: Network.RemoveDataCollectorParametersSchema,
    }),
  );
}
export namespace Network {
  export const RemoveDataCollectorParametersSchema: z.ZodLazy<
    z.ZodObject<{
      collector: typeof Network.CollectorSchema;
    }>
  > = z.lazy(() =>
    z.object({
      collector: Network.CollectorSchema,
    }),
  );
}
export namespace Network {
  export const RemoveDataCollectorResultSchema: z.ZodLazy<
    typeof EmptyResultSchema
  > = z.lazy(() => EmptyResultSchema);
}
export namespace Network {
  export const RemoveInterceptSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'network.removeIntercept'>;
      params: typeof Network.RemoveInterceptParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('network.removeIntercept'),
      params: Network.RemoveInterceptParametersSchema,
    }),
  );
}
export namespace Network {
  export const RemoveInterceptParametersSchema: z.ZodLazy<
    z.ZodObject<{
      intercept: typeof Network.InterceptSchema;
    }>
  > = z.lazy(() =>
    z.object({
      intercept: Network.InterceptSchema,
    }),
  );
}
export namespace Network {
  export const RemoveInterceptResultSchema: z.ZodLazy<
    typeof EmptyResultSchema
  > = z.lazy(() => EmptyResultSchema);
}
export namespace Network {
  export const SetCacheBehaviorSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'network.setCacheBehavior'>;
      params: typeof Network.SetCacheBehaviorParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('network.setCacheBehavior'),
      params: Network.SetCacheBehaviorParametersSchema,
    }),
  );
}
export namespace Network {
  export const SetCacheBehaviorParametersSchema: z.ZodLazy<
    z.ZodObject<{
      cacheBehavior: z.ZodEnum<{default: 'default'; bypass: 'bypass'}>;
      contexts: z.ZodOptional<
        z.ZodArray<typeof BrowsingContext.BrowsingContextSchema>
      >;
    }>
  > = z.lazy(() =>
    z.object({
      cacheBehavior: z.enum(['default', 'bypass']),
      contexts: z
        .array(BrowsingContext.BrowsingContextSchema)
        .nonempty()
        .optional(),
    }),
  );
}
export namespace Network {
  export const SetCacheBehaviorResultSchema: z.ZodLazy<
    typeof EmptyResultSchema
  > = z.lazy(() => EmptyResultSchema);
}
export namespace Network {
  export const SetExtraHeadersSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'network.setExtraHeaders'>;
      params: typeof Network.SetExtraHeadersParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('network.setExtraHeaders'),
      params: Network.SetExtraHeadersParametersSchema,
    }),
  );
}
export namespace Network {
  export const SetExtraHeadersParametersSchema: z.ZodLazy<
    z.ZodObject<{
      headers: z.ZodArray<typeof Network.HeaderSchema>;
      contexts: z.ZodOptional<
        z.ZodArray<typeof BrowsingContext.BrowsingContextSchema>
      >;
      userContexts: z.ZodOptional<z.ZodArray<typeof Browser.UserContextSchema>>;
    }>
  > = z.lazy(() =>
    z.object({
      headers: z.array(Network.HeaderSchema),
      contexts: z
        .array(BrowsingContext.BrowsingContextSchema)
        .nonempty()
        .optional(),
      userContexts: z.array(Browser.UserContextSchema).nonempty().optional(),
    }),
  );
}
export namespace Network {
  export const SetExtraHeadersResultSchema: z.ZodLazy<
    typeof EmptyResultSchema
  > = z.lazy(() => EmptyResultSchema);
}
export namespace Network {
  export const AuthRequiredSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'network.authRequired'>;
      params: typeof Network.AuthRequiredParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('network.authRequired'),
      params: Network.AuthRequiredParametersSchema,
    }),
  );
}
export namespace Network {
  export const AuthRequiredParametersSchema: z.ZodLazy<
    z.ZodIntersection<
      typeof Network.BaseParametersSchema,
      z.ZodObject<{
        response: typeof Network.ResponseDataSchema;
      }>
    >
  > = z.lazy(() =>
    Network.BaseParametersSchema.and(
      z.object({
        response: Network.ResponseDataSchema,
      }),
    ),
  );
}
export namespace Network {
  export const BeforeRequestSentSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'network.beforeRequestSent'>;
      params: typeof Network.BeforeRequestSentParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('network.beforeRequestSent'),
      params: Network.BeforeRequestSentParametersSchema,
    }),
  );
}
export namespace Network {
  export const BeforeRequestSentParametersSchema: z.ZodLazy<
    z.ZodIntersection<
      typeof Network.BaseParametersSchema,
      z.ZodObject<{
        initiator: z.ZodOptional<typeof Network.InitiatorSchema>;
      }>
    >
  > = z.lazy(() =>
    Network.BaseParametersSchema.and(
      z.object({
        initiator: Network.InitiatorSchema.optional(),
      }),
    ),
  );
}
export namespace Network {
  export const FetchErrorSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'network.fetchError'>;
      params: typeof Network.FetchErrorParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('network.fetchError'),
      params: Network.FetchErrorParametersSchema,
    }),
  );
}
export namespace Network {
  export const FetchErrorParametersSchema: z.ZodLazy<
    z.ZodIntersection<
      typeof Network.BaseParametersSchema,
      z.ZodObject<{
        errorText: z.ZodString;
      }>
    >
  > = z.lazy(() =>
    Network.BaseParametersSchema.and(
      z.object({
        errorText: z.string(),
      }),
    ),
  );
}
export namespace Network {
  export const ResponseCompletedSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'network.responseCompleted'>;
      params: typeof Network.ResponseCompletedParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('network.responseCompleted'),
      params: Network.ResponseCompletedParametersSchema,
    }),
  );
}
export namespace Network {
  export const ResponseCompletedParametersSchema: z.ZodLazy<
    z.ZodIntersection<
      typeof Network.BaseParametersSchema,
      z.ZodObject<{
        response: typeof Network.ResponseDataSchema;
      }>
    >
  > = z.lazy(() =>
    Network.BaseParametersSchema.and(
      z.object({
        response: Network.ResponseDataSchema,
      }),
    ),
  );
}
export namespace Network {
  export const ResponseStartedSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'network.responseStarted'>;
      params: typeof Network.ResponseStartedParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('network.responseStarted'),
      params: Network.ResponseStartedParametersSchema,
    }),
  );
}
export namespace Network {
  export const ResponseStartedParametersSchema: z.ZodLazy<
    z.ZodIntersection<
      typeof Network.BaseParametersSchema,
      z.ZodObject<{
        response: typeof Network.ResponseDataSchema;
      }>
    >
  > = z.lazy(() =>
    Network.BaseParametersSchema.and(
      z.object({
        response: Network.ResponseDataSchema,
      }),
    ),
  );
}
export const ScriptCommandSchema: z.ZodLazy<
  z.ZodUnion<
    readonly [
      typeof Script.AddPreloadScriptSchema,
      typeof Script.CallFunctionSchema,
      typeof Script.DisownSchema,
      typeof Script.EvaluateSchema,
      typeof Script.GetRealmsSchema,
      typeof Script.RemovePreloadScriptSchema,
    ]
  >
> = z.lazy(() =>
  z.union([
    Script.AddPreloadScriptSchema,
    Script.CallFunctionSchema,
    Script.DisownSchema,
    Script.EvaluateSchema,
    Script.GetRealmsSchema,
    Script.RemovePreloadScriptSchema,
  ]),
);
export const ScriptResultSchema: z.ZodLazy<
  z.ZodUnion<
    readonly [
      typeof Script.AddPreloadScriptResultSchema,
      typeof Script.CallFunctionResultSchema,
      typeof Script.DisownResultSchema,
      typeof Script.EvaluateResultSchema,
      typeof Script.GetRealmsResultSchema,
      typeof Script.RemovePreloadScriptResultSchema,
    ]
  >
> = z.lazy(() =>
  z.union([
    Script.AddPreloadScriptResultSchema,
    Script.CallFunctionResultSchema,
    Script.DisownResultSchema,
    Script.EvaluateResultSchema,
    Script.GetRealmsResultSchema,
    Script.RemovePreloadScriptResultSchema,
  ]),
);
export const ScriptEventSchema: z.ZodLazy<
  z.ZodUnion<
    readonly [
      typeof Script.MessageSchema,
      typeof Script.RealmCreatedSchema,
      typeof Script.RealmDestroyedSchema,
    ]
  >
> = z.lazy(() =>
  z.union([
    Script.MessageSchema,
    Script.RealmCreatedSchema,
    Script.RealmDestroyedSchema,
  ]),
);
export namespace Script {
  export const ChannelSchema: z.ZodLazy<z.ZodString> = z.lazy(() => z.string());
}
export namespace Script {
  export const ChannelValueSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'channel'>;
      value: typeof Script.ChannelPropertiesSchema;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('channel'),
      value: Script.ChannelPropertiesSchema,
    }),
  );
}
export namespace Script {
  export const ChannelPropertiesSchema: z.ZodLazy<
    z.ZodObject<{
      channel: typeof Script.ChannelSchema;
      serializationOptions: z.ZodOptional<
        typeof Script.SerializationOptionsSchema
      >;
      ownership: z.ZodOptional<typeof Script.ResultOwnershipSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      channel: Script.ChannelSchema,
      serializationOptions: Script.SerializationOptionsSchema.optional(),
      ownership: Script.ResultOwnershipSchema.optional(),
    }),
  );
}
export namespace Script {
  export const EvaluateResultSchema: z.ZodLazy<
    z.ZodUnion<
      readonly [
        typeof Script.EvaluateResultSuccessSchema,
        typeof Script.EvaluateResultExceptionSchema,
      ]
    >
  > = z.lazy(() =>
    z.union([
      Script.EvaluateResultSuccessSchema,
      Script.EvaluateResultExceptionSchema,
    ]),
  );
}
export namespace Script {
  export const EvaluateResultSuccessSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'success'>;
      result: typeof Script.RemoteValueSchema;
      realm: typeof Script.RealmSchema;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('success'),
      result: Script.RemoteValueSchema,
      realm: Script.RealmSchema,
    }),
  );
}
export namespace Script {
  export const EvaluateResultExceptionSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'exception'>;
      exceptionDetails: typeof Script.ExceptionDetailsSchema;
      realm: typeof Script.RealmSchema;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('exception'),
      exceptionDetails: Script.ExceptionDetailsSchema,
      realm: Script.RealmSchema,
    }),
  );
}
export namespace Script {
  export const ExceptionDetailsSchema: z.ZodLazy<
    z.ZodObject<{
      columnNumber: typeof JsUintSchema;
      exception: typeof Script.RemoteValueSchema;
      lineNumber: typeof JsUintSchema;
      stackTrace: typeof Script.StackTraceSchema;
      text: z.ZodString;
    }>
  > = z.lazy(() =>
    z.object({
      columnNumber: JsUintSchema,
      exception: Script.RemoteValueSchema,
      lineNumber: JsUintSchema,
      stackTrace: Script.StackTraceSchema,
      text: z.string(),
    }),
  );
}
export namespace Script {
  export const HandleSchema: z.ZodLazy<z.ZodString> = z.lazy(() => z.string());
}
export namespace Script {
  export const InternalIdSchema: z.ZodLazy<z.ZodString> = z.lazy(() =>
    z.string(),
  );
}
export namespace Script {
  export const LocalValueSchema: z.ZodType = z.lazy(() =>
    z.union([
      Script.RemoteReferenceSchema,
      Script.PrimitiveProtocolValueSchema,
      Script.ChannelValueSchema,
      Script.ArrayLocalValueSchema,
      Script.DateLocalValueSchema,
      Script.MapLocalValueSchema,
      Script.ObjectLocalValueSchema,
      Script.RegExpLocalValueSchema,
      Script.SetLocalValueSchema,
    ]),
  );
}
export namespace Script {
  export const ListLocalValueSchema: z.ZodLazy<
    z.ZodArray<typeof Script.LocalValueSchema>
  > = z.lazy(() => z.array(Script.LocalValueSchema));
}
export namespace Script {
  export const ArrayLocalValueSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'array'>;
      value: typeof Script.ListLocalValueSchema;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('array'),
      value: Script.ListLocalValueSchema,
    }),
  );
}
export namespace Script {
  export const DateLocalValueSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'date'>;
      value: z.ZodString;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('date'),
      value: z.string(),
    }),
  );
}
export namespace Script {
  export const MappingLocalValueSchema: z.ZodLazy<
    z.ZodArray<
      z.ZodTuple<
        [
          z.ZodUnion<readonly [typeof Script.LocalValueSchema, z.ZodString]>,
          typeof Script.LocalValueSchema,
        ],
        null
      >
    >
  > = z.lazy(() =>
    z.array(
      z.tuple([
        z.union([Script.LocalValueSchema, z.string()]),
        Script.LocalValueSchema,
      ]),
    ),
  );
}
export namespace Script {
  export const MapLocalValueSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'map'>;
      value: typeof Script.MappingLocalValueSchema;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('map'),
      value: Script.MappingLocalValueSchema,
    }),
  );
}
export namespace Script {
  export const ObjectLocalValueSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'object'>;
      value: typeof Script.MappingLocalValueSchema;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('object'),
      value: Script.MappingLocalValueSchema,
    }),
  );
}
export namespace Script {
  export const RegExpValueSchema: z.ZodLazy<
    z.ZodObject<{
      pattern: z.ZodString;
      flags: z.ZodOptional<z.ZodString>;
    }>
  > = z.lazy(() =>
    z.object({
      pattern: z.string(),
      flags: z.string().optional(),
    }),
  );
}
export namespace Script {
  export const RegExpLocalValueSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'regexp'>;
      value: typeof Script.RegExpValueSchema;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('regexp'),
      value: Script.RegExpValueSchema,
    }),
  );
}
export namespace Script {
  export const SetLocalValueSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'set'>;
      value: typeof Script.ListLocalValueSchema;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('set'),
      value: Script.ListLocalValueSchema,
    }),
  );
}
export namespace Script {
  export const PreloadScriptSchema: z.ZodLazy<z.ZodString> = z.lazy(() =>
    z.string(),
  );
}
export namespace Script {
  export const RealmSchema: z.ZodLazy<z.ZodString> = z.lazy(() => z.string());
}
export namespace Script {
  export const PrimitiveProtocolValueSchema: z.ZodLazy<
    z.ZodUnion<
      readonly [
        typeof Script.UndefinedValueSchema,
        typeof Script.NullValueSchema,
        typeof Script.StringValueSchema,
        typeof Script.NumberValueSchema,
        typeof Script.BooleanValueSchema,
        typeof Script.BigIntValueSchema,
      ]
    >
  > = z.lazy(() =>
    z.union([
      Script.UndefinedValueSchema,
      Script.NullValueSchema,
      Script.StringValueSchema,
      Script.NumberValueSchema,
      Script.BooleanValueSchema,
      Script.BigIntValueSchema,
    ]),
  );
}
export namespace Script {
  export const UndefinedValueSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'undefined'>;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('undefined'),
    }),
  );
}
export namespace Script {
  export const NullValueSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'null'>;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('null'),
    }),
  );
}
export namespace Script {
  export const StringValueSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'string'>;
      value: z.ZodString;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('string'),
      value: z.string(),
    }),
  );
}
export namespace Script {
  export const SpecialNumberSchema: z.ZodLazy<
    z.ZodEnum<{
      NaN: 'NaN';
      '-0': '-0';
      Infinity: 'Infinity';
      '-Infinity': '-Infinity';
    }>
  > = z.lazy(() => z.enum(['NaN', '-0', 'Infinity', '-Infinity']));
}
export namespace Script {
  export const NumberValueSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'number'>;
      value: z.ZodUnion<
        readonly [z.ZodNumber, typeof Script.SpecialNumberSchema]
      >;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('number'),
      value: z.union([z.number(), Script.SpecialNumberSchema]),
    }),
  );
}
export namespace Script {
  export const BooleanValueSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'boolean'>;
      value: z.ZodBoolean;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('boolean'),
      value: z.boolean(),
    }),
  );
}
export namespace Script {
  export const BigIntValueSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'bigint'>;
      value: z.ZodString;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('bigint'),
      value: z.string(),
    }),
  );
}
export namespace Script {
  export const RealmInfoSchema: z.ZodLazy<
    z.ZodUnion<
      readonly [
        typeof Script.WindowRealmInfoSchema,
        typeof Script.DedicatedWorkerRealmInfoSchema,
        typeof Script.SharedWorkerRealmInfoSchema,
        typeof Script.ServiceWorkerRealmInfoSchema,
        typeof Script.WorkerRealmInfoSchema,
        typeof Script.PaintWorkletRealmInfoSchema,
        typeof Script.AudioWorkletRealmInfoSchema,
        typeof Script.WorkletRealmInfoSchema,
      ]
    >
  > = z.lazy(() =>
    z.union([
      Script.WindowRealmInfoSchema,
      Script.DedicatedWorkerRealmInfoSchema,
      Script.SharedWorkerRealmInfoSchema,
      Script.ServiceWorkerRealmInfoSchema,
      Script.WorkerRealmInfoSchema,
      Script.PaintWorkletRealmInfoSchema,
      Script.AudioWorkletRealmInfoSchema,
      Script.WorkletRealmInfoSchema,
    ]),
  );
}
export namespace Script {
  export const BaseRealmInfoSchema: z.ZodLazy<
    z.ZodObject<{
      realm: typeof Script.RealmSchema;
      origin: z.ZodString;
    }>
  > = z.lazy(() =>
    z.object({
      realm: Script.RealmSchema,
      origin: z.string(),
    }),
  );
}
export namespace Script {
  export const WindowRealmInfoSchema: z.ZodLazy<
    z.ZodIntersection<
      typeof Script.BaseRealmInfoSchema,
      z.ZodObject<{
        type: z.ZodLiteral<'window'>;
        context: typeof BrowsingContext.BrowsingContextSchema;
        userContext: z.ZodOptional<typeof Browser.UserContextSchema>;
        sandbox: z.ZodOptional<z.ZodString>;
      }>
    >
  > = z.lazy(() =>
    Script.BaseRealmInfoSchema.and(
      z.object({
        type: z.literal('window'),
        context: BrowsingContext.BrowsingContextSchema,
        userContext: Browser.UserContextSchema.optional(),
        sandbox: z.string().optional(),
      }),
    ),
  );
}
export namespace Script {
  export const DedicatedWorkerRealmInfoSchema: z.ZodLazy<
    z.ZodIntersection<
      typeof Script.BaseRealmInfoSchema,
      z.ZodObject<{
        type: z.ZodLiteral<'dedicated-worker'>;
        owners: z.ZodTuple<[typeof Script.RealmSchema], null>;
      }>
    >
  > = z.lazy(() =>
    Script.BaseRealmInfoSchema.and(
      z.object({
        type: z.literal('dedicated-worker'),
        owners: z.tuple([Script.RealmSchema]),
      }),
    ),
  );
}
export namespace Script {
  export const SharedWorkerRealmInfoSchema: z.ZodLazy<
    z.ZodIntersection<
      typeof Script.BaseRealmInfoSchema,
      z.ZodObject<{
        type: z.ZodLiteral<'shared-worker'>;
      }>
    >
  > = z.lazy(() =>
    Script.BaseRealmInfoSchema.and(
      z.object({
        type: z.literal('shared-worker'),
      }),
    ),
  );
}
export namespace Script {
  export const ServiceWorkerRealmInfoSchema: z.ZodLazy<
    z.ZodIntersection<
      typeof Script.BaseRealmInfoSchema,
      z.ZodObject<{
        type: z.ZodLiteral<'service-worker'>;
      }>
    >
  > = z.lazy(() =>
    Script.BaseRealmInfoSchema.and(
      z.object({
        type: z.literal('service-worker'),
      }),
    ),
  );
}
export namespace Script {
  export const WorkerRealmInfoSchema: z.ZodLazy<
    z.ZodIntersection<
      typeof Script.BaseRealmInfoSchema,
      z.ZodObject<{
        type: z.ZodLiteral<'worker'>;
      }>
    >
  > = z.lazy(() =>
    Script.BaseRealmInfoSchema.and(
      z.object({
        type: z.literal('worker'),
      }),
    ),
  );
}
export namespace Script {
  export const PaintWorkletRealmInfoSchema: z.ZodLazy<
    z.ZodIntersection<
      typeof Script.BaseRealmInfoSchema,
      z.ZodObject<{
        type: z.ZodLiteral<'paint-worklet'>;
      }>
    >
  > = z.lazy(() =>
    Script.BaseRealmInfoSchema.and(
      z.object({
        type: z.literal('paint-worklet'),
      }),
    ),
  );
}
export namespace Script {
  export const AudioWorkletRealmInfoSchema: z.ZodLazy<
    z.ZodIntersection<
      typeof Script.BaseRealmInfoSchema,
      z.ZodObject<{
        type: z.ZodLiteral<'audio-worklet'>;
      }>
    >
  > = z.lazy(() =>
    Script.BaseRealmInfoSchema.and(
      z.object({
        type: z.literal('audio-worklet'),
      }),
    ),
  );
}
export namespace Script {
  export const WorkletRealmInfoSchema: z.ZodLazy<
    z.ZodIntersection<
      typeof Script.BaseRealmInfoSchema,
      z.ZodObject<{
        type: z.ZodLiteral<'worklet'>;
      }>
    >
  > = z.lazy(() =>
    Script.BaseRealmInfoSchema.and(
      z.object({
        type: z.literal('worklet'),
      }),
    ),
  );
}
export namespace Script {
  export const RealmTypeSchema: z.ZodLazy<
    z.ZodEnum<{
      window: 'window';
      'dedicated-worker': 'dedicated-worker';
      'shared-worker': 'shared-worker';
      'service-worker': 'service-worker';
      worker: 'worker';
      'paint-worklet': 'paint-worklet';
      'audio-worklet': 'audio-worklet';
      worklet: 'worklet';
    }>
  > = z.lazy(() =>
    z.enum([
      'window',
      'dedicated-worker',
      'shared-worker',
      'service-worker',
      'worker',
      'paint-worklet',
      'audio-worklet',
      'worklet',
    ]),
  );
}
export namespace Script {
  export const RemoteReferenceSchema: z.ZodLazy<
    z.ZodUnion<
      readonly [
        typeof Script.SharedReferenceSchema,
        typeof Script.RemoteObjectReferenceSchema,
      ]
    >
  > = z.lazy(() =>
    z.union([Script.SharedReferenceSchema, Script.RemoteObjectReferenceSchema]),
  );
}
export namespace Script {
  export const SharedReferenceSchema: z.ZodLazy<
    z.ZodIntersection<
      z.ZodObject<{
        sharedId: typeof Script.SharedIdSchema;
        handle: z.ZodOptional<typeof Script.HandleSchema>;
      }>,
      typeof ExtensibleSchema
    >
  > = z.lazy(() =>
    z
      .object({
        sharedId: Script.SharedIdSchema,
        handle: Script.HandleSchema.optional(),
      })
      .and(ExtensibleSchema),
  );
}
export namespace Script {
  export const RemoteObjectReferenceSchema: z.ZodLazy<
    z.ZodIntersection<
      z.ZodObject<{
        handle: typeof Script.HandleSchema;
        sharedId: z.ZodOptional<typeof Script.SharedIdSchema>;
      }>,
      typeof ExtensibleSchema
    >
  > = z.lazy(() =>
    z
      .object({
        handle: Script.HandleSchema,
        sharedId: Script.SharedIdSchema.optional(),
      })
      .and(ExtensibleSchema),
  );
}
export namespace Script {
  export const RemoteValueSchema: z.ZodType = z.lazy(() =>
    z.union([
      Script.PrimitiveProtocolValueSchema,
      Script.SymbolRemoteValueSchema,
      Script.ArrayRemoteValueSchema,
      Script.ObjectRemoteValueSchema,
      Script.FunctionRemoteValueSchema,
      Script.RegExpRemoteValueSchema,
      Script.DateRemoteValueSchema,
      Script.MapRemoteValueSchema,
      Script.SetRemoteValueSchema,
      Script.WeakMapRemoteValueSchema,
      Script.WeakSetRemoteValueSchema,
      Script.GeneratorRemoteValueSchema,
      Script.ErrorRemoteValueSchema,
      Script.ProxyRemoteValueSchema,
      Script.PromiseRemoteValueSchema,
      Script.TypedArrayRemoteValueSchema,
      Script.ArrayBufferRemoteValueSchema,
      Script.NodeListRemoteValueSchema,
      Script.HtmlCollectionRemoteValueSchema,
      Script.NodeRemoteValueSchema,
      Script.WindowProxyRemoteValueSchema,
    ]),
  );
}
export namespace Script {
  export const ListRemoteValueSchema: z.ZodLazy<
    z.ZodArray<typeof Script.RemoteValueSchema>
  > = z.lazy(() => z.array(Script.RemoteValueSchema));
}
export namespace Script {
  export const MappingRemoteValueSchema: z.ZodLazy<
    z.ZodArray<
      z.ZodTuple<
        [
          z.ZodUnion<readonly [typeof Script.RemoteValueSchema, z.ZodString]>,
          typeof Script.RemoteValueSchema,
        ],
        null
      >
    >
  > = z.lazy(() =>
    z.array(
      z.tuple([
        z.union([Script.RemoteValueSchema, z.string()]),
        Script.RemoteValueSchema,
      ]),
    ),
  );
}
export namespace Script {
  export const SymbolRemoteValueSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'symbol'>;
      handle: z.ZodOptional<typeof Script.HandleSchema>;
      internalId: z.ZodOptional<typeof Script.InternalIdSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('symbol'),
      handle: Script.HandleSchema.optional(),
      internalId: Script.InternalIdSchema.optional(),
    }),
  );
}
export namespace Script {
  export const ArrayRemoteValueSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'array'>;
      handle: z.ZodOptional<typeof Script.HandleSchema>;
      internalId: z.ZodOptional<typeof Script.InternalIdSchema>;
      value: z.ZodOptional<typeof Script.ListRemoteValueSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('array'),
      handle: Script.HandleSchema.optional(),
      internalId: Script.InternalIdSchema.optional(),
      value: Script.ListRemoteValueSchema.optional(),
    }),
  );
}
export namespace Script {
  export const ObjectRemoteValueSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'object'>;
      handle: z.ZodOptional<typeof Script.HandleSchema>;
      internalId: z.ZodOptional<typeof Script.InternalIdSchema>;
      value: z.ZodOptional<typeof Script.MappingRemoteValueSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('object'),
      handle: Script.HandleSchema.optional(),
      internalId: Script.InternalIdSchema.optional(),
      value: Script.MappingRemoteValueSchema.optional(),
    }),
  );
}
export namespace Script {
  export const FunctionRemoteValueSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'function'>;
      handle: z.ZodOptional<typeof Script.HandleSchema>;
      internalId: z.ZodOptional<typeof Script.InternalIdSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('function'),
      handle: Script.HandleSchema.optional(),
      internalId: Script.InternalIdSchema.optional(),
    }),
  );
}
export namespace Script {
  export const RegExpRemoteValueSchema: z.ZodLazy<
    z.ZodIntersection<
      typeof Script.RegExpLocalValueSchema,
      z.ZodObject<{
        handle: z.ZodOptional<typeof Script.HandleSchema>;
        internalId: z.ZodOptional<typeof Script.InternalIdSchema>;
      }>
    >
  > = z.lazy(() =>
    Script.RegExpLocalValueSchema.and(
      z.object({
        handle: Script.HandleSchema.optional(),
        internalId: Script.InternalIdSchema.optional(),
      }),
    ),
  );
}
export namespace Script {
  export const DateRemoteValueSchema: z.ZodLazy<
    z.ZodIntersection<
      typeof Script.DateLocalValueSchema,
      z.ZodObject<{
        handle: z.ZodOptional<typeof Script.HandleSchema>;
        internalId: z.ZodOptional<typeof Script.InternalIdSchema>;
      }>
    >
  > = z.lazy(() =>
    Script.DateLocalValueSchema.and(
      z.object({
        handle: Script.HandleSchema.optional(),
        internalId: Script.InternalIdSchema.optional(),
      }),
    ),
  );
}
export namespace Script {
  export const MapRemoteValueSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'map'>;
      handle: z.ZodOptional<typeof Script.HandleSchema>;
      internalId: z.ZodOptional<typeof Script.InternalIdSchema>;
      value: z.ZodOptional<typeof Script.MappingRemoteValueSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('map'),
      handle: Script.HandleSchema.optional(),
      internalId: Script.InternalIdSchema.optional(),
      value: Script.MappingRemoteValueSchema.optional(),
    }),
  );
}
export namespace Script {
  export const SetRemoteValueSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'set'>;
      handle: z.ZodOptional<typeof Script.HandleSchema>;
      internalId: z.ZodOptional<typeof Script.InternalIdSchema>;
      value: z.ZodOptional<typeof Script.ListRemoteValueSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('set'),
      handle: Script.HandleSchema.optional(),
      internalId: Script.InternalIdSchema.optional(),
      value: Script.ListRemoteValueSchema.optional(),
    }),
  );
}
export namespace Script {
  export const WeakMapRemoteValueSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'weakmap'>;
      handle: z.ZodOptional<typeof Script.HandleSchema>;
      internalId: z.ZodOptional<typeof Script.InternalIdSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('weakmap'),
      handle: Script.HandleSchema.optional(),
      internalId: Script.InternalIdSchema.optional(),
    }),
  );
}
export namespace Script {
  export const WeakSetRemoteValueSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'weakset'>;
      handle: z.ZodOptional<typeof Script.HandleSchema>;
      internalId: z.ZodOptional<typeof Script.InternalIdSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('weakset'),
      handle: Script.HandleSchema.optional(),
      internalId: Script.InternalIdSchema.optional(),
    }),
  );
}
export namespace Script {
  export const GeneratorRemoteValueSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'generator'>;
      handle: z.ZodOptional<typeof Script.HandleSchema>;
      internalId: z.ZodOptional<typeof Script.InternalIdSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('generator'),
      handle: Script.HandleSchema.optional(),
      internalId: Script.InternalIdSchema.optional(),
    }),
  );
}
export namespace Script {
  export const ErrorRemoteValueSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'error'>;
      handle: z.ZodOptional<typeof Script.HandleSchema>;
      internalId: z.ZodOptional<typeof Script.InternalIdSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('error'),
      handle: Script.HandleSchema.optional(),
      internalId: Script.InternalIdSchema.optional(),
    }),
  );
}
export namespace Script {
  export const ProxyRemoteValueSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'proxy'>;
      handle: z.ZodOptional<typeof Script.HandleSchema>;
      internalId: z.ZodOptional<typeof Script.InternalIdSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('proxy'),
      handle: Script.HandleSchema.optional(),
      internalId: Script.InternalIdSchema.optional(),
    }),
  );
}
export namespace Script {
  export const PromiseRemoteValueSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'promise'>;
      handle: z.ZodOptional<typeof Script.HandleSchema>;
      internalId: z.ZodOptional<typeof Script.InternalIdSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('promise'),
      handle: Script.HandleSchema.optional(),
      internalId: Script.InternalIdSchema.optional(),
    }),
  );
}
export namespace Script {
  export const TypedArrayRemoteValueSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'typedarray'>;
      handle: z.ZodOptional<typeof Script.HandleSchema>;
      internalId: z.ZodOptional<typeof Script.InternalIdSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('typedarray'),
      handle: Script.HandleSchema.optional(),
      internalId: Script.InternalIdSchema.optional(),
    }),
  );
}
export namespace Script {
  export const ArrayBufferRemoteValueSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'arraybuffer'>;
      handle: z.ZodOptional<typeof Script.HandleSchema>;
      internalId: z.ZodOptional<typeof Script.InternalIdSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('arraybuffer'),
      handle: Script.HandleSchema.optional(),
      internalId: Script.InternalIdSchema.optional(),
    }),
  );
}
export namespace Script {
  export const NodeListRemoteValueSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'nodelist'>;
      handle: z.ZodOptional<typeof Script.HandleSchema>;
      internalId: z.ZodOptional<typeof Script.InternalIdSchema>;
      value: z.ZodOptional<typeof Script.ListRemoteValueSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('nodelist'),
      handle: Script.HandleSchema.optional(),
      internalId: Script.InternalIdSchema.optional(),
      value: Script.ListRemoteValueSchema.optional(),
    }),
  );
}
export namespace Script {
  export const HtmlCollectionRemoteValueSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'htmlcollection'>;
      handle: z.ZodOptional<typeof Script.HandleSchema>;
      internalId: z.ZodOptional<typeof Script.InternalIdSchema>;
      value: z.ZodOptional<typeof Script.ListRemoteValueSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('htmlcollection'),
      handle: Script.HandleSchema.optional(),
      internalId: Script.InternalIdSchema.optional(),
      value: Script.ListRemoteValueSchema.optional(),
    }),
  );
}
export namespace Script {
  export const NodeRemoteValueSchema: z.ZodType = z.lazy(() =>
    z.object({
      type: z.literal('node'),
      sharedId: Script.SharedIdSchema.optional(),
      handle: Script.HandleSchema.optional(),
      internalId: Script.InternalIdSchema.optional(),
      value: Script.NodePropertiesSchema.optional(),
    }),
  );
}
export namespace Script {
  export const NodePropertiesSchema: z.ZodLazy<
    z.ZodObject<{
      nodeType: typeof JsUintSchema;
      childNodeCount: typeof JsUintSchema;
      attributes: z.ZodOptional<z.ZodRecord<z.ZodString, z.ZodString>>;
      children: z.ZodOptional<z.ZodArray<typeof Script.NodeRemoteValueSchema>>;
      localName: z.ZodOptional<z.ZodString>;
      mode: z.ZodOptional<z.ZodEnum<{open: 'open'; closed: 'closed'}>>;
      namespaceURI: z.ZodOptional<z.ZodString>;
      nodeValue: z.ZodOptional<z.ZodString>;
      shadowRoot: z.ZodOptional<
        z.ZodUnion<readonly [typeof Script.NodeRemoteValueSchema, z.ZodNull]>
      >;
    }>
  > = z.lazy(() =>
    z.object({
      nodeType: JsUintSchema,
      childNodeCount: JsUintSchema,
      attributes: z.record(z.string(), z.string()).optional(),
      children: z.array(Script.NodeRemoteValueSchema).optional(),
      localName: z.string().optional(),
      mode: z.enum(['open', 'closed']).optional(),
      namespaceURI: z.string().optional(),
      nodeValue: z.string().optional(),
      shadowRoot: z.union([Script.NodeRemoteValueSchema, z.null()]).optional(),
    }),
  );
}
export namespace Script {
  export const WindowProxyRemoteValueSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'window'>;
      value: typeof Script.WindowProxyPropertiesSchema;
      handle: z.ZodOptional<typeof Script.HandleSchema>;
      internalId: z.ZodOptional<typeof Script.InternalIdSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('window'),
      value: Script.WindowProxyPropertiesSchema,
      handle: Script.HandleSchema.optional(),
      internalId: Script.InternalIdSchema.optional(),
    }),
  );
}
export namespace Script {
  export const WindowProxyPropertiesSchema: z.ZodLazy<
    z.ZodObject<{
      context: typeof BrowsingContext.BrowsingContextSchema;
    }>
  > = z.lazy(() =>
    z.object({
      context: BrowsingContext.BrowsingContextSchema,
    }),
  );
}
export namespace Script {
  export const ResultOwnershipSchema: z.ZodLazy<
    z.ZodEnum<{root: 'root'; none: 'none'}>
  > = z.lazy(() => z.enum(['root', 'none']));
}
export namespace Script {
  export const SerializationOptionsSchema: z.ZodLazy<
    z.ZodObject<{
      maxDomDepth: z.ZodOptional<
        z.ZodDefault<z.ZodUnion<readonly [typeof JsUintSchema, z.ZodNull]>>
      >;
      maxObjectDepth: z.ZodOptional<
        z.ZodDefault<z.ZodUnion<readonly [typeof JsUintSchema, z.ZodNull]>>
      >;
      includeShadowTree: z.ZodOptional<
        z.ZodDefault<z.ZodEnum<{none: 'none'; open: 'open'; all: 'all'}>>
      >;
    }>
  > = z.lazy(() =>
    z.object({
      maxDomDepth: z.union([JsUintSchema, z.null()]).default(0).optional(),
      maxObjectDepth: z
        .union([JsUintSchema, z.null()])
        .default(null)
        .optional(),
      includeShadowTree: z
        .enum(['none', 'open', 'all'])
        .default('none')
        .optional(),
    }),
  );
}
export namespace Script {
  export const SharedIdSchema: z.ZodLazy<z.ZodString> = z.lazy(() =>
    z.string(),
  );
}
export namespace Script {
  export const StackFrameSchema: z.ZodLazy<
    z.ZodObject<{
      columnNumber: typeof JsUintSchema;
      functionName: z.ZodString;
      lineNumber: typeof JsUintSchema;
      url: z.ZodString;
    }>
  > = z.lazy(() =>
    z.object({
      columnNumber: JsUintSchema,
      functionName: z.string(),
      lineNumber: JsUintSchema,
      url: z.string(),
    }),
  );
}
export namespace Script {
  export const StackTraceSchema: z.ZodLazy<
    z.ZodObject<{
      callFrames: z.ZodArray<typeof Script.StackFrameSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      callFrames: z.array(Script.StackFrameSchema),
    }),
  );
}
export namespace Script {
  export const SourceSchema: z.ZodLazy<
    z.ZodObject<{
      realm: typeof Script.RealmSchema;
      context: z.ZodOptional<typeof BrowsingContext.BrowsingContextSchema>;
      userContext: z.ZodOptional<typeof Browser.UserContextSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      realm: Script.RealmSchema,
      context: BrowsingContext.BrowsingContextSchema.optional(),
      userContext: Browser.UserContextSchema.optional(),
    }),
  );
}
export namespace Script {
  export const RealmTargetSchema: z.ZodLazy<
    z.ZodObject<{
      realm: typeof Script.RealmSchema;
    }>
  > = z.lazy(() =>
    z.object({
      realm: Script.RealmSchema,
    }),
  );
}
export namespace Script {
  export const ContextTargetSchema: z.ZodLazy<
    z.ZodObject<{
      context: typeof BrowsingContext.BrowsingContextSchema;
      sandbox: z.ZodOptional<z.ZodString>;
    }>
  > = z.lazy(() =>
    z.object({
      context: BrowsingContext.BrowsingContextSchema,
      sandbox: z.string().optional(),
    }),
  );
}
export namespace Script {
  export const TargetSchema: z.ZodLazy<
    z.ZodUnion<
      readonly [
        typeof Script.ContextTargetSchema,
        typeof Script.RealmTargetSchema,
      ]
    >
  > = z.lazy(() =>
    z.union([Script.ContextTargetSchema, Script.RealmTargetSchema]),
  );
}
export namespace Script {
  export const AddPreloadScriptSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'script.addPreloadScript'>;
      params: typeof Script.AddPreloadScriptParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('script.addPreloadScript'),
      params: Script.AddPreloadScriptParametersSchema,
    }),
  );
}
export namespace Script {
  export const AddPreloadScriptParametersSchema: z.ZodLazy<
    z.ZodObject<{
      functionDeclaration: z.ZodString;
      arguments: z.ZodOptional<z.ZodArray<typeof Script.ChannelValueSchema>>;
      contexts: z.ZodOptional<
        z.ZodArray<typeof BrowsingContext.BrowsingContextSchema>
      >;
      userContexts: z.ZodOptional<z.ZodArray<typeof Browser.UserContextSchema>>;
      sandbox: z.ZodOptional<z.ZodString>;
    }>
  > = z.lazy(() =>
    z.object({
      functionDeclaration: z.string(),
      arguments: z.array(Script.ChannelValueSchema).optional(),
      contexts: z
        .array(BrowsingContext.BrowsingContextSchema)
        .nonempty()
        .optional(),
      userContexts: z.array(Browser.UserContextSchema).nonempty().optional(),
      sandbox: z.string().optional(),
    }),
  );
}
export namespace Script {
  export const AddPreloadScriptResultSchema: z.ZodLazy<
    z.ZodObject<{
      script: typeof Script.PreloadScriptSchema;
    }>
  > = z.lazy(() =>
    z.object({
      script: Script.PreloadScriptSchema,
    }),
  );
}
export namespace Script {
  export const DisownSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'script.disown'>;
      params: typeof Script.DisownParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('script.disown'),
      params: Script.DisownParametersSchema,
    }),
  );
}
export namespace Script {
  export const DisownParametersSchema: z.ZodLazy<
    z.ZodObject<{
      handles: z.ZodArray<typeof Script.HandleSchema>;
      target: typeof Script.TargetSchema;
    }>
  > = z.lazy(() =>
    z.object({
      handles: z.array(Script.HandleSchema),
      target: Script.TargetSchema,
    }),
  );
}
export namespace Script {
  export const DisownResultSchema: z.ZodLazy<typeof EmptyResultSchema> = z.lazy(
    () => EmptyResultSchema,
  );
}
export namespace Script {
  export const CallFunctionSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'script.callFunction'>;
      params: typeof Script.CallFunctionParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('script.callFunction'),
      params: Script.CallFunctionParametersSchema,
    }),
  );
}
export namespace Script {
  export const CallFunctionParametersSchema: z.ZodLazy<
    z.ZodObject<{
      functionDeclaration: z.ZodString;
      awaitPromise: z.ZodBoolean;
      target: typeof Script.TargetSchema;
      arguments: z.ZodOptional<z.ZodArray<typeof Script.LocalValueSchema>>;
      resultOwnership: z.ZodOptional<typeof Script.ResultOwnershipSchema>;
      serializationOptions: z.ZodOptional<
        typeof Script.SerializationOptionsSchema
      >;
      this: z.ZodOptional<typeof Script.LocalValueSchema>;
      userActivation: z.ZodOptional<z.ZodDefault<z.ZodBoolean>>;
    }>
  > = z.lazy(() =>
    z.object({
      functionDeclaration: z.string(),
      awaitPromise: z.boolean(),
      target: Script.TargetSchema,
      arguments: z.array(Script.LocalValueSchema).optional(),
      resultOwnership: Script.ResultOwnershipSchema.optional(),
      serializationOptions: Script.SerializationOptionsSchema.optional(),
      this: Script.LocalValueSchema.optional(),
      userActivation: z.boolean().default(false).optional(),
    }),
  );
}
export namespace Script {
  export const CallFunctionResultSchema: z.ZodLazy<
    typeof Script.EvaluateResultSchema
  > = z.lazy(() => Script.EvaluateResultSchema);
}
export namespace Script {
  export const EvaluateSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'script.evaluate'>;
      params: typeof Script.EvaluateParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('script.evaluate'),
      params: Script.EvaluateParametersSchema,
    }),
  );
}
export namespace Script {
  export const EvaluateParametersSchema: z.ZodLazy<
    z.ZodObject<{
      expression: z.ZodString;
      target: typeof Script.TargetSchema;
      awaitPromise: z.ZodBoolean;
      resultOwnership: z.ZodOptional<typeof Script.ResultOwnershipSchema>;
      serializationOptions: z.ZodOptional<
        typeof Script.SerializationOptionsSchema
      >;
      userActivation: z.ZodOptional<z.ZodDefault<z.ZodBoolean>>;
    }>
  > = z.lazy(() =>
    z.object({
      expression: z.string(),
      target: Script.TargetSchema,
      awaitPromise: z.boolean(),
      resultOwnership: Script.ResultOwnershipSchema.optional(),
      serializationOptions: Script.SerializationOptionsSchema.optional(),
      userActivation: z.boolean().default(false).optional(),
    }),
  );
}
export namespace Script {
  export const GetRealmsSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'script.getRealms'>;
      params: typeof Script.GetRealmsParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('script.getRealms'),
      params: Script.GetRealmsParametersSchema,
    }),
  );
}
export namespace Script {
  export const GetRealmsParametersSchema: z.ZodLazy<
    z.ZodObject<{
      context: z.ZodOptional<typeof BrowsingContext.BrowsingContextSchema>;
      type: z.ZodOptional<typeof Script.RealmTypeSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      context: BrowsingContext.BrowsingContextSchema.optional(),
      type: Script.RealmTypeSchema.optional(),
    }),
  );
}
export namespace Script {
  export const GetRealmsResultSchema: z.ZodLazy<
    z.ZodObject<{
      realms: z.ZodArray<typeof Script.RealmInfoSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      realms: z.array(Script.RealmInfoSchema),
    }),
  );
}
export namespace Script {
  export const RemovePreloadScriptSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'script.removePreloadScript'>;
      params: typeof Script.RemovePreloadScriptParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('script.removePreloadScript'),
      params: Script.RemovePreloadScriptParametersSchema,
    }),
  );
}
export namespace Script {
  export const RemovePreloadScriptParametersSchema: z.ZodLazy<
    z.ZodObject<{
      script: typeof Script.PreloadScriptSchema;
    }>
  > = z.lazy(() =>
    z.object({
      script: Script.PreloadScriptSchema,
    }),
  );
}
export namespace Script {
  export const RemovePreloadScriptResultSchema: z.ZodLazy<
    typeof EmptyResultSchema
  > = z.lazy(() => EmptyResultSchema);
}
export namespace Script {
  export const MessageSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'script.message'>;
      params: typeof Script.MessageParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('script.message'),
      params: Script.MessageParametersSchema,
    }),
  );
}
export namespace Script {
  export const MessageParametersSchema: z.ZodLazy<
    z.ZodObject<{
      channel: typeof Script.ChannelSchema;
      data: typeof Script.RemoteValueSchema;
      source: typeof Script.SourceSchema;
    }>
  > = z.lazy(() =>
    z.object({
      channel: Script.ChannelSchema,
      data: Script.RemoteValueSchema,
      source: Script.SourceSchema,
    }),
  );
}
export namespace Script {
  export const RealmCreatedSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'script.realmCreated'>;
      params: typeof Script.RealmInfoSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('script.realmCreated'),
      params: Script.RealmInfoSchema,
    }),
  );
}
export namespace Script {
  export const RealmDestroyedSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'script.realmDestroyed'>;
      params: typeof Script.RealmDestroyedParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('script.realmDestroyed'),
      params: Script.RealmDestroyedParametersSchema,
    }),
  );
}
export namespace Script {
  export const RealmDestroyedParametersSchema: z.ZodLazy<
    z.ZodObject<{
      realm: typeof Script.RealmSchema;
    }>
  > = z.lazy(() =>
    z.object({
      realm: Script.RealmSchema,
    }),
  );
}
export const StorageCommandSchema: z.ZodLazy<
  z.ZodUnion<
    readonly [
      typeof Storage.DeleteCookiesSchema,
      typeof Storage.GetCookiesSchema,
      typeof Storage.SetCookieSchema,
    ]
  >
> = z.lazy(() =>
  z.union([
    Storage.DeleteCookiesSchema,
    Storage.GetCookiesSchema,
    Storage.SetCookieSchema,
  ]),
);
export const StorageResultSchema: z.ZodLazy<
  z.ZodUnion<
    readonly [
      typeof Storage.DeleteCookiesResultSchema,
      typeof Storage.GetCookiesResultSchema,
      typeof Storage.SetCookieResultSchema,
    ]
  >
> = z.lazy(() =>
  z.union([
    Storage.DeleteCookiesResultSchema,
    Storage.GetCookiesResultSchema,
    Storage.SetCookieResultSchema,
  ]),
);
export namespace Storage {
  export const PartitionKeySchema: z.ZodLazy<
    z.ZodIntersection<
      z.ZodObject<{
        userContext: z.ZodOptional<z.ZodString>;
        sourceOrigin: z.ZodOptional<z.ZodString>;
      }>,
      typeof ExtensibleSchema
    >
  > = z.lazy(() =>
    z
      .object({
        userContext: z.string().optional(),
        sourceOrigin: z.string().optional(),
      })
      .and(ExtensibleSchema),
  );
}
export namespace Storage {
  export const GetCookiesSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'storage.getCookies'>;
      params: typeof Storage.GetCookiesParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('storage.getCookies'),
      params: Storage.GetCookiesParametersSchema,
    }),
  );
}
export namespace Storage {
  export const CookieFilterSchema: z.ZodLazy<
    z.ZodIntersection<
      z.ZodObject<{
        name: z.ZodOptional<z.ZodString>;
        value: z.ZodOptional<typeof Network.BytesValueSchema>;
        domain: z.ZodOptional<z.ZodString>;
        path: z.ZodOptional<z.ZodString>;
        size: z.ZodOptional<typeof JsUintSchema>;
        httpOnly: z.ZodOptional<z.ZodBoolean>;
        secure: z.ZodOptional<z.ZodBoolean>;
        sameSite: z.ZodOptional<typeof Network.SameSiteSchema>;
        expiry: z.ZodOptional<typeof JsUintSchema>;
      }>,
      typeof ExtensibleSchema
    >
  > = z.lazy(() =>
    z
      .object({
        name: z.string().optional(),
        value: Network.BytesValueSchema.optional(),
        domain: z.string().optional(),
        path: z.string().optional(),
        size: JsUintSchema.optional(),
        httpOnly: z.boolean().optional(),
        secure: z.boolean().optional(),
        sameSite: Network.SameSiteSchema.optional(),
        expiry: JsUintSchema.optional(),
      })
      .and(ExtensibleSchema),
  );
}
export namespace Storage {
  export const BrowsingContextPartitionDescriptorSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'context'>;
      context: typeof BrowsingContext.BrowsingContextSchema;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('context'),
      context: BrowsingContext.BrowsingContextSchema,
    }),
  );
}
export namespace Storage {
  export const StorageKeyPartitionDescriptorSchema: z.ZodLazy<
    z.ZodIntersection<
      z.ZodObject<{
        type: z.ZodLiteral<'storageKey'>;
        userContext: z.ZodOptional<z.ZodString>;
        sourceOrigin: z.ZodOptional<z.ZodString>;
      }>,
      typeof ExtensibleSchema
    >
  > = z.lazy(() =>
    z
      .object({
        type: z.literal('storageKey'),
        userContext: z.string().optional(),
        sourceOrigin: z.string().optional(),
      })
      .and(ExtensibleSchema),
  );
}
export namespace Storage {
  export const PartitionDescriptorSchema: z.ZodLazy<
    z.ZodUnion<
      readonly [
        typeof Storage.BrowsingContextPartitionDescriptorSchema,
        typeof Storage.StorageKeyPartitionDescriptorSchema,
      ]
    >
  > = z.lazy(() =>
    z.union([
      Storage.BrowsingContextPartitionDescriptorSchema,
      Storage.StorageKeyPartitionDescriptorSchema,
    ]),
  );
}
export namespace Storage {
  export const GetCookiesParametersSchema: z.ZodLazy<
    z.ZodObject<{
      filter: z.ZodOptional<typeof Storage.CookieFilterSchema>;
      partition: z.ZodOptional<typeof Storage.PartitionDescriptorSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      filter: Storage.CookieFilterSchema.optional(),
      partition: Storage.PartitionDescriptorSchema.optional(),
    }),
  );
}
export namespace Storage {
  export const GetCookiesResultSchema: z.ZodLazy<
    z.ZodObject<{
      cookies: z.ZodArray<typeof Network.CookieSchema>;
      partitionKey: typeof Storage.PartitionKeySchema;
    }>
  > = z.lazy(() =>
    z.object({
      cookies: z.array(Network.CookieSchema),
      partitionKey: Storage.PartitionKeySchema,
    }),
  );
}
export namespace Storage {
  export const SetCookieSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'storage.setCookie'>;
      params: typeof Storage.SetCookieParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('storage.setCookie'),
      params: Storage.SetCookieParametersSchema,
    }),
  );
}
export namespace Storage {
  export const PartialCookieSchema: z.ZodLazy<
    z.ZodIntersection<
      z.ZodObject<{
        name: z.ZodString;
        value: typeof Network.BytesValueSchema;
        domain: z.ZodString;
        path: z.ZodOptional<z.ZodString>;
        httpOnly: z.ZodOptional<z.ZodBoolean>;
        secure: z.ZodOptional<z.ZodBoolean>;
        sameSite: z.ZodOptional<typeof Network.SameSiteSchema>;
        expiry: z.ZodOptional<typeof JsUintSchema>;
      }>,
      typeof ExtensibleSchema
    >
  > = z.lazy(() =>
    z
      .object({
        name: z.string(),
        value: Network.BytesValueSchema,
        domain: z.string(),
        path: z.string().optional(),
        httpOnly: z.boolean().optional(),
        secure: z.boolean().optional(),
        sameSite: Network.SameSiteSchema.optional(),
        expiry: JsUintSchema.optional(),
      })
      .and(ExtensibleSchema),
  );
}
export namespace Storage {
  export const SetCookieParametersSchema: z.ZodLazy<
    z.ZodObject<{
      cookie: typeof Storage.PartialCookieSchema;
      partition: z.ZodOptional<typeof Storage.PartitionDescriptorSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      cookie: Storage.PartialCookieSchema,
      partition: Storage.PartitionDescriptorSchema.optional(),
    }),
  );
}
export namespace Storage {
  export const SetCookieResultSchema: z.ZodLazy<
    z.ZodObject<{
      partitionKey: typeof Storage.PartitionKeySchema;
    }>
  > = z.lazy(() =>
    z.object({
      partitionKey: Storage.PartitionKeySchema,
    }),
  );
}
export namespace Storage {
  export const DeleteCookiesSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'storage.deleteCookies'>;
      params: typeof Storage.DeleteCookiesParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('storage.deleteCookies'),
      params: Storage.DeleteCookiesParametersSchema,
    }),
  );
}
export namespace Storage {
  export const DeleteCookiesParametersSchema: z.ZodLazy<
    z.ZodObject<{
      filter: z.ZodOptional<typeof Storage.CookieFilterSchema>;
      partition: z.ZodOptional<typeof Storage.PartitionDescriptorSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      filter: Storage.CookieFilterSchema.optional(),
      partition: Storage.PartitionDescriptorSchema.optional(),
    }),
  );
}
export namespace Storage {
  export const DeleteCookiesResultSchema: z.ZodLazy<
    z.ZodObject<{
      partitionKey: typeof Storage.PartitionKeySchema;
    }>
  > = z.lazy(() =>
    z.object({
      partitionKey: Storage.PartitionKeySchema,
    }),
  );
}
export const LogEventSchema: z.ZodLazy<typeof Log.EntryAddedSchema> = z.lazy(
  () => Log.EntryAddedSchema,
);
export namespace Log {
  export const LevelSchema: z.ZodLazy<
    z.ZodEnum<{debug: 'debug'; info: 'info'; warn: 'warn'; error: 'error'}>
  > = z.lazy(() => z.enum(['debug', 'info', 'warn', 'error']));
}
export namespace Log {
  export const EntrySchema: z.ZodLazy<
    z.ZodUnion<
      readonly [
        typeof Log.GenericLogEntrySchema,
        typeof Log.ConsoleLogEntrySchema,
        typeof Log.JavascriptLogEntrySchema,
      ]
    >
  > = z.lazy(() =>
    z.union([
      Log.GenericLogEntrySchema,
      Log.ConsoleLogEntrySchema,
      Log.JavascriptLogEntrySchema,
    ]),
  );
}
export namespace Log {
  export const BaseLogEntrySchema: z.ZodLazy<
    z.ZodObject<{
      level: typeof Log.LevelSchema;
      source: typeof Script.SourceSchema;
      text: z.ZodUnion<readonly [z.ZodString, z.ZodNull]>;
      timestamp: typeof JsUintSchema;
      stackTrace: z.ZodOptional<typeof Script.StackTraceSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      level: Log.LevelSchema,
      source: Script.SourceSchema,
      text: z.union([z.string(), z.null()]),
      timestamp: JsUintSchema,
      stackTrace: Script.StackTraceSchema.optional(),
    }),
  );
}
export namespace Log {
  export const GenericLogEntrySchema: z.ZodLazy<
    z.ZodIntersection<
      typeof Log.BaseLogEntrySchema,
      z.ZodObject<{
        type: z.ZodString;
      }>
    >
  > = z.lazy(() =>
    Log.BaseLogEntrySchema.and(
      z.object({
        type: z.string(),
      }),
    ),
  );
}
export namespace Log {
  export const ConsoleLogEntrySchema: z.ZodLazy<
    z.ZodIntersection<
      typeof Log.BaseLogEntrySchema,
      z.ZodObject<{
        type: z.ZodLiteral<'console'>;
        method: z.ZodString;
        args: z.ZodArray<typeof Script.RemoteValueSchema>;
      }>
    >
  > = z.lazy(() =>
    Log.BaseLogEntrySchema.and(
      z.object({
        type: z.literal('console'),
        method: z.string(),
        args: z.array(Script.RemoteValueSchema),
      }),
    ),
  );
}
export namespace Log {
  export const JavascriptLogEntrySchema: z.ZodLazy<
    z.ZodIntersection<
      typeof Log.BaseLogEntrySchema,
      z.ZodObject<{
        type: z.ZodLiteral<'javascript'>;
      }>
    >
  > = z.lazy(() =>
    Log.BaseLogEntrySchema.and(
      z.object({
        type: z.literal('javascript'),
      }),
    ),
  );
}
export namespace Log {
  export const EntryAddedSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'log.entryAdded'>;
      params: typeof Log.EntrySchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('log.entryAdded'),
      params: Log.EntrySchema,
    }),
  );
}
export const InputCommandSchema: z.ZodLazy<
  z.ZodUnion<
    readonly [
      typeof Input.PerformActionsSchema,
      typeof Input.ReleaseActionsSchema,
      typeof Input.SetFilesSchema,
    ]
  >
> = z.lazy(() =>
  z.union([
    Input.PerformActionsSchema,
    Input.ReleaseActionsSchema,
    Input.SetFilesSchema,
  ]),
);
export const InputResultSchema: z.ZodLazy<
  z.ZodUnion<
    readonly [
      typeof Input.PerformActionsResultSchema,
      typeof Input.ReleaseActionsResultSchema,
      typeof Input.SetFilesResultSchema,
    ]
  >
> = z.lazy(() =>
  z.union([
    Input.PerformActionsResultSchema,
    Input.ReleaseActionsResultSchema,
    Input.SetFilesResultSchema,
  ]),
);
export const InputEventSchema: z.ZodLazy<typeof Input.FileDialogOpenedSchema> =
  z.lazy(() => Input.FileDialogOpenedSchema);
export namespace Input {
  export const ElementOriginSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'element'>;
      element: typeof Script.SharedReferenceSchema;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('element'),
      element: Script.SharedReferenceSchema,
    }),
  );
}
export namespace Input {
  export const PerformActionsSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'input.performActions'>;
      params: typeof Input.PerformActionsParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('input.performActions'),
      params: Input.PerformActionsParametersSchema,
    }),
  );
}
export namespace Input {
  export const PerformActionsParametersSchema: z.ZodLazy<
    z.ZodObject<{
      context: typeof BrowsingContext.BrowsingContextSchema;
      actions: z.ZodArray<typeof Input.SourceActionsSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      context: BrowsingContext.BrowsingContextSchema,
      actions: z.array(Input.SourceActionsSchema),
    }),
  );
}
export namespace Input {
  export const SourceActionsSchema: z.ZodLazy<
    z.ZodUnion<
      readonly [
        typeof Input.NoneSourceActionsSchema,
        typeof Input.KeySourceActionsSchema,
        typeof Input.PointerSourceActionsSchema,
        typeof Input.WheelSourceActionsSchema,
      ]
    >
  > = z.lazy(() =>
    z.union([
      Input.NoneSourceActionsSchema,
      Input.KeySourceActionsSchema,
      Input.PointerSourceActionsSchema,
      Input.WheelSourceActionsSchema,
    ]),
  );
}
export namespace Input {
  export const NoneSourceActionsSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'none'>;
      id: z.ZodString;
      actions: z.ZodArray<typeof Input.NoneSourceActionSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('none'),
      id: z.string(),
      actions: z.array(Input.NoneSourceActionSchema),
    }),
  );
}
export namespace Input {
  export const NoneSourceActionSchema: z.ZodLazy<
    typeof Input.PauseActionSchema
  > = z.lazy(() => Input.PauseActionSchema);
}
export namespace Input {
  export const KeySourceActionsSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'key'>;
      id: z.ZodString;
      actions: z.ZodArray<typeof Input.KeySourceActionSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('key'),
      id: z.string(),
      actions: z.array(Input.KeySourceActionSchema),
    }),
  );
}
export namespace Input {
  export const KeySourceActionSchema: z.ZodLazy<
    z.ZodUnion<
      readonly [
        typeof Input.PauseActionSchema,
        typeof Input.KeyDownActionSchema,
        typeof Input.KeyUpActionSchema,
      ]
    >
  > = z.lazy(() =>
    z.union([
      Input.PauseActionSchema,
      Input.KeyDownActionSchema,
      Input.KeyUpActionSchema,
    ]),
  );
}
export namespace Input {
  export const PointerSourceActionsSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'pointer'>;
      id: z.ZodString;
      parameters: z.ZodOptional<typeof Input.PointerParametersSchema>;
      actions: z.ZodArray<typeof Input.PointerSourceActionSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('pointer'),
      id: z.string(),
      parameters: Input.PointerParametersSchema.optional(),
      actions: z.array(Input.PointerSourceActionSchema),
    }),
  );
}
export namespace Input {
  export const PointerTypeSchema: z.ZodLazy<
    z.ZodEnum<{mouse: 'mouse'; pen: 'pen'; touch: 'touch'}>
  > = z.lazy(() => z.enum(['mouse', 'pen', 'touch']));
}
export namespace Input {
  export const PointerParametersSchema: z.ZodLazy<
    z.ZodObject<{
      pointerType: z.ZodOptional<z.ZodDefault<typeof Input.PointerTypeSchema>>;
    }>
  > = z.lazy(() =>
    z.object({
      pointerType: Input.PointerTypeSchema.default('mouse').optional(),
    }),
  );
}
export namespace Input {
  export const PointerSourceActionSchema: z.ZodLazy<
    z.ZodUnion<
      readonly [
        typeof Input.PauseActionSchema,
        typeof Input.PointerDownActionSchema,
        typeof Input.PointerUpActionSchema,
        typeof Input.PointerMoveActionSchema,
      ]
    >
  > = z.lazy(() =>
    z.union([
      Input.PauseActionSchema,
      Input.PointerDownActionSchema,
      Input.PointerUpActionSchema,
      Input.PointerMoveActionSchema,
    ]),
  );
}
export namespace Input {
  export const WheelSourceActionsSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'wheel'>;
      id: z.ZodString;
      actions: z.ZodArray<typeof Input.WheelSourceActionSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('wheel'),
      id: z.string(),
      actions: z.array(Input.WheelSourceActionSchema),
    }),
  );
}
export namespace Input {
  export const WheelSourceActionSchema: z.ZodLazy<
    z.ZodUnion<
      readonly [
        typeof Input.PauseActionSchema,
        typeof Input.WheelScrollActionSchema,
      ]
    >
  > = z.lazy(() =>
    z.union([Input.PauseActionSchema, Input.WheelScrollActionSchema]),
  );
}
export namespace Input {
  export const PauseActionSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'pause'>;
      duration: z.ZodOptional<typeof JsUintSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('pause'),
      duration: JsUintSchema.optional(),
    }),
  );
}
export namespace Input {
  export const KeyDownActionSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'keyDown'>;
      value: z.ZodString;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('keyDown'),
      value: z.string(),
    }),
  );
}
export namespace Input {
  export const KeyUpActionSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'keyUp'>;
      value: z.ZodString;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('keyUp'),
      value: z.string(),
    }),
  );
}
export namespace Input {
  export const PointerUpActionSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'pointerUp'>;
      button: typeof JsUintSchema;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('pointerUp'),
      button: JsUintSchema,
    }),
  );
}
export namespace Input {
  export const PointerDownActionSchema: z.ZodLazy<
    z.ZodIntersection<
      z.ZodObject<{
        type: z.ZodLiteral<'pointerDown'>;
        button: typeof JsUintSchema;
      }>,
      typeof Input.PointerCommonPropertiesSchema
    >
  > = z.lazy(() =>
    z
      .object({
        type: z.literal('pointerDown'),
        button: JsUintSchema,
      })
      .and(Input.PointerCommonPropertiesSchema),
  );
}
export namespace Input {
  export const PointerMoveActionSchema: z.ZodLazy<
    z.ZodIntersection<
      z.ZodObject<{
        type: z.ZodLiteral<'pointerMove'>;
        x: z.ZodNumber;
        y: z.ZodNumber;
        duration: z.ZodOptional<typeof JsUintSchema>;
        origin: z.ZodOptional<typeof Input.OriginSchema>;
      }>,
      typeof Input.PointerCommonPropertiesSchema
    >
  > = z.lazy(() =>
    z
      .object({
        type: z.literal('pointerMove'),
        x: z.number(),
        y: z.number(),
        duration: JsUintSchema.optional(),
        origin: Input.OriginSchema.optional(),
      })
      .and(Input.PointerCommonPropertiesSchema),
  );
}
export namespace Input {
  export const WheelScrollActionSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'scroll'>;
      x: typeof JsIntSchema;
      y: typeof JsIntSchema;
      deltaX: typeof JsIntSchema;
      deltaY: typeof JsIntSchema;
      duration: z.ZodOptional<typeof JsUintSchema>;
      origin: z.ZodOptional<z.ZodDefault<typeof Input.OriginSchema>>;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('scroll'),
      x: JsIntSchema,
      y: JsIntSchema,
      deltaX: JsIntSchema,
      deltaY: JsIntSchema,
      duration: JsUintSchema.optional(),
      origin: Input.OriginSchema.default('viewport').optional(),
    }),
  );
}
export namespace Input {
  export const PointerCommonPropertiesSchema: z.ZodLazy<
    z.ZodObject<{
      width: z.ZodOptional<typeof JsUintSchema>;
      height: z.ZodOptional<typeof JsUintSchema>;
      pressure: z.ZodOptional<z.ZodNumber>;
      tangentialPressure: z.ZodOptional<z.ZodNumber>;
      twist: z.ZodOptional<z.ZodNumber>;
      altitudeAngle: z.ZodOptional<z.ZodNumber>;
      azimuthAngle: z.ZodOptional<z.ZodNumber>;
    }>
  > = z.lazy(() =>
    z.object({
      width: JsUintSchema.optional(),
      height: JsUintSchema.optional(),
      pressure: z.number().gte(0).lte(1).optional(),
      tangentialPressure: z.number().gte(-1).lte(1).optional(),
      twist: z.number().int().nonnegative().gte(0).lte(359).optional(),
      altitudeAngle: z.number().gte(0).lte(1.5707963267948966).optional(),
      azimuthAngle: z.number().gte(0).lte(6.283185307179586).optional(),
    }),
  );
}
export namespace Input {
  export const OriginSchema: z.ZodLazy<
    z.ZodUnion<
      readonly [
        z.ZodLiteral<'viewport'>,
        z.ZodLiteral<'pointer'>,
        typeof Input.ElementOriginSchema,
      ]
    >
  > = z.lazy(() =>
    z.union([
      z.literal('viewport'),
      z.literal('pointer'),
      Input.ElementOriginSchema,
    ]),
  );
}
export namespace Input {
  export const PerformActionsResultSchema: z.ZodLazy<typeof EmptyResultSchema> =
    z.lazy(() => EmptyResultSchema);
}
export namespace Input {
  export const ReleaseActionsSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'input.releaseActions'>;
      params: typeof Input.ReleaseActionsParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('input.releaseActions'),
      params: Input.ReleaseActionsParametersSchema,
    }),
  );
}
export namespace Input {
  export const ReleaseActionsParametersSchema: z.ZodLazy<
    z.ZodObject<{
      context: typeof BrowsingContext.BrowsingContextSchema;
    }>
  > = z.lazy(() =>
    z.object({
      context: BrowsingContext.BrowsingContextSchema,
    }),
  );
}
export namespace Input {
  export const ReleaseActionsResultSchema: z.ZodLazy<typeof EmptyResultSchema> =
    z.lazy(() => EmptyResultSchema);
}
export namespace Input {
  export const SetFilesSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'input.setFiles'>;
      params: typeof Input.SetFilesParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('input.setFiles'),
      params: Input.SetFilesParametersSchema,
    }),
  );
}
export namespace Input {
  export const SetFilesParametersSchema: z.ZodLazy<
    z.ZodObject<{
      context: typeof BrowsingContext.BrowsingContextSchema;
      element: typeof Script.SharedReferenceSchema;
      files: z.ZodArray<z.ZodString>;
    }>
  > = z.lazy(() =>
    z.object({
      context: BrowsingContext.BrowsingContextSchema,
      element: Script.SharedReferenceSchema,
      files: z.array(z.string()),
    }),
  );
}
export namespace Input {
  export const SetFilesResultSchema: z.ZodLazy<typeof EmptyResultSchema> =
    z.lazy(() => EmptyResultSchema);
}
export namespace Input {
  export const FileDialogOpenedSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'input.fileDialogOpened'>;
      params: typeof Input.FileDialogInfoSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('input.fileDialogOpened'),
      params: Input.FileDialogInfoSchema,
    }),
  );
}
export namespace Input {
  export const FileDialogInfoSchema: z.ZodLazy<
    z.ZodObject<{
      context: typeof BrowsingContext.BrowsingContextSchema;
      userContext: z.ZodOptional<typeof Browser.UserContextSchema>;
      element: z.ZodOptional<typeof Script.SharedReferenceSchema>;
      multiple: z.ZodBoolean;
    }>
  > = z.lazy(() =>
    z.object({
      context: BrowsingContext.BrowsingContextSchema,
      userContext: Browser.UserContextSchema.optional(),
      element: Script.SharedReferenceSchema.optional(),
      multiple: z.boolean(),
    }),
  );
}
export const WebExtensionCommandSchema: z.ZodLazy<
  z.ZodUnion<
    readonly [
      typeof WebExtension.InstallSchema,
      typeof WebExtension.UninstallSchema,
    ]
  >
> = z.lazy(() =>
  z.union([WebExtension.InstallSchema, WebExtension.UninstallSchema]),
);
export const WebExtensionResultSchema: z.ZodLazy<
  z.ZodUnion<
    readonly [
      typeof WebExtension.InstallResultSchema,
      typeof WebExtension.UninstallResultSchema,
    ]
  >
> = z.lazy(() =>
  z.union([
    WebExtension.InstallResultSchema,
    WebExtension.UninstallResultSchema,
  ]),
);
export namespace WebExtension {
  export const ExtensionSchema: z.ZodLazy<z.ZodString> = z.lazy(() =>
    z.string(),
  );
}
export namespace WebExtension {
  export const InstallSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'webExtension.install'>;
      params: typeof WebExtension.InstallParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('webExtension.install'),
      params: WebExtension.InstallParametersSchema,
    }),
  );
}
export namespace WebExtension {
  export const InstallParametersSchema: z.ZodLazy<
    z.ZodObject<{
      extensionData: typeof WebExtension.ExtensionDataSchema;
    }>
  > = z.lazy(() =>
    z.object({
      extensionData: WebExtension.ExtensionDataSchema,
    }),
  );
}
export namespace WebExtension {
  export const ExtensionDataSchema: z.ZodLazy<
    z.ZodUnion<
      readonly [
        typeof WebExtension.ExtensionArchivePathSchema,
        typeof WebExtension.ExtensionBase64EncodedSchema,
        typeof WebExtension.ExtensionPathSchema,
      ]
    >
  > = z.lazy(() =>
    z.union([
      WebExtension.ExtensionArchivePathSchema,
      WebExtension.ExtensionBase64EncodedSchema,
      WebExtension.ExtensionPathSchema,
    ]),
  );
}
export namespace WebExtension {
  export const ExtensionPathSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'path'>;
      path: z.ZodString;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('path'),
      path: z.string(),
    }),
  );
}
export namespace WebExtension {
  export const ExtensionArchivePathSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'archivePath'>;
      path: z.ZodString;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('archivePath'),
      path: z.string(),
    }),
  );
}
export namespace WebExtension {
  export const ExtensionBase64EncodedSchema: z.ZodLazy<
    z.ZodObject<{
      type: z.ZodLiteral<'base64'>;
      value: z.ZodString;
    }>
  > = z.lazy(() =>
    z.object({
      type: z.literal('base64'),
      value: z.string(),
    }),
  );
}
export namespace WebExtension {
  export const InstallResultSchema: z.ZodLazy<
    z.ZodObject<{
      extension: typeof WebExtension.ExtensionSchema;
    }>
  > = z.lazy(() =>
    z.object({
      extension: WebExtension.ExtensionSchema,
    }),
  );
}
export namespace WebExtension {
  export const UninstallSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'webExtension.uninstall'>;
      params: typeof WebExtension.UninstallParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('webExtension.uninstall'),
      params: WebExtension.UninstallParametersSchema,
    }),
  );
}
export namespace WebExtension {
  export const UninstallParametersSchema: z.ZodLazy<
    z.ZodObject<{
      extension: typeof WebExtension.ExtensionSchema;
    }>
  > = z.lazy(() =>
    z.object({
      extension: WebExtension.ExtensionSchema,
    }),
  );
}
export namespace WebExtension {
  export const UninstallResultSchema: z.ZodLazy<typeof EmptyResultSchema> =
    z.lazy(() => EmptyResultSchema);
}
